import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Target, Trophy, Zap, Clock, CheckCircle, RefreshCw, Users } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import useRealTimeHealthData from '../../../hooks/useRealTimeHealthData';
import { useMediCureOnData } from '../../../hooks/useMediCureOnData';
import { apiConfig } from '../../../config/apiConfig';


const ChallengesTab = ({ 
  userChallenges, 
  challenges, 
  getChallengeStatus, 
  getUserChallengeStatus,
  updateChallengeProgress,
  joinChallenge,  // Add this prop
  setSelectedChallenge, 
  setShowChallengeDetails,
  customBadges,
  userSubscription, // NEW: User subscription data from check-subscription-status function
  updateSharedChallengeData // NEW: Callback to update shared challenge data for OverviewTab
}) => {
  // Backend Integration - Add real-time health data and API connectivity
  const { user, userInfo } = useAuth();
  const { healthData } = useRealTimeHealthData();

  // 🚀 ENTERPRISE MEDIPOINTS INTEGRATION
  const { 
    mediPointsBalance: enterpriseMediPointsBalance, 
    addMediPointsForChallengeCompletion,
    fetchMediPoints,
    isBackendConnected: enterpriseBackendConnected,
    userSubscription: hookUserSubscription // Get subscription from hook too
  } = useMediCureOnData();
  
  // Get the proper user ID for API calls
  const userId = user?.localAccountId || user?.username || userInfo?.localAccountId;

  // Production Challenge State Management - NO MOCK DATA
  const [userActiveChallenges, setUserActiveChallenges] = useState([]);
  const [userCompletedChallenges, setUserCompletedChallenges] = useState([]);
  const [userFailedChallenges, setUserFailedChallenges] = useState([]); // 🔥 NEW: Track failed challenges
  const [challengeProgress, setChallengeProgress] = useState({});
  const [isBackendConnected, setIsBackendConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // 🔒 SECURITY: Challenge completion fraud prevention
  const [completionTracker, setCompletionTracker] = useState(new Set());
  const [lastCompletionCheck, setLastCompletionCheck] = useState(0);
  const [lastExpirationCheck, setLastExpirationCheck] = useState(0); // 🔥 NEW: Track expiration checks
  
  // Comprehensive MediPoints tracking state
  const [mediPointsBalance, setMediPointsBalance] = useState({
    currentBalance: 0,      // Available points for spending
    totalEarned: 0,         // Lifetime earned (for titles/tiers)
    totalSpent: 0,          // Total used for services/products
    tier: 'Starter',        // User tier based on totalEarned
    pointsToNextTier: 500   // Points needed for next tier
  });
  const [pointsHistory, setPointsHistory] = useState([]);
  // Fair Play user messaging
  const [fairPlayWarnings, setFairPlayWarnings] = useState([]);
  const [fairPlayBanner, setFairPlayBanner] = useState(null); // { type: 'warning' | 'error', message: string }
  
  // Visual feedback states
  const [joiningStates, setJoiningStates] = useState({});
  const [stoppingStates, setStoppingStates] = useState({});
  
  // Details popup state
  const [showDetailsPopup, setShowDetailsPopup] = useState(false);
  const [selectedChallengeDetails, setSelectedChallengeDetails] = useState(null);

  // Memoize user ID to prevent unnecessary re-renders
  const memoizedUserId = useMemo(() => {
    return user?.localAccountId || user?.username || userInfo?.localAccountId;
  }, [user?.localAccountId, user?.username, userInfo?.localAccountId]);

  // Memoize user country using EXACT ProfileManager.jsx pattern with enhanced debugging
  const [profileCountry, setProfileCountry] = useState(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  
  // � Load user profile to get their actual country setting
  useEffect(() => {
    const loadUserProfileCountry = async () => {
      if (memoizedUserId && !profileLoaded) {
        try {
          console.log('🔍 Loading user profile to get country data...');
          console.log('🔍 User ID:', memoizedUserId);
          console.log('🔍 Profile API Config:', {
            backendUrl: apiConfig.backendUrl,
            functionKey: apiConfig.functionKeys.profileGet?.substring(0, 10) + '...'
          });
          
        // 🔧 FIX: Use the EXACT same endpoint that ProfileManager.jsx uses successfully
        const profileResponse = await fetch(`${apiConfig.backendUrl}/api/profiles/${memoizedUserId}?code=${apiConfig.functionKeys.profileGet}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        console.log('🔍 Profile response status:', profileResponse.status);
          
          if (profileResponse.ok) {
            const profileData = await profileResponse.json();
            console.log('🔍 FULL Profile response:', JSON.stringify(profileData, null, 2));
            
            // 🔧 FIX: Extract country using ProfileManager.jsx's exact structure  
            const actualCountry = profileData?.data?.generalInfo?.country ||
                                 profileData?.data?.personalDetails?.country ||
                                 profileData?.generalInfo?.country ||
                                 profileData?.personalDetails?.country;
            console.log('🔍 Extracted country from profile generalInfo:', actualCountry);
            
            if (actualCountry) {
              console.log(`🏁 SUCCESS: Found user's actual country in profile: ${actualCountry}`);
              setProfileCountry(actualCountry);
            } else {
              console.log('⚠️ No country found in profile generalInfo');
              console.log('🔍 Available generalInfo keys:', Object.keys(profileData?.data?.generalInfo || {}));
              console.log('🔍 Full generalInfo:', profileData?.data?.generalInfo);
            }
          } else {
            const errorText = await profileResponse.text();
            console.warn('⚠️ Profile API failed - Status:', profileResponse.status, 'Error:', errorText);
          }
          
          setProfileLoaded(true);
        } catch (error) {
          console.error('❌ Error loading user profile:', error);
          setProfileLoaded(true);
        }
      }
    };

    loadUserProfileCountry();
  }, [memoizedUserId, profileLoaded, user?.idToken]);

  // 🌍 DYNAMIC: Browser-based country detection helper (supports all countries)
  const detectCountryFromBrowser = () => {
    const browserLocale = navigator.language || navigator.languages?.[0];
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    console.log('🌍 Browser detection info:', { browserLocale, timezone });
    
    // Dynamic timezone-based detection for major countries
    const timezoneCountryMap = {
      'Asia/Karachi': 'Pakistan',
      'Asia/Lahore': 'Pakistan', 
      'Europe/Stockholm': 'Sweden',
      'Europe/Berlin': 'Germany',
      'Europe/Paris': 'France',
      'Europe/London': 'United Kingdom',
      'Europe/Rome': 'Italy',
      'Europe/Madrid': 'Spain',
      'Asia/Tokyo': 'Japan',
      'Asia/Seoul': 'South Korea',
      'Asia/Shanghai': 'China',
      'Asia/Singapore': 'Singapore',
      'Asia/Mumbai': 'India',
      'Asia/Dubai': 'UAE',
      'Australia/Sydney': 'Australia',
      'Canada/Eastern': 'Canada',
      'America/Toronto': 'Canada',
      'America/Vancouver': 'Canada',
      'America/Mexico_City': 'Mexico',
      'America/Sao_Paulo': 'Brazil',
      'America/Argentina/Buenos_Aires': 'Argentina',
      'Africa/Cairo': 'Egypt',
      'Africa/Lagos': 'Nigeria',
      'Africa/Johannesburg': 'South Africa'
    };
    
    // Check timezone first
    if (timezone && timezoneCountryMap[timezone]) {
      return timezoneCountryMap[timezone];
    }
    
    // Fallback to locale-based detection
    const localeCountryMap = {
      'sv': 'Sweden',
      'de': 'Germany', 
      'fr': 'France',
      'es': 'Spain',
      'it': 'Italy',
      'ja': 'Japan',
      'ko': 'South Korea',
      'zh': 'China',
      'ur': 'Pakistan',
      'hi': 'India',
      'ar': 'UAE',
      'pt': 'Brazil'
    };
    
    const localeCode = browserLocale?.split('-')[0];
    if (localeCode && localeCountryMap[localeCode]) {
      return localeCountryMap[localeCode];
    }
    
    return null;
  };

  const memoizedUserCountry = useMemo(() => {
    // 🚨 DEBUG: Log everything we know about the user
    console.log('🔍 COMPREHENSIVE USER DEBUG:', {
      'memoizedUserId': memoizedUserId,
      'profileLoaded': profileLoaded,
      'profileCountry': profileCountry,
      'userInfo full': userInfo,
      'user full': user,
      'window.location.href': window.location.href
    });

    // 🌍 DYNAMIC SOLUTION: Profile-based country detection for ALL 195 countries
    // This system works dynamically without hardcoding specific users
    
    // 🎯 PRIORITY 1: Use country from loaded profile (ProfileManager.jsx pattern)
    if (profileCountry) {
      console.log(`🎯 Using profile country: ${profileCountry}`);
      return profileCountry;
    }

    // 🎯 PRIORITY 2: Use country from userInfo context (any country)
    const contextCountry = userInfo?.country || userInfo?.personalDetails?.country;
    if (contextCountry && contextCountry !== 'United States') {
      console.log(`🎯 Using context country: ${contextCountry}`);
      return contextCountry;
    }
    
    // � PRIORITY 3: Use country from user token (any country)
    const tokenCountry = user?.idTokenClaims?.country || user?.country;
    if (tokenCountry && tokenCountry !== 'United States') {
      console.log(`🎯 Using token country: ${tokenCountry}`);
      return tokenCountry;
    }

    // � PRIORITY 4: Browser-based detection (for non-US countries)
    const browserCountry = detectCountryFromBrowser();
    if (browserCountry && browserCountry !== 'United States') {
      console.log(`🎯 Using browser-detected country: ${browserCountry}`);
      return browserCountry;
    }

    // 🎯 FALLBACK: Default to United States (covers Americas/Africa/Middle East region)
    console.log('🇺� Using default: United States');
    return 'United States';
  }, [profileCountry, userInfo, user, memoizedUserId, profileLoaded]);

  // 🔍 DEBUG: Log final country being sent to API
  useEffect(() => {
    console.log('🚀 API COUNTRY HEADER will be:', memoizedUserCountry);
    if (memoizedUserCountry === 'Pakistan') {
      console.log('✅ Pakistan user detected - should route to APAC region');
    } else if (memoizedUserCountry === 'United States') {
      console.log('⚠️ Defaulting to United States - verify user profile has country data');
    }
  }, [memoizedUserCountry]);

  // Share challenge data with OverviewTab whenever active challenges change
  useEffect(() => {
    if (updateSharedChallengeData) {
      updateSharedChallengeData({
        userActiveChallenges,
        challengeProgress,
        mediPointsBalance
      });
    }
  }, [userActiveChallenges, challengeProgress, mediPointsBalance, updateSharedChallengeData]); // Now stable due to useCallback in parent

  // Emit challenge data update event for LandingPage and other components
  useEffect(() => {
    // Dispatch custom event when challenge data changes
    window.dispatchEvent(new CustomEvent('challengeDataUpdated', {
      detail: {
        userActiveChallenges,
        challengeProgress,
        mediPointsBalance
      }
    }));
  }, [userActiveChallenges, challengeProgress, mediPointsBalance]);

  // 🚀 ENTERPRISE MEDIPOINTS: Load enterprise balance on component mount
  useEffect(() => {
    const loadEnterpriseMediPoints = async () => {
      if (memoizedUserId && enterpriseBackendConnected) {
        try {
          await fetchMediPoints();
        } catch (error) {
          console.error('❌ ChallengesTab: Error loading enterprise MediPoints:', error);
        }
      }
    };

    loadEnterpriseMediPoints();
  }, [memoizedUserId, enterpriseBackendConnected, fetchMediPoints]);

  // Production Challenge Data Loading from Azure Data Lake (following ProfileManager pattern)
  // Load challenge data from Azure Data Lake Profile containers (following ProfileManager pattern)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadProductionChallengeData = useCallback(async () => {
    if (!memoizedUserId) {
      console.warn('⚠️ No user ID available for loading challenge data');
      return;
    }
    
    try {
      setLoading(true);
      
      console.log('� Loading challenge data from backend (always fresh)...');
      
      // SIMPLIFIED: Always fetch fresh data from Azure Data Lake - no local caching complexity
      const response = await fetch(`${apiConfig.backendUrl}/api/challenges-manager?code=${apiConfig.functionKeys.challengesManager}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user?.idToken}`,
          'X-User-Country': memoizedUserCountry
        },
        body: JSON.stringify({
          action: 'get',
          userId: memoizedUserId,
          country: memoizedUserCountry
        })
      });
      
      if (response.ok) {
        const resJson = await response.json();
        const challengeData = resJson.data?.challengeData || {};
        
        console.log('📥 Fresh backend challenge data:', {
          activeChallenges: challengeData.activeChallenges?.map(c => c.challengeId) || [],
          completedChallenges: challengeData.completedChallenges?.map(c => c.challengeId) || [],
          progressKeys: Object.keys(challengeData.challengeProgress || {})
        });
        
        // SIMPLIFIED: Set data directly from backend - backend is the source of truth
        setUserActiveChallenges(challengeData.activeChallenges || []);
        setUserCompletedChallenges(challengeData.completedChallenges || []);
        setChallengeProgress(challengeData.challengeProgress || {});
        
        console.log(`☁️ Challenge data loaded from ${resJson.data?.region || 'unknown'} region`);
        console.log('✅ Active challenges from backend:', challengeData.activeChallenges?.map(c => c.challengeId) || []);
        
        setIsBackendConnected(true);
        
      } else {
        // Don't clear local data if we have active challenges (preserves successful joins)
        if (userActiveChallenges.length === 0) {
          setUserActiveChallenges([]);
          setUserCompletedChallenges([]);
          setChallengeProgress({});
        } else {
        }
        setIsBackendConnected(false);
      }
      
    } catch (error) {
      console.error('❌ Error loading challenge data from Azure Data Lake:', error);
      setIsBackendConnected(false);
      
      // Don't clear local data if we have active challenges (preserves successful joins)
      if (userActiveChallenges.length === 0) {
        setUserActiveChallenges([]);
        setUserCompletedChallenges([]);
        setChallengeProgress({});
      } else {
      }
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoizedUserId, memoizedUserCountry, user?.country, user?.idTokenClaims?.country, userInfo?.country, userInfo?.personalDetails?.country]);

  // Save challenge data to Azure Data Lake Profile containers (following ProfileManager pattern)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const saveChallengeDataToStorage = useCallback(async (activeChals, completedChals, progressData) => {
    if (!memoizedUserId) return;
    
    try {
      console.log('🌍 REGIONAL ROUTING DEBUG:', {
        userId: memoizedUserId,
        detectedCountry: memoizedUserCountry,
        userInfoCountry: userInfo?.country,
        personalDetailsCountry: userInfo?.personalDetails?.country,
        idTokenCountry: user?.idTokenClaims?.country,
        userCountry: user?.country,
        region: (() => {
          // Complete 195-country regional mapping system
          const countryToRegionMap = {
            // US Region (Americas, Africa, Middle East + default) - 115 countries
            'United States': 'US', 'Canada': 'US', 'Mexico': 'US', 'Guatemala': 'US', 'Belize': 'US',
            'El Salvador': 'US', 'Honduras': 'US', 'Nicaragua': 'US', 'Costa Rica': 'US', 'Panama': 'US',
            'Cuba': 'US', 'Jamaica': 'US', 'Haiti': 'US', 'Dominican Republic': 'US', 'Puerto Rico': 'US',
            'Trinidad and Tobago': 'US', 'Barbados': 'US', 'Saint Lucia': 'US', 'Saint Vincent and the Grenadines': 'US',
            'Grenada': 'US', 'Antigua and Barbuda': 'US', 'Dominica': 'US', 'Saint Kitts and Nevis': 'US',
            'Bahamas': 'US', 'Brazil': 'US', 'Argentina': 'US', 'Chile': 'US', 'Uruguay': 'US', 'Paraguay': 'US',
            'Bolivia': 'US', 'Peru': 'US', 'Ecuador': 'US', 'Colombia': 'US', 'Venezuela': 'US',
            'Guyana': 'US', 'Suriname': 'US', 'French Guiana': 'US',
            // African countries
            'Egypt': 'US', 'Libya': 'US', 'Tunisia': 'US', 'Algeria': 'US', 'Morocco': 'US', 'Sudan': 'US',
            'South Sudan': 'US', 'Ethiopia': 'US', 'Eritrea': 'US', 'Djibouti': 'US', 'Somalia': 'US',
            'Kenya': 'US', 'Uganda': 'US', 'Tanzania': 'US', 'Rwanda': 'US', 'Burundi': 'US',
            'Democratic Republic of Congo': 'US', 'Republic of Congo': 'US', 'Central African Republic': 'US',
            'Cameroon': 'US', 'Chad': 'US', 'Niger': 'US', 'Nigeria': 'US', 'Benin': 'US', 'Togo': 'US',
            'Ghana': 'US', 'Burkina Faso': 'US', 'Mali': 'US', 'Senegal': 'US', 'Mauritania': 'US',
            'Gambia': 'US', 'Guinea-Bissau': 'US', 'Guinea': 'US', 'Sierra Leone': 'US', 'Liberia': 'US',
            'Ivory Coast': 'US', 'Cape Verde': 'US', 'São Tomé and Príncipe': 'US', 'Equatorial Guinea': 'US',
            'Gabon': 'US', 'Angola': 'US', 'Zambia': 'US', 'Malawi': 'US', 'Mozambique': 'US',
            'Zimbabwe': 'US', 'Botswana': 'US', 'Namibia': 'US', 'South Africa': 'US', 'Lesotho': 'US',
            'Eswatini': 'US', 'Madagascar': 'US', 'Mauritius': 'US', 'Seychelles': 'US', 'Comoros': 'US',
            // Middle East
            'Saudi Arabia': 'US', 'Yemen': 'US', 'Oman': 'US', 'United Arab Emirates': 'US', 'Qatar': 'US',
            'Bahrain': 'US', 'Kuwait': 'US', 'Iraq': 'US', 'Syria': 'US', 'Lebanon': 'US', 'Jordan': 'US',
            'Israel': 'US', 'Palestine': 'US', 'Iran': 'US', 'Turkey': 'US', 'Georgia': 'US',
            'Armenia': 'US', 'Azerbaijan': 'US',
            // Central Asian countries
            'Kazakhstan': 'US', 'Uzbekistan': 'US', 'Kyrgyzstan': 'US', 'Tajikistan': 'US', 'Turkmenistan': 'US',
            
            // EU Region (Europe - GDPR compliance) - 44 countries
            'Austria': 'EU', 'Belgium': 'EU', 'Bulgaria': 'EU', 'Croatia': 'EU', 'Cyprus': 'EU',
            'Czech Republic': 'EU', 'Denmark': 'EU', 'Estonia': 'EU', 'Finland': 'EU', 'France': 'EU',
            'Germany': 'EU', 'Greece': 'EU', 'Hungary': 'EU', 'Ireland': 'EU', 'Italy': 'EU',
            'Latvia': 'EU', 'Lithuania': 'EU', 'Luxembourg': 'EU', 'Malta': 'EU', 'Netherlands': 'EU',
            'Poland': 'EU', 'Portugal': 'EU', 'Romania': 'EU', 'Slovakia': 'EU', 'Slovenia': 'EU',
            'Spain': 'EU', 'Sweden': 'EU', 'United Kingdom': 'EU', 'Norway': 'EU', 'Switzerland': 'EU',
            'Iceland': 'EU', 'Liechtenstein': 'EU', 'Monaco': 'EU', 'San Marino': 'EU', 'Vatican City': 'EU',
            'Andorra': 'EU', 'Serbia': 'EU', 'Montenegro': 'EU', 'North Macedonia': 'EU', 'Albania': 'EU',
            'Bosnia and Herzegovina': 'EU', 'Kosovo': 'EU', 'Moldova': 'EU', 'Ukraine': 'EU',
            'Belarus': 'EU', 'Russia': 'EU',
            
            // APAC Region (Asia-Pacific) - 36 countries
            'Afghanistan': 'APAC', 'Pakistan': 'APAC', 'India': 'APAC', 'Bangladesh': 'APAC', 'Sri Lanka': 'APAC',
            'Nepal': 'APAC', 'Bhutan': 'APAC', 'Maldives': 'APAC', 'Myanmar': 'APAC', 'Thailand': 'APAC',
            'Laos': 'APAC', 'Cambodia': 'APAC', 'Vietnam': 'APAC', 'Malaysia': 'APAC', 'Singapore': 'APAC',
            'Indonesia': 'APAC', 'Philippines': 'APAC', 'Brunei': 'APAC', 'East Timor': 'APAC', 'China': 'APAC',
            'Mongolia': 'APAC', 'North Korea': 'APAC', 'South Korea': 'APAC', 'Japan': 'APAC', 'Taiwan': 'APAC',
            'Hong Kong': 'APAC', 'Macau': 'APAC', 'Australia': 'APAC', 'New Zealand': 'APAC', 'Fiji': 'APAC',
            'Papua New Guinea': 'APAC', 'Solomon Islands': 'APAC', 'Vanuatu': 'APAC', 'Samoa': 'APAC',
            'Tonga': 'APAC', 'Tuvalu': 'APAC', 'Kiribati': 'APAC', 'Nauru': 'APAC', 'Palau': 'APAC',
            'Marshall Islands': 'APAC', 'Micronesia': 'APAC'
          };
          
          return countryToRegionMap[memoizedUserCountry] || 'US'; // Default to US for unknown countries
        })()
      });
      console.log('📝 Function key being used:', apiConfig.functionKeys.challengesManager?.substring(0, 20) + '...');
      // Use action-based pattern as expected by challenges-manager function
      const challengeData = {
        activeChallenges: activeChals || [],
        completedChallenges: completedChals || [],
        challengeProgress: progressData || {},
        lastUpdated: new Date().toISOString(),
        country: memoizedUserCountry
      };
      const response = await fetch(`${apiConfig.backendUrl}/api/challenges-manager?code=${apiConfig.functionKeys.challengesManager}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user?.idToken}`,
          'X-User-Country': memoizedUserCountry
        },
        body: JSON.stringify({
          action: 'save',
          userId: memoizedUserId,
          challengeData: challengeData,
          country: memoizedUserCountry
        })
      });
      
      console.log('🔍 DEBUG: Request body JSON:', JSON.stringify({
        action: 'save',
        userId: memoizedUserId,
        challengeData: challengeData,
        country: memoizedUserCountry
      }, null, 2));
      
      if (response.ok) {
        const result = await response.json();
        // If backend enforced Fair Play and returned normalized data + warnings, surface them
        if (result?.warnings && Array.isArray(result.warnings) && result.warnings.length > 0) {
          setFairPlayWarnings(result.warnings);
          // Show concise banner message
          const msg = result.warnings.join('\n');
          setFairPlayBanner({ type: 'warning', message: msg });
          // Auto-clear banner after 10s
          setTimeout(() => setFairPlayBanner(null), 10000);
        }
        // If backend returned canonical challengeData, prefer syncing local state to it to avoid UI drift
        if (result?.data?.challengeData) {
          const srv = result.data.challengeData;
          if (srv.activeChallenges) setUserActiveChallenges(srv.activeChallenges);
          if (srv.completedChallenges) setUserCompletedChallenges(srv.completedChallenges);
          if (srv.challengeProgress) setChallengeProgress(srv.challengeProgress);
        }
        // Also save to main localStorage for other components to access
        try {
          localStorage.setItem(`challenges_${memoizedUserId}`, JSON.stringify({
            userActiveChallenges: activeChals,
            completedChallenges: completedChals,
            challengeProgress: progressData,
            lastUpdated: new Date().toISOString()
          }));
        } catch (localError) {
          console.warn('⚠️ Main localStorage save failed:', localError);
        }
        return result;
      } else {
        const errorText = await response.text();
        console.error(`❌ Save failed with status ${response.status}:`, errorText);
        throw new Error(`Failed to save challenge data: ${response.status} - ${errorText}`);
      }
      
    } catch (error) {
      console.warn('⚠️ Challenge data save failed - Azure Data Lake unavailable:', error.message);
      // Don't re-throw to prevent breaking the UI - save locally instead
      try {
        localStorage.setItem(`challenges_fallback_${memoizedUserId}`, JSON.stringify({
          activeChallenges: activeChals,
          completedChallenges: completedChals,
          challengeProgress: progressData,
          lastUpdated: new Date().toISOString()
        }));
      } catch (localError) {
        console.error('❌ Even localStorage save failed:', localError);
        return undefined;
    }
    }
  }, [memoizedUserId, memoizedUserCountry, user?.idToken, user?.country, user?.idTokenClaims?.country, userInfo?.country, userInfo?.personalDetails?.country]);

  // Save MediPoints to MediCureOn Data Storage using comprehensive backend system
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const saveMediPointsToStorage = useCallback(async (pointsToAdd, newPointEntry) => {
    if (!memoizedUserId) return;
    
    try {
      // 🚀 USE ENTERPRISE MEDIPOINTS SYSTEM FOR CHALLENGE COMPLETION
      console.log(`💰 Attempting to award ${pointsToAdd} MediPoints for challenge: ${newPointEntry.challengeName}`);
      console.log(`🔧 Challenge details:`, newPointEntry);
      
      const result = await addMediPointsForChallengeCompletion(
        pointsToAdd,  // amount (number)
        newPointEntry.challengeId || newPointEntry.challengeName || 'unknown-challenge', // challengeId (string)
        newPointEntry.challengeType || 'health-challenge', // challengeType (string)
        {
          challengeName: newPointEntry.challengeName || newPointEntry.description || 'Challenge completion',
          completionData: newPointEntry.completionData,
          source: 'ChallengesTab'
        }
      );
      
      console.log(`📊 MediPoints award result:`, result);
      
      if (result.success) {
        console.log(`✅ Successfully awarded ${pointsToAdd} MediPoints via enterprise system`);
      } else {
        console.warn('⚠️ Enterprise challenge completion failed:', result.error);
        console.warn('🔍 This might explain why user received wrong amount of points');
      }
      
      // Refresh enterprise balance to get updated data with analytics
      await fetchMediPoints();
      
      // Update local state with enterprise data
      if (enterpriseMediPointsBalance) {
        setMediPointsBalance({
          currentBalance: enterpriseMediPointsBalance.currentBalance || 0,
          totalEarned: enterpriseMediPointsBalance.totalEarned || 0,
          totalSpent: enterpriseMediPointsBalance.totalSpent || 0,
          tier: enterpriseMediPointsBalance.currentBalance > 5000 ? 'Gold' : 
                enterpriseMediPointsBalance.currentBalance > 2000 ? 'Silver' : 'Bronze',
          pointsToNextTier: 500
        });
      }
      
      // Add to local history for immediate UI feedback
      const updatedHistory = [newPointEntry, ...pointsHistory];
      setPointsHistory(updatedHistory.slice(0, 50)); // Keep last 50 for UI
      return result.data;
      
    } catch (error) {
      console.warn('⚠️ Enterprise MediPoints save failed:', error.message);
      
      // Fallback: Update local state optimistically
      const newBalance = mediPointsBalance.currentBalance + pointsToAdd;
      const newTotalEarned = mediPointsBalance.totalEarned + pointsToAdd;
      
      setMediPointsBalance(prev => ({
        currentBalance: newBalance,
        totalEarned: newTotalEarned,
        totalSpent: prev.totalSpent,
        tier: prev.tier,
        pointsToNextTier: prev.pointsToNextTier
      }));
      
      const updatedHistory = [newPointEntry, ...pointsHistory];
      setPointsHistory(updatedHistory.slice(0, 50));
      throw error; // Re-throw to handle in calling function
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoizedUserId, memoizedUserCountry, pointsHistory, mediPointsBalance, enterpriseMediPointsBalance, fetchMediPoints, addMediPointsForChallengeCompletion]);

  // Load comprehensive MediPoints data from MediCureOn Data Storage (following ProfileManager pattern)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadUserMediPoints = useCallback(async () => {
    if (!memoizedUserId) return;
    
    try {
      // Use the comprehensive medi-points-manager backend function
      const response = await fetch(`${apiConfig.backendUrl}/api/medi-points-manager?code=${apiConfig.functionKeys.mediPointsManager}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user?.idToken}`,
          'X-User-Country': memoizedUserCountry
        },
        body: JSON.stringify({
          action: 'balance',
          userId: memoizedUserId,
          country: memoizedUserCountry
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        const pointsData = result.data || {};
        // Update comprehensive MediPoints state
        setMediPointsBalance({
          currentBalance: pointsData.currentBalance || 0,
          totalEarned: pointsData.totalEarned || 0,
          totalSpent: pointsData.totalSpent || 0,
          tier: pointsData.tier || 'Starter',
          pointsToNextTier: pointsData.pointsToNextTier || 500
        });
        console.log(`   • Current Tier: ${pointsData.tier || 'Starter'}`);
        
        setIsBackendConnected(true);
      } else {
        // Initialize with empty comprehensive data
        setMediPointsBalance({
          currentBalance: 0,
          totalEarned: 0,
          totalSpent: 0,
          tier: 'Starter',
          pointsToNextTier: 500
        });
        setPointsHistory([]);
        setIsBackendConnected(false);
      }
      
    } catch (error) {
      console.error('❌ Error loading MediPoints from MediCureOn Data Storage:', error);
      // Initialize with empty comprehensive data if MediCureOn Data Storage fails
      setMediPointsBalance({
        currentBalance: 0,
        totalEarned: 0,
        totalSpent: 0,
        tier: 'Starter',
        pointsToNextTier: 500
      });
      setPointsHistory([]);
    }
  }, [memoizedUserId, memoizedUserCountry, user?.idToken]);

  // Cleanup duplicate challenges (fix for existing duplicates)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const cleanupDuplicateChallenges = useCallback(() => {
    setUserActiveChallenges(prev => {
      const uniqueChallenges = [];
      const seenIds = new Set();
      
      for (const challenge of prev) {
        if (!seenIds.has(challenge.challengeId)) {
          seenIds.add(challenge.challengeId);
          uniqueChallenges.push(challenge);
        } else {
        }
      }
      
      if (uniqueChallenges.length !== prev.length) {
      }
      
      return uniqueChallenges;
    });
  }, []);

  // Single initialization effect - runs when user ID changes or component mounts
  useEffect(() => {
    if (!memoizedUserId) {
      console.warn('⚠️ No user ID available for loading challenge data');
      return;
    }
    const initializeWithMigration = async () => {
      try {
        // First, clean up any existing duplicates
        cleanupDuplicateChallenges();
        
        // Always attempt to load from Azure Data Lake on page refresh/mount
        // This ensures challenges persist across page refreshes
        await loadProductionChallengeData();
        await loadUserMediPoints();
        
        // Final cleanup after loading data
        setTimeout(() => cleanupDuplicateChallenges(), 1000);
        
      } catch (error) {
        console.error('❌ Error during initialization:', error);
        // Fallback to normal loading
        try {
          await loadProductionChallengeData();
          await loadUserMediPoints();
        } catch (fallbackError) {
          console.error('❌ Fallback loading also failed:', fallbackError);
        }
      }
    };
    
    initializeWithMigration();
    
    // Include all dependencies
  }, [memoizedUserId, memoizedUserCountry, cleanupDuplicateChallenges, loadProductionChallengeData, loadUserMediPoints]);

  // Challenge completion detection and MediPoints awarding system
  // eslint-disable-next-line no-unused-vars
  const checkAndAwardChallengeCompletion = useCallback(async (challengeId, currentProgress, targetValue) => {
    try {
      const isCompleted = currentProgress >= targetValue;
      if (!isCompleted) return false;
      
      // Check if this challenge was already completed (avoid double awarding)
      const alreadyCompleted = userCompletedChallenges.some(c => c.challengeId === challengeId);
      if (alreadyCompleted) return false;
      
      // Get challenge details for points calculation
      const challenge = getAllChallenges().find(c => c.id === challengeId);
      if (!challenge) {
        console.error(`❌ Challenge not found for ID: ${challengeId}`);
        return false;
      }
      
      const pointsToAward = challenge.points || 100; // Default 100 points if not specified
      const completionTimestamp = new Date().toISOString();
      
      console.log(`🏆 CHALLENGE COMPLETED: "${challenge.name}" (ID: ${challengeId})`);
      console.log(`💰 Points to award: ${pointsToAward} (defined in challenge: ${challenge.points})`);
      console.log(`📊 Challenge details:`, {
        id: challenge.id,
        name: challenge.name,
        points: challenge.points,
        targetValue,
        currentProgress
      });
      
      // Create completed challenge entry
      const completedChallenge = {
        challengeId: challengeId,
        userId: userId,
        name: challenge.name,
        pointsAwarded: pointsToAward,
        completedAt: completionTimestamp,
        duration: challenge.duration,
        targetValue: targetValue,
        finalProgress: currentProgress,
        dataType: detectChallengeDataType(challenge)
      };
      
      // Update completed challenges
      const updatedCompletedChallenges = [...userCompletedChallenges, completedChallenge];
      setUserCompletedChallenges(updatedCompletedChallenges);
      
      // Remove from active challenges
      const updatedActiveChallenges = userActiveChallenges.filter(c => c.challengeId !== challengeId);
      setUserActiveChallenges(updatedActiveChallenges);
      
      // Award MediPoints using comprehensive backend system
      const newPointEntry = {
        source: 'Challenge Completion',
        challengeName: challenge.name,
        description: `Completed challenge: ${challenge.name}`,
        points: pointsToAward,
        timestamp: completionTimestamp,
        challengeId: challengeId
      };
      
      // Add points using backend system (not setting total)
      await saveMediPointsToStorage(pointsToAward, newPointEntry);
      
      // Save updated challenge data
      saveChallengeDataToStorage(updatedActiveChallenges, updatedCompletedChallenges, challengeProgress);
      
      // 🎉 ENHANCED COMPLETION CELEBRATION - Show achievement with proper points
      const congratulationMessage = `🏆 CHALLENGE COMPLETED! 🏆
      
✨ "${challenge.name}" ✨
🎯 Earned: ${pointsToAward} MediPoints (as promised!)

💰 Your Updated MediPoints:
   • Available Balance: ${mediPointsBalance.currentBalance + pointsToAward} MP
   • Lifetime Total: ${mediPointsBalance.totalEarned + pointsToAward} MP
   • Current Tier: ${mediPointsBalance.tier}

🚀 Way to go! Keep up the amazing work!
Ready for your next challenge? 💪`;

      console.log(`🎉 CHALLENGE COMPLETION SUCCESS: ${challenge.name} → ${pointsToAward} MediPoints awarded`);
      
      // Enhanced alert with better formatting
      alert(congratulationMessage);
      
      return true;
      
    } catch (error) {
      console.error('❌ Error processing challenge completion:', error);
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, userCompletedChallenges, userActiveChallenges, mediPointsBalance, challengeProgress, saveMediPointsToStorage, saveChallengeDataToStorage]); // getAllChallenges is defined later in the file

  // Azure Data Lake historical data collection for multi-day challenges - ALL IoMT DEVICES WITH REGIONAL SUPPORT
  // eslint-disable-next-line no-unused-vars
  const collectHistoricalHealthData = async (userId, startDate, durationDays, dataType = 'steps') => {
    try {
      // Parse duration if it's a string like "70 days"
      const durationNumber = typeof durationDays === 'string' 
        ? parseInt(durationDays.match(/(\d+)/)?.[1] || '70') 
        : (durationDays || 70);
      // Use user country for backend regional routing
      const userCountry = memoizedUserCountry;
      console.log(`🌍 REGIONAL ROUTING: User country "${userCountry}" → Backend handles regional routing`);
      
      // Calculate date range for historical data (go backwards from start date)
      let startDateObj;
      if (typeof startDate === 'string') {
        startDateObj = new Date(startDate);
      } else {
        startDateObj = new Date(startDate);
      }
      
      // Validate the date
      if (isNaN(startDateObj.getTime())) {
        console.error('❌ Invalid start date provided:', startDate);
        throw new Error('Invalid start date');
      }
      
      // For historical data, calculate end date by going backwards using proper number
      const endDate = new Date(startDateObj);
      endDate.setDate(startDateObj.getDate() - durationNumber);
      
      // Alternative method if date calculation fails (handles month boundaries better)
      if (isNaN(endDate.getTime())) {
        console.warn('⚠️ Using alternative date calculation method');
        const endDateAlt = new Date(startDateObj.getTime() - (durationNumber * 24 * 60 * 60 * 1000));
        endDate.setTime(endDateAlt.getTime());
      }
      
      // Validate end date
      if (isNaN(endDate.getTime())) {
        console.error('❌ Invalid end date calculated:', endDate);
        console.error('❌ Original start date:', startDate);
        console.error('❌ Duration days:', durationNumber);
        throw new Error('Invalid end date');
      }
      
      console.log(`📅 Date range: ${endDate.toISOString().split('T')[0]} to ${startDateObj.toISOString().split('T')[0]} (${durationNumber} days)`);
      
      // Use the same pattern as other backend calls
      const response = await fetch(`${apiConfig.backendUrl}/api/iomt-data-get?code=${apiConfig.functionKeys.iomtDataGet}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user?.idToken}`,
          'X-User-Country': userCountry
        },
        body: JSON.stringify({
          userId,
          startDate: endDate.toISOString().split('T')[0], // Start from earliest date
          endDate: startDateObj.toISOString().split('T')[0], // End at today
          dataType,
          country: userCountry,
          // IoMT device hierarchy: DeviceType -> Year -> Month -> Day structure
          deviceTypes: ['apple', 'samsung', 'fitbit', 'withings', 'garmin', 'oura', 'dexcom'],
          dataHierarchy: 'device/year/month/day'
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        const historicalData = result.data || {};
        return historicalData;
      } else {
        throw new Error(`Azure Data Lake API error: ${response.status}`);
      }
    } catch (error) {
      console.warn('⚠️ Azure Data Lake collection failed:', error);
      throw error;
    }
  };

  // Helper function to parse duration strings like "7 days", "21 days", "70 days" to numbers
  // eslint-disable-next-line no-unused-vars
  const parseDurationToNumber = (duration) => {
    if (typeof duration === 'number') {
      return duration;
    }
    if (typeof duration === 'string') {
      const match = duration.match(/(\d+)/);
      if (match) {
        return parseInt(match[1], 10);
      }
    }
    // Default to 70 days for "70 days" challenges if parsing fails
    return 70;
  };



  // Get all challenges from production challenge data (static data)
  const getAllChallenges = useCallback(() => {
    return [
      ...(productionChallenges.beginner || []),
      ...(productionChallenges.easy || []),
      ...(productionChallenges.medium || []),
      ...(productionChallenges.hard || []),
      ...(productionChallenges.extreme || []),
      ...(productionChallenges.century || [])
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // productionChallenges is static data, no need to include

  // Detect challenge data type from challenge name/details for ALL IoMT devices
  const detectChallengeDataType = (challenge) => {
    const name = challenge.name.toLowerCase();
    const details = challenge.details?.toLowerCase() || '';
    const combined = `${name} ${details}`;
    
    console.log(`🔍 CHALLENGE TYPE DETECTION: Analyzing "${challenge.name}"`);
    
    // Special daily consistency challenges (check before general step detection)
    if (name.includes('gentle morning walker') || (combined.includes('morning') && combined.includes('walk'))) {
      return 'days'; // Display unit for daily consistency challenges
    }
    
    // Steps challenges (Apple Health, Samsung Health, Fitbit, Garmin)
    if (combined.includes('step') || combined.includes('walk') || combined.includes('stride')) {
      return 'steps';
    }
    
    // Heart rate challenges (Apple Watch, Samsung Galaxy Watch, Fitbit Sense, Garmin)
    if (combined.includes('heart') || combined.includes('cardio') || combined.includes('pulse') || combined.includes('bpm')) {
      return 'heart_rate';
    }
    
    // Sleep challenges (Oura Ring, Apple Watch, Fitbit, Garmin sleep tracking)
    if (combined.includes('sleep') || combined.includes('rest') || combined.includes('recovery') || combined.includes('hour')) {
      return 'sleep_duration';
    }
    
    // Blood glucose challenges (Dexcom CGM, Abbott FreeStyle)
    if (combined.includes('glucose') || combined.includes('sugar') || combined.includes('diabetic') || combined.includes('cgm')) {
      return 'blood_glucose';
    }
    
    // Weight challenges (Withings Body+, Nokia scales)
    if (combined.includes('weight') || combined.includes('scale') || combined.includes('body') || combined.includes('mass')) {
      return 'weight';
    }
    
    // Blood pressure challenges (Withings BPM, Omron)
    if (combined.includes('pressure') || combined.includes('systolic') || combined.includes('diastolic') || combined.includes('hypertension')) {
      return 'blood_pressure';
    }
    
    // Hydration challenges (manual tracking, smart bottles)
    if (combined.includes('water') || combined.includes('hydrat') || combined.includes('drink') || combined.includes('fluid')) {
      return 'hydration';
    }
    
    // Calorie challenges (all fitness devices)
    if (combined.includes('calorie') || combined.includes('energy') || combined.includes('burn') || combined.includes('expenditure')) {
      return 'calories_burned';
    }
    
    // Exercise challenges (all fitness devices)
    if (combined.includes('exercise') || combined.includes('workout') || combined.includes('training') || combined.includes('active')) {
      return 'exercise_minutes';
    }
    
    // Default to steps for unrecognized challenges
    return 'steps';
  };

  // Production Challenge Join with Azure Data Lake persistence
  const handleJoinChallenge = async (challengeId) => {
    if (!userId) {
      console.warn('No user ID available for challenge join');
      return;
    }
    
    // 🔒 RESTART PREVENTION: Check if challenge is already active
    const isAlreadyActive = userActiveChallenges.some(c => c.challengeId === challengeId && c.status === 'active');
    if (isAlreadyActive) {
      alert(`⚠️ You already have "${challengeId}" active! 

Complete or stop your current challenge before starting a new one.

This prevents duplicate challenges and ensures fair point distribution. 🛡️`);
      return;
    }
    
    // 🔒 RESTART COOLDOWN: Check if recently completed (prevent immediate restarts)
    const recentCompletion = userCompletedChallenges.find(c => {
      if (c.challengeId !== challengeId) return false;
      const completedDate = new Date(c.completedAt);
      const hoursSinceCompletion = (Date.now() - completedDate.getTime()) / (1000 * 60 * 60);
      return hoursSinceCompletion < 24; // 24-hour cooldown
    });
    
    if (recentCompletion) {
      const completedDate = new Date(recentCompletion.completedAt);
      const hoursRemaining = Math.ceil(24 - (Date.now() - completedDate.getTime()) / (1000 * 60 * 60));
      alert(`🏆 Challenge Recently Completed!

You completed "${challengeId}" recently! To maintain fair play and prevent point farming, please wait ${hoursRemaining} hours before restarting.

Your completion: ${completedDate.toLocaleDateString()} at ${completedDate.toLocaleTimeString()}
Points earned: ${recentCompletion.pointsAwarded || 0} MediPoints

Try a different challenge in the meantime! 🌟`);
      return;
    }
    
    setJoiningStates(prev => ({ ...prev, [challengeId]: true }));
    
    try {
      // Get challenge details for duration information
      const challenge = getAllChallenges().find(c => c.id === challengeId);
      if (!challenge) {
        throw new Error(`Challenge not found: ${challengeId}`);
      }
      // Pre-approval: call specific backend join action when available to enforce Fair Play before optimistic UI updates
      const normalizeChallengeIdForJoin = (id) => (id === 'gentle-morning-walker' ? 'gentle-morning-walk' : id);
      const getJoinActionForChallenge = (id) => {
        switch (id) {
          // Steps
          case 'step-scout': return 'join-step-scout';
          case 'step-streak': return 'join-step-streak';
          case 'step-commander': return 'join-step-commander';
          case 'million-step-challenge': return 'join-million-step-challenge';
          case 'century-step-master': return 'join-century-step-master';
          // Sleep
          case 'sleep-starter': return 'join'; // generic join maps to Sleep Starter
          case 'sleep-champion': return 'join-sleep-champion';
          case 'sleep-rhythm-reset': return 'join-sleep-rhythm-reset';
          case 'century-sleep-reset': return 'join-century-sleep-reset';
          // Morning Walk
          case 'gentle-morning-walker':
          case 'gentle-morning-walk': return 'join-morning-walk';
          // Hydration
          case 'hydration-hero': return 'join-hydration-hero';
          case 'hydration-initiate': return 'join-hydration-initiate';
          case 'hydration-strider': return 'join-hydration-strider';
          case 'hydration-master': return 'join-hydration-master';
          // Heart Rate
          case 'heart-rate-calm': return 'join-heart-rate-calm';
          case 'heart-rate-cadet': return 'join-heart-rate-cadet';
          case 'heart-rate-mover': return 'join-heart-rate-mover';
          case 'heart-rate-legend': return 'join-heart-rate-legend';
          default: return null;
        }
      };
      const joinAction = getJoinActionForChallenge(challengeId);
      
      // 🐛 DEBUG: Check if joinAction is mapped correctly for Century Step Master
      if (challengeId === 'century-step-master') {
        console.log('🔍 CENTURY STEP MASTER JOIN ACTION DEBUG:', {
          challengeId: challengeId,
          joinAction: joinAction,
          hasJoinAction: !!joinAction,
          willUseBackendPath: !!joinAction,
          willUseOptimisticPath: !joinAction
        });
      }
      
      if (joinAction) {
        try {
          // 🐛 DEBUG: Log detailed join request for Century Step Master
          const requestBody = {
            action: joinAction,
            userId: memoizedUserId,
            country: memoizedUserCountry,
            challengeId: normalizeChallengeIdForJoin(challengeId),
            joinTimestamp: new Date().toISOString(),
            challengeData: { startDate: new Date().toISOString() }
          };
          
          // 🔧 FIX: Use subscription from hook as fallback and add to headers
          const effectiveSubscription = userSubscription || hookUserSubscription;
          const subscriptionTier = effectiveSubscription?.tier || effectiveSubscription?.type || 'Standard';
          
          const requestHeaders = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${user?.idToken}`,
            'X-User-Country': memoizedUserCountry
          };
          
          // Add subscription tier to headers if available
          if (subscriptionTier) {
            requestHeaders['X-User-Subscription-Tier'] = subscriptionTier;
          }
          
          if (challengeId === 'century-step-master') {
            console.log('🚫 CENTURY STEP MASTER JOIN REQUEST:', {
              challengeId: challengeId,
              joinAction: joinAction,
              requestBody: requestBody,
              propSubscription: userSubscription,
              hookSubscription: hookUserSubscription,
              effectiveSubscription: effectiveSubscription,
              subscriptionTier: subscriptionTier,
              backendUrl: `${apiConfig.backendUrl}/api/challenges-manager?code=${apiConfig.functionKeys.challengesManager}`,
              headers: requestHeaders
            });
          }
          
          const response = await fetch(`${apiConfig.backendUrl}/api/challenges-manager?code=${apiConfig.functionKeys.challengesManager}`, {
            method: 'POST',
            headers: requestHeaders,
            body: JSON.stringify(requestBody)
          });
          if (response.ok) {
            const resJson = await response.json();
            const payload = resJson?.data || {};
            if (payload?.error === 'FAIR_PLAY_CATEGORY_LIMIT' || resJson?.error === 'FAIR_PLAY_CATEGORY_LIMIT') {
              const msg = payload?.message || resJson?.message || 'You can have only one active challenge in this category.';
              
              // 🐛 DEBUG: Log Fair Play category limit details
              console.log('🚫 FAIR PLAY CATEGORY LIMIT TRIGGERED:', {
                challengeId: challengeId,
                errorMessage: msg,
                payload: payload,
                resJson: resJson,
                currentActiveChallenges: userActiveChallenges?.map(c => ({ id: c.challengeId || c.id, category: c.category }))
              });
              
              setFairPlayBanner({ type: 'error', message: msg });
              alert(`Fair Play Protection: ${msg}`);
              await loadProductionChallengeData();
              setJoiningStates(prev => ({ ...prev, [challengeId]: 'error' }));
              setTimeout(() => setJoiningStates(prev => ({ ...prev, [challengeId]: false })), 2000);
              return; // stop here; do not add optimistically
            }
            if (payload?.success === false) {
              throw new Error(payload?.message || 'Join failed');
            }
            // Success path: backend persisted; refresh from backend to get canonical state
            await loadProductionChallengeData();
            setJoiningStates(prev => ({ ...prev, [challengeId]: 'success' }));
            setTimeout(() => setJoiningStates(prev => ({ ...prev, [challengeId]: false })), 2000);
            const dataTypeForMsg = detectChallengeDataType(challenge);
            const durationTextForMsg = challenge.duration?.includes('days') ? challenge.duration : `${challenge.duration} days`;
            alert(`🎉 Successfully joined "${challenge.name}"! \n\n✅ Challenge is now active\n📊 Your progress will be tracked automatically\n💪 Keep going to earn MediPoints!\n\nChallenge Goal: ${challenge.targetValue || challenge.total} ${dataTypeForMsg}\nDuration: ${durationTextForMsg}`);
            return; // avoid optimistic path below for mapped actions
          } else {
            const errorText = await response.text();
            console.warn(`⚠️ Backend join action failed (${joinAction}):`, errorText);
            
            // 🐛 DEBUG: Log detailed join failure for Century Step Master
            if (challengeId === 'century-step-master') {
              console.log('🚫 CENTURY STEP MASTER JOIN FAILURE:', {
                challengeId: challengeId,
                joinAction: joinAction,
                responseStatus: response.status,
                responseStatusText: response.statusText,
                errorText: errorText,
                userActiveChallenges: userActiveChallenges?.map(c => ({ 
                  id: c.challengeId || c.id, 
                  name: c.name,
                  category: c.category || 'unknown'
                }))
              });
            }
            
            // Do not proceed optimistically for mapped actions to avoid violating Fair Play
            setJoiningStates(prev => ({ ...prev, [challengeId]: 'error' }));
            setTimeout(() => setJoiningStates(prev => ({ ...prev, [challengeId]: false })), 2000);
            return;
          }
        } catch (preErr) {
          console.warn('⚠️ Pre-approval join call failed:', preErr);
          setJoiningStates(prev => ({ ...prev, [challengeId]: 'error' }));
          setTimeout(() => setJoiningStates(prev => ({ ...prev, [challengeId]: false })), 2000);
          return;
        }
      }
      // PRODUCTION: Initialize daily progress tracking from join date forward (no historical data)
      const dataType = detectChallengeDataType(challenge);
      const today = new Date();
      const todayKey = today.toISOString().split('T')[0];
      let optimisticDailyProgress = {};
        try {
          // Initialize with today's progress only - no historical data before join date
          if (healthData?.realTimeMetrics?.steps && dataType === 'steps') {
            optimisticDailyProgress[todayKey] = healthData.realTimeMetrics.steps;
            console.log(`✅ Initialized today's progress: ${todayKey} = ${healthData.realTimeMetrics.steps} steps`);
          } else {
            optimisticDailyProgress[todayKey] = 0;
            console.log(`📊 Initialized today's progress: ${todayKey} = 0 (will update with real data)`);
          }
        } catch (dataLakeError) {
          console.warn('⚠️ Using join-date initialization instead of simulated historical data:', dataLakeError);
          
          // Initialize with today's progress only - no historical simulation
          if (healthData?.realTimeMetrics?.steps && dataType === 'steps') {
            optimisticDailyProgress[todayKey] = healthData.realTimeMetrics.steps;
          } else {
            optimisticDailyProgress[todayKey] = 0;
          }
        }
      
      // Add to active challenges immediately (optimistic update)
      const newActiveChallenge = {
        challengeId: challengeId,
        userId: userId,
        name: challenge.name,
        duration: challenge.duration,
        targetValue: challenge.targetValue || challenge.total,
        trackingMethod: challenge.trackingMethod, // CRITICAL: Include trackingMethod for proper progress calculation
        startDate: new Date().toISOString(),
        status: 'active',
        currentProgress: 0,
        dailyProgress: optimisticDailyProgress, // Azure Data Lake historical data for full duration
        joinTimestamp: new Date().toISOString()
      };
      
      // Remove this challenge from localStorage stopped challenges (if it was stopped before)
      try {
        const stoppedChallenges = JSON.parse(localStorage.getItem(`stoppedChallenges_${memoizedUserId}`) || '[]');
        const updatedStoppedChallenges = stoppedChallenges.filter(id => id !== challengeId);
        localStorage.setItem(`stoppedChallenges_${memoizedUserId}`, JSON.stringify(updatedStoppedChallenges));
        console.log('🗑️ Removed from localStorage stopped challenges:', challengeId);
      } catch (storageError) {
        console.warn('⚠️ localStorage cleanup failed:', storageError);
      }
      
      // CRITICAL FIX: Load existing challenges from Azure first, then merge new challenge
      let existingActiveChallenges = [];
      let existingCompletedChallenges = userCompletedChallenges;
      let existingProgress = challengeProgress;
      
      try {
        // Load current Azure state to ensure we don't overwrite existing challenges
        const response = await fetch(`${apiConfig.backendUrl}/api/challenges-manager?code=${apiConfig.functionKeys.challengesManager}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${user?.idToken}`,
            'X-User-Country': memoizedUserCountry
          },
          body: JSON.stringify({
            action: 'get',
            userId: memoizedUserId,
            country: memoizedUserCountry
          })
        });
        
        if (response.ok) {
          const result = await response.json();
          const azureData = result.data?.challengeData || {};
          existingActiveChallenges = azureData.activeChallenges || [];
          existingCompletedChallenges = azureData.completedChallenges || [];
          existingProgress = azureData.challengeProgress || {};
        }
      } catch (loadError) {
        console.warn('⚠️ Could not load existing challenges from Azure, using local state:', loadError);
        existingActiveChallenges = userActiveChallenges;
      }
      
      // Check if challenge already exists in Azure data (prevent duplicates)
      const challengeExists = existingActiveChallenges.some(c => c.challengeId === challengeId);
      if (challengeExists) {
        console.warn('⚠️ Challenge already exists in Azure data, skipping duplicate addition');
        setJoiningStates(prev => ({ ...prev, [challengeId]: false }));
        return;
      }
      
      // 🚫 DO-NOT-WIPE PROTECTION: Merge Azure and local active challenges (union + dedup)
      const localActiveChallenges = Array.isArray(userActiveChallenges) ? userActiveChallenges : [];
      const byId = new Map();
      // Prefer Azure version when both exist to keep canonical fields, then enrich with local fields
      [...existingActiveChallenges, ...localActiveChallenges].forEach(c => {
        if (!c || !c.challengeId) return;
        if (!byId.has(c.challengeId)) {
          byId.set(c.challengeId, { ...c });
        } else {
          const existing = byId.get(c.challengeId);
          byId.set(c.challengeId, { ...existing, ...c });
        }
      });
      // Add the new challenge
      if (!byId.has(challengeId)) {
        byId.set(challengeId, newActiveChallenge);
      }
      const mergedActiveChallenges = Array.from(byId.values());

      // Merge progress non-destructively (Azure first, then local)
      const mergedProgress = { ...(existingProgress || {}) };
      Object.entries(challengeProgress || {}).forEach(([key, value]) => {
        if (!(key in mergedProgress)) {
          mergedProgress[key] = value;
        } else {
          // Shallow merge for known fields, never clobber with undefined/zero
          mergedProgress[key] = {
            ...mergedProgress[key],
            ...Object.fromEntries(Object.entries(value || {}).filter(([_, v]) => v !== undefined))
          };
        }
      });

      // Update local state with merged union
      setUserActiveChallenges(mergedActiveChallenges);
      setUserCompletedChallenges(existingCompletedChallenges);
      setChallengeProgress(mergedProgress);
      
      console.log(`🎉 Challenge "${challenge.name}" joined and merged with ${existingActiveChallenges.length} existing challenges`);
      // Save merged data to Azure Data Lake and capture any Fair Play warnings/pruning
      try {
        const saveResult = await saveChallengeDataToStorage(mergedActiveChallenges, existingCompletedChallenges, mergedProgress);
        if (saveResult?.warnings?.length) {
          console.warn('⚠️ Fair Play enforcement on save:', saveResult.warnings);
          // If server pruned the newly added challenge due to category conflict, inform user
          const prunedMsg = saveResult.warnings.find(w => w.toLowerCase().includes('category')) || saveResult.warnings[0];
          alert(`Fair Play Protection: ${prunedMsg}`);
        }
      } catch (saveError) {
        console.warn('⚠️ Azure save failed, but challenge is active locally:', saveError);
      }
      
      // Try to sync with API (but don't fail if it doesn't work)
      try {
        const response = await fetch(apiConfig.challenges.joinChallenge, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-functions-key': apiConfig.functionKeys.challengesManager,
            'x-user-country': memoizedUserCountry
          },
          body: JSON.stringify({
            userId: userId,
            challengeId: challengeId,
            country: memoizedUserCountry,
            joinTimestamp: new Date().toISOString(),
            initialHealthMetrics: healthData || {},
          subscriptionTier: userSubscription?.tier || 'basic',
          deviceInfo: {
            platform: navigator.platform,
            userAgent: navigator.userAgent
          }
        })
      });

      if (response.ok) {
        const result = await response.json();
        
        if (result.success) {
          // Use the same Azure Data Lake historical data that was collected in optimistic update
          let backendDailyProgress = optimisticDailyProgress;
          
          // If for some reason we don't have the data, initialize with today's progress only
          if (Object.keys(backendDailyProgress).length === 0) {
            // Initialize with today's progress only - no historical data
            if (healthData?.realTimeMetrics?.steps && dataType === 'steps') {
              backendDailyProgress[todayKey] = healthData.realTimeMetrics.steps;
            } else {
              backendDailyProgress[todayKey] = 0;
            }
          }
          
          console.log('🎯 BACKEND SYNC: Updated challenge with comprehensive data:', {
            challengeId: challengeId,
            daysOfData: Object.keys(backendDailyProgress).length,
            totalSteps: Object.values(backendDailyProgress).reduce((sum, val) => sum + val, 0),
            challenge: challenge.name
          });
          
          // Update the existing challenge instead of adding a new one
          setUserActiveChallenges(prev => {
            const list = Array.isArray(prev) ? prev : [];
            const existsIndex = list.findIndex(c => c.challengeId === challengeId);
            const updated = existsIndex >= 0
              ? list.map(c => c.challengeId === challengeId ? { ...c, dailyProgress: backendDailyProgress, challengeData: result.challengeData || {}, progress: 0, status: 'active' } : c)
              : [...list, { ...newActiveChallenge, dailyProgress: backendDailyProgress }];
            return updated;
          });
          // Success feedback
          setJoiningStates(prev => ({ ...prev, [challengeId]: 'success' }));
          setTimeout(() => {
            setJoiningStates(prev => ({ ...prev, [challengeId]: false }));
          }, 2000);
          
          // console.log('✅ Challenge joined successfully:', result);
          // console.log('🔄 Active challenges updated:', userActiveChallenges.length + 1);
        } else {
          // Handle Fair Play error specifically
          if (result.error === 'FAIR_PLAY_CATEGORY_LIMIT') {
            const msg = result.message || 'You can have only one active challenge in this category.';
            setFairPlayBanner({ type: 'error', message: msg });
            alert(`Fair Play Protection: ${msg}`);
            // Reload from backend to ensure local state reflects server decision
            await loadProductionChallengeData();
            setJoiningStates(prev => ({ ...prev, [challengeId]: 'error' }));
            setTimeout(() => setJoiningStates(prev => ({ ...prev, [challengeId]: false })), 2000);
            return; // Exit early
          }
          throw new Error(result.message || 'Failed to join challenge');
        }
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (apiError) {
      console.warn('⚠️ API sync failed, continuing with Azure Data Lake:', apiError);
      // API failed but Azure Data Lake update already succeeded, so that's fine
    }
    
    // Set success state regardless of API sync
    setJoiningStates(prev => ({ ...prev, [challengeId]: 'success' }));
    
    // Show success message immediately
    const durationText = challenge.duration?.includes('days') ? challenge.duration : `${challenge.duration} days`;
    alert(`🎉 Successfully joined "${challenge.name}"! 
    
✅ Challenge is now active
📊 Your progress will be tracked automatically
💪 Keep going to earn MediPoints!

Challenge Goal: ${challenge.targetValue || challenge.total} ${dataType}
Duration: ${durationText}`);
    
    setTimeout(() => {
      setJoiningStates(prev => ({ ...prev, [challengeId]: false }));
    }, 2000);
    } catch (error) {
    console.error('❌ Error joining challenge:', error);
    
    // Only revert optimistic update for critical errors (not Azure save failures)
    // Check if this is a critical error that should revert the join
    const isCriticalError = error.message?.includes('Challenge not found') || 
                           error.message?.includes('No user ID') ||
                           !userId;
    
    if (isCriticalError) {
      setUserActiveChallenges(prev => prev.filter(c => c.challengeId !== challengeId));
      alert('❌ Failed to join challenge. Please try again.');
    } else {
      alert('⚠️ Challenge joined locally, but cloud sync had issues. Your progress is still tracked!');
    }
    
    setJoiningStates(prev => ({ ...prev, [challengeId]: 'error' }));
    setTimeout(() => {
      setJoiningStates(prev => ({ ...prev, [challengeId]: false }));
    }, 3000);
  }
};

  // Challenge Management Functions - Production Implementation
  const handlePauseChallenge = async (challengeId) => {
    try {
      const userId = user?.localAccountId || user?.username || userInfo?.localAccountId;
      if (!userId) return;
      // Update challenge status to paused locally
      const updatedActiveChallenges = userActiveChallenges.map(c => 
        c.challengeId === challengeId 
          ? { ...c, status: 'paused', pausedAt: new Date().toISOString() }
          : c
      );
      setUserActiveChallenges(updatedActiveChallenges);
      
      // Save to Azure Data Lake immediately
      await saveChallengeDataToStorage(updatedActiveChallenges, userCompletedChallenges, challengeProgress);
      
      // Show immediate feedback
      alert('⏸️ Challenge paused successfully! Resume anytime when ready.');
      
      // Try to sync with API
      try {
        const response = await fetch(`${apiConfig.challenges.pauseChallenge}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-functions-key': apiConfig.functionKeys.challenges,
            'x-user-country': memoizedUserCountry
          },
          body: JSON.stringify({
            userId,
            challengeId,
            pauseReason: 'user_requested',
            timestamp: new Date().toISOString()
          })
        });

        if (response.ok) {
        } else {
          console.warn('⚠️ Server sync failed, but local pause was successful');
        }
      } catch (apiError) {
        console.warn('⚠️ API sync failed, but Azure Data Lake update succeeded:', apiError);
      }
      
      await loadProductionChallengeData();
      
    } catch (error) {
      console.error('❌ Error pausing challenge:', error);
      alert('❌ Error pausing challenge. Please try again.');
    }
  };

  const handleResumeChallenge = async (challengeId) => {
    try {
      const userId = user?.localAccountId || user?.username || userInfo?.localAccountId;
      if (!userId) return;

      // Update challenge status to active locally
      const updatedActiveChallenges = userActiveChallenges.map(c => 
        c.challengeId === challengeId 
          ? { ...c, status: 'active', resumedAt: new Date().toISOString() }
          : c
      );
      setUserActiveChallenges(updatedActiveChallenges);
      
      // Save to Azure Data Lake immediately
      await saveChallengeDataToStorage(updatedActiveChallenges, userCompletedChallenges, challengeProgress);
      
      // Show immediate feedback
      alert('▶️ Challenge resumed successfully! Keep going!');
      
      // Try to sync with API
      try {
        const response = await fetch(`${apiConfig.challenges.resumeChallenge || apiConfig.challenges.joinChallenge}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-functions-key': apiConfig.functionKeys.challenges,
            'x-user-country': memoizedUserCountry
          },
          body: JSON.stringify({
            userId,
            challengeId,
            action: 'resume',
            timestamp: new Date().toISOString()
          })
        });

        if (response.ok) {
        } else {
          console.warn('⚠️ Server sync failed, but local resume was successful');
        }
      } catch (apiError) {
        console.warn('⚠️ API sync failed, but Azure Data Lake update succeeded:', apiError);
      }
      
      await loadProductionChallengeData();
      
    } catch (error) {
      console.error('❌ Error resuming challenge:', error);
      alert('❌ Error resuming challenge. Please try again.');
    }
  };

  const handleStopChallenge = async (challengeId) => {
    try {
      const userId = user?.localAccountId || user?.username || userInfo?.localAccountId;
      if (!userId) {
        alert('❌ User ID not found. Please try again.');
        return;
      }
      
      // Check if already stopping this challenge
      if (stoppingStates[challengeId]) {
        console.warn('⚠️ Challenge is already being stopped:', challengeId);
        return;
      }
      
      // Set stopping state to prevent duplicate operations
      setStoppingStates(prev => ({ ...prev, [challengeId]: true }));
      
      console.log('� STOP CHALLENGE DEBUG - Starting stop process for:', challengeId);
      console.log('�🔍 Current active challenges before stop:', userActiveChallenges.map(c => c.challengeId));
      
      // Check if challenge exists in active list
      const challengeExists = userActiveChallenges.find(c => c.challengeId === challengeId);
      if (!challengeExists) {
        console.warn('⚠️ Challenge not found in active list:', challengeId);
        alert('⚠️ Challenge not found in active list. It may already be stopped.');
        setStoppingStates(prev => ({ ...prev, [challengeId]: false }));
        return;
      }
      
      // SIMPLIFIED APPROACH: Call backend first, then reload fresh data
      console.log('🌐 Calling backend to stop challenge:', challengeId);
      const response = await fetch(`${apiConfig.backendUrl}/api/challenges-manager?code=${apiConfig.functionKeys.challengesManager}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user?.idToken}`,
          'X-User-Country': memoizedUserCountry
        },
        body: JSON.stringify({
          action: 'stop',
          userId: memoizedUserId,
          challengeId: challengeId,
          markAsCompleted: false, // Just stop, don't mark as completed
          country: memoizedUserCountry
        })
      });

      console.log('🌐 Backend stop response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ Backend challenge stop successful:', result);
        
        // SIMPLIFIED: Always reload fresh data from backend after any operation
        await loadProductionChallengeData();
        
        alert('✅ Challenge stopped successfully! The change has been saved.');
      } else {
        console.warn('⚠️ Backend challenge stop failed with status:', response.status);
        const errorText = await response.text();
        console.warn('⚠️ Backend error response:', errorText);
        
        alert('❌ Failed to stop challenge. Please try again or contact support.');
      }
      
      // Clear stopping state
      setStoppingStates(prev => ({ ...prev, [challengeId]: false }));
    } catch (error) {
      console.error('❌ Error stopping challenge:', error);
      setStoppingStates(prev => ({ ...prev, [challengeId]: false }));
      
      alert('❌ Error stopping challenge. Please try again.');
    }
  };

  const handleViewChallengeDetails = (challengeId) => {
    // Find the challenge data from all challenges (not just user active ones)
    const allChallenges = [
      ...(productionChallenges.beginner || []),
      ...(productionChallenges.easy || []),
      ...(productionChallenges.medium || []),
      ...(productionChallenges.hard || []),
      ...(productionChallenges.extreme || []),
      ...(productionChallenges.century || [])
    ];
    const challenge = allChallenges.find(c => c.id === challengeId);
    const userChallenge = userActiveChallenges.find(c => c.challengeId === challengeId);
    
    if (challenge) {
      setSelectedChallengeDetails({ challenge, userChallenge });
      setShowDetailsPopup(true);
    }
  };

  // Challenge Status Check - Production Implementation
  const isUserChallengeActive = (challengeId) => {
    const isActive = userActiveChallenges.some(challenge => 
      challenge.challengeId === challengeId && challenge.status === 'active'
    );
    // Development debug only - commented out for production
    // console.log(`🔍 Checking if challenge ${challengeId} is active:`, isActive);
    // console.log(`🔍 Active challenges:`, userActiveChallenges.map(c => ({ id: c.challengeId, status: c.status })));
    // console.log(`🔍 Total active challenges count:`, userActiveChallenges.length);
    return isActive;
  };

  // Check if challenge is paused
  const isUserChallengePaused = (challengeId) => {
    return userActiveChallenges.some(challenge => 
      challenge.challengeId === challengeId && challenge.status === 'paused'
    );
  };

  // Check if user has this challenge in any state (active or paused)
  // eslint-disable-next-line no-unused-vars
  const hasUserChallenge = (challengeId) => {
    return userActiveChallenges.some(challenge => 
      challenge.challengeId === challengeId
    );
  };

  const getUserChallengeProgress = useCallback((challengeId) => {
    // 🚨 SLEEP SCHEDULE FIX: Block fake progress for non-started challenges
  if (challengeId === 'sleep-rookie' || challengeId === 'sleep-champion' || challengeId === 'sleep-schedule') {
      const isActiveChallenge = userActiveChallenges.some(c => c.challengeId === challengeId);
      const isCompletedChallenge = userCompletedChallenges.some(c => c.challengeId === challengeId);
      
      if (!isActiveChallenge && !isCompletedChallenge) {
        console.warn(`🛑 BLOCKED: sleep-schedule progress blocked - user never started this challenge`);
        return { currentProgress: 0, completed: false, message: 'Challenge not started' };
      }
    }
    
    // 💧 FIXED: Get data from activeChallenges first (source of truth), then fallback to challengeProgress
    const activeChallenge = userActiveChallenges.find(c => c.challengeId === challengeId);
    const storedProgress = challengeProgress[challengeId] || { currentProgress: 0, completed: false };
    
    // Merge data with priority: activeChallenge > storedProgress > defaults
    const mergedProgress = {
      // Prefer the higher of active vs stored to avoid showing 0 when backend snapshot has real streak
      currentProgress: Math.max(
        Number(activeChallenge?.currentProgress ?? 0),
        Number(storedProgress.currentProgress ?? 0)
      ),
      consecutiveDays: Math.max(
        Number(activeChallenge?.consecutiveDays ?? 0),
        Number(storedProgress.consecutiveDays ?? 0)
      ),
      completed: storedProgress.completed || false,
      message: storedProgress.message || 'In progress',
      dailyProgressPercentage: storedProgress.dailyProgressPercentage ?? 0,
      // Add specific progress fields
      sleepHours: storedProgress.sleepHours ?? 0,
      waterGlasses: storedProgress.waterGlasses ?? 0,
      heartRate: storedProgress.heartRate ?? 0
    };
    
    // Try to get real-time progress from health data if available
    // Development debug - commented out for production
    // console.log('🔍 getUserChallengeProgress - healthData:', healthData);
    
    // Check multiple data sources for real-time health data (using same logic as LandingPage)
    let realtimeSteps = 0;
    if (healthData?.realTimeMetrics?.steps) {
      realtimeSteps = healthData.realTimeMetrics.steps;
      // Reduced logging to prevent console spam - only log when value changes
      if (window.lastLoggedSteps !== realtimeSteps) {
        window.lastLoggedSteps = realtimeSteps;
      }
    } else if (healthData?.activity?.steps?.value) {
      realtimeSteps = healthData.activity.steps.value;
      if (window.lastLoggedSteps !== realtimeSteps) {
        window.lastLoggedSteps = realtimeSteps;
      }
    }
    
    if (realtimeSteps > 0) {
      const challenge = userActiveChallenges.find(c => c.challengeId === challengeId);
      if (challenge) {
        // PRODUCTION FIX: Use comprehensive step challenge identification for enhanced tracking
        if (isStepChallenge(challengeId)) {
          // 🔧 ROBUST STEP TRACKING FIX - Prevent stale data multiplication
          const today = new Date();
          const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          const todayKey = today.toISOString().split('T')[0];
          
          // 🕐 CRITICAL: Validate data freshness - handle missing lastSync gracefully
          const lastUpdate = healthData?.lastSync ? new Date(healthData.lastSync) : null;
          const dataAge = lastUpdate ? (Date.now() - lastUpdate.getTime()) / (1000 * 60 * 60) : 999; // hours
          const isDataFresh = lastUpdate && lastUpdate >= todayStart && dataAge < 24;
          console.log('- Last health data update:', lastUpdate?.toLocaleString() || 'Unknown');
          console.log('- Data age (hours):', dataAge.toFixed(1));
          // 🚨 SMART LOGIC: Use fresh data if available, otherwise fall back to stored data
          const todaysSteps = isDataFresh ? realtimeSteps : 0;
          
          if (!lastUpdate) {
          } else if (!isDataFresh && realtimeSteps > 0) {
            console.warn('⚠️ STALE DATA DETECTED: Step count may be from previous day, not counting for today');
          }
          
          // � COMPREHENSIVE CUMULATIVE CALCULATION - Fixed for Individual Challenge Tracking
          // 🔍 CRITICAL FIX: Ensure each challenge has its own unique dailyProgress
          let dailyProgress = challenge.dailyProgress || {};
          
          // Use only real-time data and stored progress (no hardcoded test data)
          
          // 📅 PROPER DATE RANGE CALCULATION
          const startDate = new Date(challenge.startDate);
          startDate.setHours(0, 0, 0, 0); // Normalize to start of day
          
          const todayDateVar = new Date();
          todayDateVar.setHours(0, 0, 0, 0); // Normalize to start of today
          
          console.log(`� Date range: ${startDate.toISOString().split('T')[0]} to ${today.toISOString().split('T')[0]}`);
          
          // 🧮 COMPREHENSIVE CUMULATIVE CALCULATION
          let cumulativeSteps = 0;
          let validDaysCount = 0;
          let missingDaysCount = 0;
          let dailyBreakdown = [];
          
          // Calculate total days from challenge start to today (inclusive)
          const totalDaysSinceStart = Math.floor((today - startDate) / (1000 * 60 * 60 * 24)) + 1;
          // 🔄 ITERATE THROUGH ALL DAYS FROM START TO TODAY
          for (let dayOffset = 0; dayOffset < totalDaysSinceStart; dayOffset++) {
            const currentDate = new Date(startDate);
            currentDate.setDate(startDate.getDate() + dayOffset);
            const dateKey = currentDate.toISOString().split('T')[0];
            const isToday = dateKey === todayKey;
            
            // 📊 GET STEPS FOR THIS SPECIFIC DATE AND CHALLENGE
            let daySteps = dailyProgress[dateKey] || 0;
            
            // 🚨 SPECIAL HANDLING FOR TODAY: Use real-time data if available and higher
            if (isToday && todaysSteps > 0) {
              daySteps = Math.max(daySteps, todaysSteps);
            }
            
            // 📝 RECORD THIS DAY'S DATA
            const dayData = {
              date: dateKey,
              dayNumber: dayOffset + 1,
              steps: daySteps,
              isToday: isToday,
              hasData: daySteps > 0,
              source: isToday && todaysSteps > daySteps ? 'realtime' : 'stored'
            };
            
            dailyBreakdown.push(dayData);
            
            // 🧮 ADD TO CUMULATIVE TOTAL
            if (daySteps > 0) {
              cumulativeSteps += daySteps;
              validDaysCount++;
              console.log(`✅ Day ${dayOffset + 1} (${dateKey}): ${daySteps} steps ${isToday ? '[TODAY]' : '[HISTORICAL]'} - CUMULATIVE NOW: ${cumulativeSteps}`);
            } else {
              missingDaysCount++;
              console.log(`❌ Day ${dayOffset + 1} (${dateKey}): No data ${isToday ? '[TODAY - WAITING]' : '[MISSING]'} - CUMULATIVE UNCHANGED: ${cumulativeSteps}`);
            }
          }
          
          // � FINAL CALCULATION VERIFICATION - Using comprehensive loop results
          // Get today's steps for metadata (already included in cumulative)
          const todayStepsFromBreakdown = dailyBreakdown.find(d => d.isToday)?.steps || 0;
          
          // 📈 DATA QUALITY REPORTING
          console.log(`- Data quality: ${missingDaysCount === 0 ? 'Perfect' : missingDaysCount < 3 ? 'Good' : 'Needs attention'}`);
          
          return {
            currentProgress: cumulativeSteps,
            cumulativeSteps: cumulativeSteps,
            todaysSteps: todayStepsFromBreakdown, // Use today's steps from breakdown
            dailyProgress: dailyProgress,
            completed: cumulativeSteps >= (challenge.targetValue || challenge.total || 3000),
            lastUpdated: new Date().toISOString(),
            source: 'realtime_enhanced',
            // 🔧 NEW: Data quality indicators
            dataQuality: {
              isDataFresh: isDataFresh,
              validDays: validDaysCount,
              missingDays: missingDaysCount,
              dataAge: dataAge,
              lastSync: lastUpdate?.toISOString(),
              qualityScore: missingDaysCount === 0 ? 'perfect' : missingDaysCount < 3 ? 'good' : 'needs_attention'
            }
          };
        }
        // For hydration challenges (exclude dedicated Hydration Hero and Initiate which are handled below)
        if (challengeId.includes('hydration') && challengeId !== 'hydration-hero' && challengeId !== 'hydration-initiate') {
          const waterIntake = healthData?.realTimeMetrics?.waterIntake || 
                            healthData?.wellness?.hydration?.waterIntake?.value || 0;
          // console.log('💧 Real-time water intake for challenge:', waterIntake, 'stored:', storedProgress.currentProgress);
          return {
            currentProgress: Math.max(waterIntake, storedProgress.currentProgress),
            completed: waterIntake >= (challenge.targetValue || challenge.total || 8),
            lastUpdated: new Date().toISOString(),
            source: 'realtime'
          };
        }
  // For sleep challenges - Extract from correct path in health data
  // Exclude sleep-starter, sleep-rookie, sleep-champion, sleep-rhythm-reset, and century-sleep-reset which have dedicated handling below
  if (challengeId.includes('sleep') && challengeId !== 'sleep-starter' && challengeId !== 'sleep-rookie' && challengeId !== 'sleep-champion' && challengeId !== 'sleep-rhythm-reset' && challengeId !== 'century-sleep-reset') {
          const sleepHours = healthData?.healthMetrics?.lifestyle?.sleep?.value || 
                           healthData?.realTimeMetrics?.sleep || 
                           healthData?.wellness?.sleep?.totalSleep?.value || 0;
          
          console.log('😴 Real-time sleep hours for challenge:', sleepHours, 'Challenge ID:', challengeId, 'Health data path:', healthData?.healthMetrics?.lifestyle?.sleep);
          
          const result = {
            currentProgress: Math.max(sleepHours, storedProgress.currentProgress),
            completed: sleepHours >= (challenge.targetValue || challenge.total || 7),
            lastUpdated: new Date().toISOString(),
            source: 'realtime',
            sleepHours: sleepHours,
            consecutiveDays: storedProgress.consecutiveDays || 0
          };
          
          console.log('😴 Sleep challenge progress result:', result);
          return result;
        }
        
        // 🌙 SLEEP STARTER: Get actual progress from challenge data instead of hardcoded zeros
        if (challengeId === 'sleep-starter') {
          console.log('🌙 Sleep Starter getting actual progress from challenge data');
          
          // Find the actual Sleep Starter challenge from userActiveChallenges
          const sleepChallenge = userActiveChallenges.find(c => c.challengeId === 'sleep-starter');
          
          if (sleepChallenge) {
            // Get today's sleep hours from health data
            const todaysSleepHours = healthData?.healthMetrics?.lifestyle?.sleep?.value || 
                                   healthData?.realTimeMetrics?.sleep || 0;
            const today = new Date().toISOString().split('T')[0];
            const todaysProgress = sleepChallenge.dailyProgress?.[today] || todaysSleepHours;
            // Merge consecutiveDays with any stored progress snapshot to avoid transient zeros
            const mergedConsecutiveDays = Math.max(
              Number(sleepChallenge.consecutiveDays || 0),
              Number(storedProgress.consecutiveDays || 0)
            );
            const mergedCurrentProgress = Math.max(
              Number(sleepChallenge.currentProgress || 0),
              Number(storedProgress.currentProgress || 0),
              mergedConsecutiveDays
            );
            const actualProgress = {
              currentProgress: mergedCurrentProgress,
              completed: sleepChallenge.status === 'completed',
              consecutiveDays: mergedConsecutiveDays,
              sleepHours: todaysSleepHours,
              todaysProgress: todaysProgress,
              todaysPercentage: Math.min((todaysProgress / 7) * 100, 100),
              lastValidated: sleepChallenge.lastValidated || null,
              dailyProgress: sleepChallenge.dailyProgress || {},
              message: sleepChallenge.consecutiveDays >= 7 ? 'Challenge completed!' : 'Tracking sleep progress...',
              source: 'challenge-data'
            };
            
            console.log('🌙 Sleep Starter actual progress:', actualProgress);
            return actualProgress;
          }
          
          // Fallback if challenge not found
          return {
            currentProgress: 0,
            completed: false,
            consecutiveDays: 0,
            sleepHours: 0,
            todaysProgress: 0,
            todaysPercentage: 0,
            lastValidated: null,
            message: 'Sleep Starter not found - please join challenge',
            source: 'not-found'
          };
        }
        
  // 🌙 SLEEP ROOKIE: Same as Sleep Starter but 21-night duration
        if (challengeId === 'sleep-rookie') {
          console.log('🌙 Sleep Rookie getting actual progress from challenge data');
          const sleepChallenge = userActiveChallenges.find(c => c.challengeId === 'sleep-rookie');
          if (sleepChallenge) {
            const todaysSleepHours = healthData?.healthMetrics?.lifestyle?.sleep?.value || 
                                     healthData?.realTimeMetrics?.sleep || 0;
            const today = new Date().toISOString().split('T')[0];
            const todaysProgress = sleepChallenge.dailyProgress?.[today] || todaysSleepHours;
            // Merge consecutiveDays with stored snapshot to avoid temporary 0/21 display after validation
            const mergedConsecutiveDays = Math.max(
              Number(sleepChallenge.consecutiveDays || 0),
              Number(storedProgress.consecutiveDays || 0)
            );
            const mergedCurrentProgress = Math.max(
              Number(sleepChallenge.currentProgress || 0),
              Number(storedProgress.currentProgress || 0),
              mergedConsecutiveDays
            );
            const actualProgress = {
              currentProgress: mergedCurrentProgress,
              completed: sleepChallenge.status === 'completed',
              consecutiveDays: mergedConsecutiveDays,
              sleepHours: todaysSleepHours,
              todaysProgress: todaysProgress,
              // Daily target remains 7 hours
              todaysPercentage: Math.min((todaysProgress / 7) * 100, 100),
              lastValidated: sleepChallenge.lastValidated || null,
              dailyProgress: sleepChallenge.dailyProgress || {},
              message: (sleepChallenge.consecutiveDays || 0) >= 21 ? 'Challenge completed!' : 'Tracking sleep progress...',
              source: 'challenge-data'
            };
            console.log('🌙 Sleep Rookie actual progress:', actualProgress);
            return actualProgress;
          }
          return {
            currentProgress: 0,
            completed: false,
            consecutiveDays: 0,
            sleepHours: 0,
            todaysProgress: 0,
            todaysPercentage: 0,
            lastValidated: null,
            message: 'Sleep Rookie not found - please join challenge',
            source: 'not-found'
          };
        }

  // 🌙 SLEEP CHAMPION: Same pattern as Sleep Rookie but 60-night duration
        if (challengeId === 'sleep-champion') {
          console.log('🌙 Sleep Champion getting actual progress from challenge data');
          const sleepChallenge = userActiveChallenges.find(c => c.challengeId === 'sleep-champion');
          if (sleepChallenge) {
            const todaysSleepHours = healthData?.healthMetrics?.lifestyle?.sleep?.value || 
                                     healthData?.realTimeMetrics?.sleep || 0;
            const today = new Date().toISOString().split('T')[0];
            const todaysProgress = sleepChallenge.dailyProgress?.[today] || todaysSleepHours;
            const mergedConsecutiveDays = Math.max(
              Number(sleepChallenge.consecutiveDays || 0),
              Number(storedProgress.consecutiveDays || 0)
            );
            const mergedCurrentProgress = Math.max(
              Number(sleepChallenge.currentProgress || 0),
              Number(storedProgress.currentProgress || 0),
              mergedConsecutiveDays
            );
            const actualProgress = {
              currentProgress: mergedCurrentProgress,
              completed: sleepChallenge.status === 'completed',
              consecutiveDays: mergedConsecutiveDays,
              sleepHours: todaysSleepHours,
              todaysProgress: todaysProgress,
              // Daily target remains 7 hours
              todaysPercentage: Math.min((todaysProgress / 7) * 100, 100),
              lastValidated: sleepChallenge.lastValidated || null,
              dailyProgress: sleepChallenge.dailyProgress || {},
              message: (sleepChallenge.consecutiveDays || 0) >= 60 ? 'Challenge completed!' : 'Tracking sleep progress...',
              source: 'challenge-data'
            };
            console.log('🌙 Sleep Champion actual progress:', actualProgress);
            return actualProgress;
          }

          // 🌙 SLEEP RHYTHM RESET: Same pattern as Sleep Champion but 90-night duration
          if (challengeId === 'sleep-rhythm-reset') {
            console.log('🌙 Sleep Rhythm Reset getting actual progress from challenge data');
            const sleepChallenge = userActiveChallenges.find(c => c.challengeId === 'sleep-rhythm-reset');
            if (sleepChallenge) {
              const todaysSleepHours = healthData?.healthMetrics?.lifestyle?.sleep?.value || 
                                       healthData?.realTimeMetrics?.sleep || 0;
              const today = new Date().toISOString().split('T')[0];
              const todaysProgress = sleepChallenge.dailyProgress?.[today] || todaysSleepHours;
              const mergedConsecutiveDays = Math.max(
                Number(sleepChallenge.consecutiveDays || 0),
                Number(storedProgress.consecutiveDays || 0)
              );
              const mergedCurrentProgress = Math.max(
                Number(sleepChallenge.currentProgress || 0),
                Number(storedProgress.currentProgress || 0),
                mergedConsecutiveDays
              );
              const actualProgress = {
                currentProgress: mergedCurrentProgress,
                completed: sleepChallenge.status === 'completed',
                consecutiveDays: mergedConsecutiveDays,
                sleepHours: todaysSleepHours,
                todaysProgress: todaysProgress,
                // Daily target remains 7 hours
                todaysPercentage: Math.min((todaysProgress / 7) * 100, 100),
                lastValidated: sleepChallenge.lastValidated || null,
                dailyProgress: sleepChallenge.dailyProgress || {},
                message: (sleepChallenge.consecutiveDays || 0) >= 90 ? 'Challenge completed!' : 'Tracking sleep progress...',
                source: 'challenge-data'
              };
              console.log('🌙 Sleep Rhythm Reset actual progress:', actualProgress);
              return actualProgress;
            }
            return {
              currentProgress: 0,
              completed: false,
              consecutiveDays: 0,
              sleepHours: 0,
              todaysProgress: 0,
              todaysPercentage: 0,
              lastValidated: null,
              message: 'Sleep Rhythm Reset not found - please join challenge',
              source: 'not-found'
            };
          }
          return {
            currentProgress: 0,
            completed: false,
            consecutiveDays: 0,
            sleepHours: 0,
            todaysProgress: 0,
            todaysPercentage: 0,
            lastValidated: null,
            message: 'Sleep Champion not found - please join challenge',
            source: 'not-found'
          };
        }

        // 🌙 CENTURY SLEEP RESET: 100-night sleep streak at 7h/night
        if (challengeId === 'century-sleep-reset') {
          console.log('🌙 Century Sleep Reset getting actual progress from challenge data');
          const sleepChallenge = userActiveChallenges.find(c => c.challengeId === 'century-sleep-reset');
          if (sleepChallenge) {
            const todaysSleepHours = healthData?.healthMetrics?.lifestyle?.sleep?.value ||
                                     healthData?.realTimeMetrics?.sleep || 0;
            const today = new Date().toISOString().split('T')[0];
            const todaysProgress = sleepChallenge.dailyProgress?.[today] || todaysSleepHours;
            const mergedConsecutiveDays = Math.max(
              Number(sleepChallenge.consecutiveDays || 0),
              Number(storedProgress.consecutiveDays || 0)
            );
            const mergedCurrentProgress = Math.max(
              Number(sleepChallenge.currentProgress || 0),
              Number(storedProgress.currentProgress || 0),
              mergedConsecutiveDays
            );
            const actualProgress = {
              currentProgress: mergedCurrentProgress,
              completed: sleepChallenge.status === 'completed',
              consecutiveDays: mergedConsecutiveDays,
              sleepHours: todaysSleepHours,
              todaysProgress: todaysProgress,
              todaysPercentage: Math.min((todaysProgress / 7) * 100, 100),
              lastValidated: sleepChallenge.lastValidated || null,
              dailyProgress: sleepChallenge.dailyProgress || {},
              message: (sleepChallenge.consecutiveDays || 0) >= 100 ? 'Challenge completed!' : 'Tracking sleep progress...',
              source: 'challenge-data'
            };
            console.log('🌙 Century Sleep Reset actual progress:', actualProgress);
            return actualProgress;
          }
          return {
            currentProgress: 0,
            completed: false,
            consecutiveDays: 0,
            sleepHours: 0,
            todaysProgress: 0,
            todaysPercentage: 0,
            lastValidated: null,
            message: 'Century Sleep Reset not found - please join challenge',
            source: 'not-found'
          };
        }
        
        // 💧 HYDRATION HERO: Handle like Sleep Starter with consecutive days
        if (challengeId === 'hydration-hero') {
          console.log('💧 Hydration Hero getting actual progress from challenge data');
          
          const hydrationChallenge = userActiveChallenges.find(c => c.challengeId === 'hydration-hero');
          
          if (hydrationChallenge) {
            // Get today's water intake from health data
            const waterML = healthData?.healthMetrics?.lifestyle?.waterIntake?.value ||
                          healthData?.realTimeMetrics?.water ||
                          healthData?.wellness?.hydration?.ml || 0;
            const waterGlasses = Math.floor(waterML / 240); // 1 glass = 240ml
            const today = new Date().toISOString().split('T')[0];
            const todaysProgress = hydrationChallenge.dailyProgress?.[today] || waterGlasses;
            
            const actualProgress = {
              currentProgress: hydrationChallenge.currentProgress || hydrationChallenge.consecutiveDays || 0,
              completed: hydrationChallenge.status === 'completed',
              consecutiveDays: hydrationChallenge.consecutiveDays || 0,
              waterGlasses: waterGlasses,
              waterML: waterML,
              todaysProgress: todaysProgress,
              // Prefer backend-provided daily progress percentage when available
              dailyProgressPercentage: (challengeProgress['hydration-hero']?.dailyProgressPercentage ?? Math.min((todaysProgress / 7) * 100, 100)),
              todaysPercentage: Math.min((todaysProgress / 7) * 100, 100), // 7 glasses target
              lastValidated: hydrationChallenge.lastValidated || null,
              dailyProgress: hydrationChallenge.dailyProgress || {},
              message: hydrationChallenge.consecutiveDays >= 7 ? 'Challenge completed!' : 'Tracking hydration progress...',
              source: 'challenge-data'
            };
            
            console.log('💧 Hydration Hero actual progress:', actualProgress);
            return actualProgress;
          }

          // 💦 HYDRATION INITIATE: 21-day hydration challenge like Sleep Rookie semantics
          if (challengeId === 'hydration-initiate') {
            console.log('💦 Hydration Initiate getting actual progress from challenge data');
            const hydrationChallenge = userActiveChallenges.find(c => c.challengeId === 'hydration-initiate');
            if (hydrationChallenge) {
              // Get today's water intake from health data
              const waterML = healthData?.healthMetrics?.lifestyle?.waterIntake?.value ||
                              healthData?.realTimeMetrics?.waterIntake ||
                              healthData?.wellness?.hydration?.ml || 0;
              const waterGlassesExplicit = healthData?.realTimeMetrics?.water || 0;
              const derivedGlasses = waterML > 0 ? Math.floor(waterML / 240) : 0;
              const waterGlasses = waterGlassesExplicit > 0 ? waterGlassesExplicit : derivedGlasses;
              const today = new Date().toISOString().split('T')[0];
              const todaysProgress = hydrationChallenge.dailyProgress?.[today] || waterGlasses;
              const actualProgress = {
                currentProgress: hydrationChallenge.currentProgress || hydrationChallenge.consecutiveDays || 0,
                completed: hydrationChallenge.status === 'completed',
                consecutiveDays: hydrationChallenge.consecutiveDays || 0,
                waterGlasses: waterGlasses,
                waterML: waterML,
                todaysProgress: todaysProgress,
                // Prefer backend-provided daily progress percentage when available (target 7 glasses)
                dailyProgressPercentage: (challengeProgress['hydration-initiate']?.dailyProgressPercentage ?? Math.min((todaysProgress / 7) * 100, 100)),
                todaysPercentage: Math.min((todaysProgress / 7) * 100, 100),
                lastValidated: hydrationChallenge.lastValidated || null,
                dailyProgress: hydrationChallenge.dailyProgress || {},
                message: (hydrationChallenge.consecutiveDays || 0) >= 21 ? 'Challenge completed!' : 'Tracking hydration progress...',
                source: 'challenge-data'
              };
              console.log('💦 Hydration Initiate actual progress:', actualProgress);
              return actualProgress;
            }
            return {
              currentProgress: 0,
              completed: false,
              consecutiveDays: 0,
              waterGlasses: 0,
              waterML: 0,
              todaysProgress: 0,
              todaysPercentage: 0,
              lastValidated: null,
              message: 'Hydration Initiate not found - please join challenge',
              source: 'not-found'
            };
          }
          
          return {
            currentProgress: 0,
            completed: false,
            consecutiveDays: 0,
            waterGlasses: 0,
            waterML: 0,
            todaysProgress: 0,
            todaysPercentage: 0,
            lastValidated: null,
            message: 'Hydration Hero not found - please join challenge',
            source: 'not-found'
          };
        }
        
        // 💓 HEART RATE CALM/CADET: Handle like Sleep Starter with consecutive days (7 vs 21)
        if (challengeId === 'heart-rate-calm' || challengeId === 'heart-rate-cadet') {
          console.log('💓 Heart Rate getting actual progress from challenge data:', challengeId);
          
          const heartRateChallenge = userActiveChallenges.find(c => c.challengeId === challengeId);
          
          if (heartRateChallenge) {
            // Get today's resting heart rate from health data
            const restingHeartRate =
              // Prefer normalized healthMetrics path
              (healthData?.healthMetrics?.vitalSigns?.heartRate?.value ??
               healthData?.realTimeMetrics?.heartRate ??
               healthData?.vitals?.heartRate?.resting ??
               healthData?.realTimeMetrics?.restingHeartRate ?? 0);
            const today = new Date().toISOString().split('T')[0];
            const todaysHeartRate = heartRateChallenge.dailyProgress?.[today] || restingHeartRate;
            
            // For heart rate, lower is better, so calculate percentage differently
            // If heart rate is below 70 bpm, it's 100% achieved
            const targetHR = heartRateChallenge?.targetValue || 70;
            const todaysPercentage = todaysHeartRate > 0 ? 
              (todaysHeartRate <= targetHR ? 100 : Math.max(0, 100 - ((todaysHeartRate - targetHR) * 5))) : 0;
            
            const actualProgress = {
              currentProgress: heartRateChallenge.currentProgress || heartRateChallenge.consecutiveDays || 0,
              completed: heartRateChallenge.status === 'completed',
              consecutiveDays: heartRateChallenge.consecutiveDays || 0,
              restingHeartRate: restingHeartRate,
              todaysProgress: todaysHeartRate,
              // Prefer backend-provided daily progress percentage when available
              dailyProgressPercentage: (challengeProgress[challengeId]?.dailyProgressPercentage ?? todaysPercentage),
              todaysPercentage: todaysPercentage,
              lastValidated: heartRateChallenge.lastValidated || null,
              dailyProgress: heartRateChallenge.dailyProgress || {},
              message: heartRateChallenge.consecutiveDays >= (challengeId === 'heart-rate-cadet' ? 21 : 7) ? 'Challenge completed!' : 'Tracking heart rate progress...',
              source: 'challenge-data'
            };
            
            console.log('💓 Heart Rate actual progress:', actualProgress);
            return actualProgress;
          }
          
          return {
            currentProgress: 0,
            completed: false,
            consecutiveDays: 0,
            restingHeartRate: 0,
            todaysProgress: 0,
            todaysPercentage: 0,
            lastValidated: null,
            message: `${challengeId === 'heart-rate-cadet' ? 'Heart Rate Cadet' : 'Heart Rate Calm'} not found - please join challenge`,
            source: 'not-found'
          };
        }
      }
    }
    
    // Handle specific challenge types based on trackingMethod
    const challenge = userActiveChallenges.find(c => c.challengeId === challengeId);
    let trackingMethod = challenge?.trackingMethod;
    
    // FALLBACK: Detect trackingMethod for challenges loaded without it
    if (!trackingMethod && challengeId === 'gentle-morning-walker') {
      trackingMethod = 'steps_before_time';
    }
    
    if (challenge && trackingMethod) {
      switch (trackingMethod) {
        case 'steps_before_time':
          // For time-specific step challenges like Gentle Morning Walker (500 steps before 9AM daily)
          let dailyProgress = challenge.dailyProgress || {};
          const today = new Date().toISOString().split('T')[0];
          const todayStart = new Date();
          todayStart.setHours(0, 0, 0, 0);
          
          // Use only stored challenge progress data (no hardcoded test data)
          // 🗓️ CALCULATE CHALLENGE DAY - Critical for proper day tracking
          const challengeStartDate = new Date(challenge.startDate);
          challengeStartDate.setHours(0, 0, 0, 0); // Start of challenge day
          const todayDate = new Date();
          todayDate.setHours(0, 0, 0, 0); // Start of today
          
          const daysDifference = Math.floor((todayDate - challengeStartDate) / (1000 * 60 * 60 * 24));
          const currentChallengeDay = Math.max(1, daysDifference + 1); // Day 1, 2, 3, etc.
          const targetDays = challenge.targetValue || 7;
          
          // Get today's steps from real-time data if available
          let todayStepsFromRealTime = 0;
          if (healthData?.realTimeMetrics?.steps) {
            todayStepsFromRealTime = healthData.realTimeMetrics.steps;
          } else if (healthData?.activity?.steps?.value) {
            todayStepsFromRealTime = healthData.activity.steps.value;
          }
          
          // Count successful days (days where the daily goal was met)
          let successfulDays = 0;
          let totalDaysInChallenge = Math.min(currentChallengeDay, targetDays);
          let dailyBreakdown = [];
          console.log(`   - Today's date: ${today}`);
          // Analyze each day from start to current day
          for (let dayNum = 1; dayNum <= totalDaysInChallenge; dayNum++) {
            const challengeDate = new Date(challengeStartDate);
            challengeDate.setDate(challengeStartDate.getDate() + (dayNum - 1));
            const dateKey = challengeDate.toISOString().split('T')[0];
            
            let daySteps = dailyProgress[dateKey] || 0;
            
            // For today, use real-time data if it's higher than stored data
            if (dateKey === today) {
              daySteps = Math.max(daySteps, todayStepsFromRealTime);
            }
            
            const goalMet = daySteps >= 500;
            if (goalMet) {
              successfulDays++;
            }
            
            // Create daily breakdown for detailed tracking
            dailyBreakdown.push({
              day: dayNum,
              date: dateKey,
              steps: daySteps,
              goalMet: goalMet,
              isToday: dateKey === today,
              status: goalMet ? '✅ Success' : (dateKey === today ? '⏳ In Progress' : '❌ Not Met'),
              dataSource: 'Real-time'
            });
            
            console.log(`   Day ${dayNum} (${dateKey}): ${daySteps} steps - ${goalMet ? '✅ SUCCESS' : '❌ NOT MET'}${dateKey === today ? ' [TODAY]' : ''} [REAL-TIME]`);
          }
          
          // Check if today's goal has been met (using real-time data)
          const todaySteps = Math.max(dailyProgress[today] || 0, todayStepsFromRealTime);
          const todaysGoalMet = todaySteps >= 500;
          
          // Calculate completion status
          const isCompleted = successfulDays >= targetDays;
          const progressPercentage = Math.min((successfulDays / targetDays) * 100, 100);
          
          // Enhanced progress messages
          let progressMessage = '';
          let dayProgressMessage = '';
          
          if (currentChallengeDay > targetDays) {
            if (isCompleted) {
              progressMessage = `🎉 Challenge completed! ${successfulDays} out of ${targetDays} morning walks achieved`;
              dayProgressMessage = 'Challenge Complete';
            } else {
              progressMessage = `⚠️ Challenge failed. Only ${successfulDays} out of ${targetDays} morning walks completed`;
              dayProgressMessage = 'Challenge Failed';
            }
          } else {
            progressMessage = `${successfulDays} out of ${targetDays} morning walks completed`;
            dayProgressMessage = `Day ${currentChallengeDay} of ${targetDays}`;
            
            if (todaysGoalMet) {
              progressMessage += ` • Today's goal: ✅ ACHIEVED (${todaySteps} steps)`;
            } else {
              const remaining = 500 - todaySteps;
              progressMessage += ` • Today's goal: ${remaining > 0 ? `${remaining} more steps needed` : 'In progress'}`;
            }
          }
          
          const resultData = {
            // Main progress indicators
            currentProgress: successfulDays,
            successfulDays: successfulDays,
            targetDays: targetDays,
            
            // Day tracking
            currentChallengeDay: currentChallengeDay,
            totalDaysInChallenge: totalDaysInChallenge,
            dayProgressMessage: dayProgressMessage,
            
            // Today's specific data
            todaysGoalMet: todaysGoalMet,
            todaysSteps: todaySteps,
            
            // Completion status
            completed: isCompleted,
            progressPercentage: progressPercentage,
            
            // Detailed tracking
            dailyBreakdown: dailyBreakdown,
            
            // Messages and metadata
            message: progressMessage,
            lastUpdated: new Date().toISOString(),
            source: 'enhanced_daily_consistency'
          };
          
          console.log(`🎯 ENHANCED Final Result:`, {
            challengeDay: `${currentChallengeDay} of ${targetDays}`,
            successfulDays: `${successfulDays}/${targetDays}`,
            todayProgress: `${todaySteps} steps (goal: ${todaysGoalMet ? 'MET' : 'NOT MET'})`,
            completion: `${progressPercentage.toFixed(1)}%`
          });
          
          return resultData;
          
        case 'sugar_monitoring':
          // For glucose monitoring challenges like Low Sugar Champion
          return {
            ...storedProgress,
            todaysGlucose: storedProgress.todaysGlucose || 0,
            cumulativeSuccessfulDays: storedProgress.cumulativeSuccessfulDays || 0,
            message: storedProgress.message || 'No glucose monitoring device connected'
          };
          
        case 'blood_pressure_log':
          // 🩺 PRODUCTION SAFEGUARD: Only use real device data, never fake/simulated data
          const hasRealBPDevice = healthData?.connectedDevices?.includes('withings-bpm') || 
                                 healthData?.connectedDevices?.includes('omron-bp') || 
                                 healthData?.realTimeMetrics?.bloodPressure?.source === 'device';
          
          return {
            ...storedProgress,
            // 🚨 PRODUCTION FIX: If no real device, force all readings to 0
            todaysBPReadings: hasRealBPDevice ? (storedProgress.todaysBPReadings || 0) : 0,
            cumulativeReadings: hasRealBPDevice ? (storedProgress.cumulativeReadings || 0) : 0,
            message: hasRealBPDevice ? (storedProgress.message || 'Device connected') : 'No blood pressure monitoring device connected'
          };
          
        default:
          break;
      }
    }
    
    // 💧 FIXED: Return merged progress (activeChallenges data takes priority)
    console.log(`🔍 getUserChallengeProgress(${challengeId}):`, {
      activeConsecutive: activeChallenge?.consecutiveDays,
      storedConsecutive: storedProgress.consecutiveDays,
      finalConsecutive: mergedProgress.consecutiveDays,
      dailyProgressPercentage: mergedProgress.dailyProgressPercentage
    });
    
    return mergedProgress;
  }, [challengeProgress, healthData, userActiveChallenges, userCompletedChallenges]);

  // =====================================================================
  // 🚀 ENHANCED PROGRESS TRACKING - Total Progress Calculation & Real-time Updates
  // =====================================================================
  
  // NEW: Calculate Total Progress for All Active Challenges
  const calculateTotalProgress = useCallback((activeChallenges, progressData) => {
    let totalProgress = 0;
    let totalTarget = 0;
    
    activeChallenges.forEach(challenge => {
      const challengeProgress = getUserChallengeProgress(challenge.challengeId);
      const target = challenge.targetValue || challenge.total || 100;
      
      totalProgress += challengeProgress.currentProgress || 0;
      totalTarget += target;
    });
    
    return {
      totalProgress,
      totalTarget,
      overallCompletionPercentage: totalTarget > 0 ? Math.min((totalProgress / totalTarget) * 100, 100) : 0,
      activeChallengesCount: activeChallenges.length
    };
  }, [getUserChallengeProgress]);

  // 🏥 HEALTHCARE COMPLIANCE: Helper function to get challenge status message
  // eslint-disable-next-line no-unused-vars
  const getHealthcareComplianceStatus = useCallback((challenge, progress, daysElapsed) => {
    if (!isStepChallenge(challenge.challengeId || challenge.id)) {
      return null; // Only apply to step challenges
    }
    
    const target = challenge.targetValue || challenge.total || 100;
    const durationMatch = challenge.duration?.match(/(\d+)\s*days?/i);
    const requiredDays = durationMatch ? parseInt(durationMatch[1]) : 21;
    
    const hasReachedTarget = progress.currentProgress >= target;
    const hasCompletedDuration = daysElapsed >= requiredDays;
    const remainingDays = requiredDays - daysElapsed;
    
    if (hasReachedTarget && hasCompletedDuration) {
      return {
        type: 'completed',
        title: '🏥 Healthcare Goal Achieved!',
        message: `You've completed both the step target and duration requirement. This builds lasting healthy habits!`,
        canComplete: true
      };
    } else if (hasReachedTarget && !hasCompletedDuration) {
      return {
        type: 'target_reached',
        title: '🎯 Target Reached - Continue for Health!',
        message: `Great job reaching ${target.toLocaleString()} steps! Keep going ${remainingDays} more days for maximum healthcare benefits. MediCureOn prioritizes consistency over speed.`,
        canComplete: false
      };
    } else if (!hasReachedTarget && hasCompletedDuration) {
      return {
        type: 'time_complete',
        title: '⏰ Duration Complete',
        message: `You've completed the ${requiredDays}-day duration but haven't reached the step target yet. Consider trying this challenge again when ready!`,
        canComplete: false
      };
    } else {
      return {
        type: 'in_progress',
        title: '🚶‍♂️ Building Healthy Habits',
        message: `Keep going! You need ${remainingDays} more days and ${(target - progress.currentProgress).toLocaleString()} more steps to complete this healthcare challenge.`,
        canComplete: false
      };
    }
  }, []);

  // 🏥 HEALTHCARE-FOCUSED: Enhanced Challenge Completion Detection with Fraud Prevention
  const detectAndProcessCompletedChallenges = useCallback(async () => {
    // 🔒 ANTI-FRAUD: Debounce rapid completion checks (prevent spam clicking)
    const now = Date.now();
    if (now - lastCompletionCheck < 2000) { // 2 second cooldown
      console.log('🔒 Completion check blocked - too frequent');
      return [];
    }
    setLastCompletionCheck(now);
    
    const completedChallenges = [];
    
    for (const challenge of userActiveChallenges) {
      const progress = getUserChallengeProgress(challenge.challengeId);
      const target = challenge.targetValue || challenge.total || 100;
      
      // 🔒 CRITICAL FIX: Check if challenge was already completed to prevent duplicate rewards
      const completionKey = `${challenge.challengeId}_${challenge.startDate}`;
      if (completionTracker.has(completionKey)) {
        console.log(`🔒 Challenge ${challenge.challengeId} already completed - skipping duplicate reward`);
        continue;
      }
      
      // 🏥 HEALTHCARE PRINCIPLE: Calculate challenge duration and ensure minimum timeframe completion
      const startDate = new Date(challenge.startDate);
      const currentDate = new Date();
      const daysElapsed = Math.floor((currentDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
      
      // Parse challenge duration (e.g., "70 days" -> 70)
      const durationMatch = challenge.duration?.match(/(\d+)\s*days?/i);
      const requiredDays = durationMatch ? parseInt(durationMatch[1]) : 21; // default 21 days
      
      // 🔒 CRITICAL FIX: Remove dangerous progress.completed check - only use actual progress
      // 🏥 HEALTHCARE RULE: Challenge can only be completed when BOTH conditions are met:
      // 1. Target steps/metrics achieved (progress.currentProgress >= target) - REMOVED progress.completed check
      // 2. Minimum duration completed (daysElapsed >= requiredDays)
      const hasReachedTarget = progress.currentProgress >= target; // FIXED: No more progress.completed bypass
      const hasCompletedDuration = daysElapsed >= requiredDays;
      const isCompleted = hasReachedTarget && hasCompletedDuration;
      
      // 🔒 VALIDATION: Log actual progress for debugging
      console.log(`🔍 Challenge ${challenge.challengeId} validation:`, {
        currentProgress: progress.currentProgress,
        target,
        hasReachedTarget,
        daysElapsed,
        requiredDays,
        hasCompletedDuration,
        isCompleted
      });
      
      // Log healthcare compliance for step challenges
      if (isStepChallenge(challenge.challengeId)) {
        if (hasReachedTarget && !hasCompletedDuration) {
          console.log(`⏰ ${challenge.challengeId}: Target reached but duration incomplete`);
        } else if (!hasReachedTarget && hasCompletedDuration) {
          console.log(`🎯 ${challenge.challengeId}: Duration complete but target not reached`);
        } else if (isCompleted) {
          console.log(`✅ ${challenge.challengeId}: Both target and duration completed`);
        }
      }
      
      if (isCompleted && !challenge.isCompleted) {
        // 🔒 CRITICAL FIX: Get correct point values from original challenge definition
        const originalChallenge = getAllChallenges().find(c => c.id === challenge.challengeId);
        const pointsToAward = originalChallenge?.points || challenge.points || 100;
        
        console.log(`🏆 POINTS VERIFICATION for ${challenge.challengeId}:`, {
          originalChallengePoints: originalChallenge?.points,
          userChallengePoints: challenge.points,
          finalPointsAwarded: pointsToAward
        });
        
        // 🔒 ANTI-FRAUD: Mark as completed in tracker immediately to prevent duplicates
        setCompletionTracker(prev => new Set([...prev, completionKey]));
        
        const completedChallenge = {
          ...challenge,
          isCompleted: true,
          completedAt: new Date().toISOString(),
          finalProgress: progress.currentProgress,
          completionRate: Math.min((progress.currentProgress / target) * 100, 100),
          pointsAwarded: pointsToAward,
          healthcareCompliant: true, // Mark as healthcare compliant
          daysToComplete: daysElapsed, // Track actual completion time
          completionValidated: true // Mark as properly validated
        };
        
        completedChallenges.push(completedChallenge);
        
        // 🏆 MEDIPOINTS AWARD SYSTEM - Production logging with comprehensive backend
        const newPointEntry = {
          points: pointsToAward,
          timestamp: new Date().toISOString(),
          source: 'challenge_completion',
          challengeId: challenge.challengeId,
          challengeName: challenge.name,
          description: `Completed challenge: ${challenge.name}`,
          healthcareCompliant: true,
          completionDetails: {
            finalProgress: progress.currentProgress,
            target,
            daysElapsed,
            requiredDays,
            completionRate: Math.min((progress.currentProgress / target) * 100, 100)
          }
        };
        
        try {
          // Use comprehensive backend system to add points
          await saveMediPointsToStorage(pointsToAward, newPointEntry);
        } catch (error) {
          console.warn('⚠️ Failed to save MediPoints to MediCureOn Data Storage, using local storage fallback');
          // Fallback: Update local state optimistically
          setMediPointsBalance(prev => ({
            currentBalance: prev.currentBalance + pointsToAward,
            totalEarned: prev.totalEarned + pointsToAward,
            totalSpent: prev.totalSpent,
            tier: prev.tier,
            pointsToNextTier: prev.pointsToNextTier
          }));
          setPointsHistory(prev => [newPointEntry, ...prev]);
          
          // Save to localStorage as backup
          const userId = user?.localAccountId || user?.username || userInfo?.localAccountId || 'guest';
          localStorage.setItem(`mediPoints_${userId}`, JSON.stringify({
            currentBalance: mediPointsBalance.currentBalance + pointsToAward,
            totalEarned: mediPointsBalance.totalEarned + pointsToAward,
            totalSpent: mediPointsBalance.totalSpent,
            tier: mediPointsBalance.tier,
            history: [...pointsHistory, newPointEntry],
            lastUpdated: new Date().toISOString()
          }));
        }
      }
    }
    
    if (completedChallenges.length > 0) {
      // Move completed challenges to completed array
      const remainingActive = userActiveChallenges.filter(
        challenge => !completedChallenges.some(comp => comp.challengeId === challenge.challengeId)
      );
      
      setUserActiveChallenges(remainingActive);
      setUserCompletedChallenges(prev => [...prev, ...completedChallenges]);
      
      // Save updated data
      await saveChallengeDataToStorage(remainingActive, [...userCompletedChallenges, ...completedChallenges], challengeProgress);
      
      // 🔒 CRITICAL FIX: Replace dangerous alert loop with single notification
      if (completedChallenges.length === 1) {
        const challenge = completedChallenges[0];
        alert(`🎉 Congratulations! You completed "${challenge.name}" and earned ${challenge.pointsAwarded} MediPoints!

Your available balance: ${mediPointsBalance.currentBalance + challenge.pointsAwarded} MediPoints
Your lifetime total: ${mediPointsBalance.totalEarned + challenge.pointsAwarded} MediPoints
Current tier: ${mediPointsBalance.tier}

Ready for your next challenge? 💪`);
      } else {
        // Multiple completions - show summary
        const totalPoints = completedChallenges.reduce((sum, c) => sum + c.pointsAwarded, 0);
        const challengeNames = completedChallenges.map(c => c.name).join(', ');
        alert(`🎉 Amazing! You completed ${completedChallenges.length} challenges: ${challengeNames}

Total points earned: ${totalPoints} MediPoints
Your new balance: ${mediPointsBalance.currentBalance + totalPoints} MediPoints
Current tier: ${mediPointsBalance.tier}

Incredible progress! 🚀`);
      }
    }
    
    return completedChallenges;
  }, [userActiveChallenges, userCompletedChallenges, challengeProgress, mediPointsBalance, pointsHistory, getUserChallengeProgress, saveMediPointsToStorage, saveChallengeDataToStorage, user?.localAccountId, user?.username, userInfo?.localAccountId, completionTracker, lastCompletionCheck, getAllChallenges]);

  // 🔥 NEW: Challenge Failure Detection and Auto-Expiration System
  const detectAndProcessFailedChallenges = useCallback(async () => {
    // 🔒 ANTI-SPAM: Debounce rapid expiration checks
    const now = Date.now();
    if (now - lastExpirationCheck < 5000) { // 5 second cooldown for expiration checks
      return [];
    }
    setLastExpirationCheck(now);
    
    const failedChallenges = [];
    const currentDate = new Date();
    
    for (const challenge of userActiveChallenges) {
      const progress = getUserChallengeProgress(challenge.challengeId);
      const target = challenge.targetValue || challenge.total || 100;
      
      // Calculate challenge duration and expiration
      const startDate = new Date(challenge.startDate);
      const daysElapsed = Math.floor((currentDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
      
      // Parse challenge duration (e.g., "21 days" -> 21)
      const durationMatch = challenge.duration?.match(/(\d+)\s*days?/i);
      const requiredDays = durationMatch ? parseInt(durationMatch[1]) : 21;
      
      // 🚨 CRITICAL: Challenge expiration detection
      const hasExpired = daysElapsed > requiredDays; // Challenge duration has passed
      const hasReachedTarget = progress.currentProgress >= target;
      
      // 🔥 FAILURE CONDITION: Challenge expired without reaching target
      if (hasExpired && !hasReachedTarget && !challenge.isCompleted && challenge.status === 'active') {
        console.log(`⚰️ Challenge ${challenge.challengeId} has FAILED - expired without reaching target`);
        
        const failedChallenge = {
          ...challenge,
          status: 'failed',
          failedAt: new Date().toISOString(),
          finalProgress: progress.currentProgress,
          completionRate: Math.min((progress.currentProgress / target) * 100, 100),
          daysElapsed,
          requiredDays,
          failureReason: 'duration_expired_target_not_reached'
        };
        
        failedChallenges.push(failedChallenge);
        
        // 💔 GENTLE FAILURE MESSAGE (not too harsh)
        const progressPercent = Math.round((progress.currentProgress / target) * 100);
        const challengeName = challenge.name || 'Challenge';
        
        // 🏆 Show what points they would have earned to maintain transparency
        const originalChallenge = getAllChallenges().find(c => c.id === challenge.challengeId);
        const wouldHaveEarned = originalChallenge?.points || challenge.points || 100;
        
        alert(`💙 ${challengeName} Journey Complete

You gave it your best effort and made ${progressPercent}% progress! 

• Target: ${target.toLocaleString()} ${challenge.unit || 'points'}
• Your achievement: ${progress.currentProgress.toLocaleString()} ${challenge.unit || 'points'}
• Days completed: ${daysElapsed}/${requiredDays}
• Would have earned: ${wouldHaveEarned} MediPoints if completed

Every step forward is progress! Ready to try again when you feel ready? 🌟

No points awarded this time, but your next attempt could earn ${wouldHaveEarned} MediPoints! 💪`);
      }
    }
    
    if (failedChallenges.length > 0) {
      // Move failed challenges from active to failed array
      const remainingActive = userActiveChallenges.filter(
        challenge => !failedChallenges.some(failed => failed.challengeId === challenge.challengeId)
      );
      
      setUserActiveChallenges(remainingActive);
      setUserFailedChallenges(prev => [...prev, ...failedChallenges]);
      
      // Save updated data (including failed challenges)
      await saveChallengeDataToStorage(remainingActive, userCompletedChallenges, challengeProgress);
      
      console.log(`🔄 Processed ${failedChallenges.length} failed challenges, ${remainingActive.length} remain active`);
    }
    
    return failedChallenges;
  }, [userActiveChallenges, getUserChallengeProgress, saveChallengeDataToStorage, userCompletedChallenges, challengeProgress, lastExpirationCheck, getAllChallenges]);

  // (Removed calculateCompletionPoints - no longer used after removing testing tools section)

  // Real-time IoMT Data Validation for Challenge Progress with Enhanced Completion Detection
  const validateChallengeProgress = useCallback(async (challengeId) => {
    if (!userId || !healthData) {
      console.log('❌ validateChallengeProgress early return:', { userId: userId, healthDataExists: !!healthData });
      return;
    }
    
    console.log('🚀 validateChallengeProgress called with:', { challengeId, userId: userId, healthDataExists: !!healthData });
    
    try {
      // 🌙 SLEEP STARTER: Use dedicated challenges-manager endpoint
  if (challengeId === 'sleep-starter') {
        console.log('✅ SLEEP STARTER validation path selected');
        
        // 🌙 ENHANCED SLEEP DATA EXTRACTION - same as display logic
        const sleepHours = 
          healthData?.healthMetrics?.lifestyle?.sleep?.value ||
          healthData?.realTimeMetrics?.sleep ||
          healthData?.wellness?.sleep?.totalSleep?.value ||
          healthData?.wellness?.sleep?.duration ||
          healthData?.lifestyle?.sleep?.value ||
          healthData?.sleep?.value ||
          0;
        
        console.log('🌙 SLEEP DATA EXTRACTION DEBUG:');
        console.log('- healthData?.healthMetrics?.lifestyle?.sleep?.value:', healthData?.healthMetrics?.lifestyle?.sleep?.value);
        console.log('- healthData?.realTimeMetrics?.sleep:', healthData?.realTimeMetrics?.sleep);
        console.log('- healthData?.wellness?.sleep?.totalSleep?.value:', healthData?.wellness?.sleep?.totalSleep?.value);
        console.log('- healthData?.lifestyle?.sleep?.value:', healthData?.lifestyle?.sleep?.value);
        console.log('- healthData?.sleep?.value:', healthData?.sleep?.value);
        console.log('- Final sleepHours extracted:', sleepHours);
        
        console.log('🔄 Auto-validating Sleep Starter:', { challengeId, sleepHours, healthData: !!healthData });
        
        // Always call the backend, even if sleepHours is 0, to fetch existing snapshot/streak
        if (sleepHours === 0) {
          console.log('ℹ️ Sleep hours is 0 - calling API to fetch snapshot/streak');
        } else {
          console.log('🚀 Making API call to challenges-manager with sleep hours:', sleepHours);
        }
        
        const endpoint = `${apiConfig.backendUrl}/api/challenges-manager?code=${apiConfig.functionKeys.challengesManager}`;
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: userId,
            action: 'validate',
            challengeId: challengeId,
            challengeType: 'Sleep Starter',
            sleepHours: sleepHours,
            healthData: healthData,
            date: new Date().toISOString().split('T')[0]
          }),
        });

  const result = await response.json();
        console.log('🔄 Auto-validation API Response:', result);
        
  if (result.success && result.data) {
          console.log('🔄 Auto-validation SUCCESS for Sleep Starter:', {
            consecutiveDays: result.data.consecutiveDays,
            sleepHours: result.data.sleepHours,
            message: result.data.message,
            progressPercentage: result.data.progressPercentage,
            completed: result.data.completed,
            failed: result.data.failed,
            fullResult: result.data
          });
          
          // 🚨 CHECK FOR CHALLENGE FAILURE FIRST - NOTIFY USER OF RESET
          if (result.data.failed && result.data.needsRestart) {
            const previousStreak = result.data.previousStreak || 0;
            alert(`😴 Sleep Starter Challenge Reset

❌ Target Not Met: Only ${result.data.sleepHours} hours sleep (need 7+ hours)

📊 Previous Progress: ${previousStreak} consecutive day${previousStreak !== 1 ? 's' : ''}
🔄 Starting Over: Challenge has been reset to Day 1

💪 Don't give up! Try again tomorrow with 7+ hours of sleep.
Consistent sleep is key to building healthy habits! 🌙✨`);
          }
          
          // 🏆 CHECK FOR COMPLETION FIRST - ENTERPRISE GAMIFICATION
          if (result.data.completed) {
            console.log('🎉 SLEEP STARTER COMPLETED!', result.data);
            
            // Build comprehensive completion message with enterprise features
            const achievements = result.data.achievements || [];
            const achievementText = achievements.length > 0 
              ? `\n\n🏅 New Achievements Unlocked:\n${achievements.map(a => `• ${a.title || a}`).join('\n')}`
              : '';
            
            const enterpriseText = result.data.enterpriseCompletion 
              ? `\n\n📊 Completion Stats:\n• Duration: ${result.data.enterpriseCompletion.completionSummary?.duration || '7 days'}\n• Success Rate: ${result.data.enterpriseCompletion.completionSummary?.completionRate || 100}%`
              : '';
            
            // Show comprehensive congratulations with enterprise features
            alert(`🎉 Congratulations! You completed "Sleep Starter"!

🏆 Rewards Earned:
• ${result.data.mediPointsAwarded || 200} MediPoints
• Sleep Mastery Achievement${achievementText}${enterpriseText}

7 consecutive nights of great sleep achieved! 
Total sleep on final night: ${result.data.finalSleepHours} hours
Challenge completion date: ${new Date().toLocaleDateString()}

Your healthy sleep habits are now established! 💤✨`);

            // Move challenge to completed list with enterprise data
            const currentChallenge = userActiveChallenges.find(c => c.challengeId === challengeId);
            if (currentChallenge) {
              const completedChallenge = {
                ...currentChallenge,
                status: 'completed',
                completedAt: new Date().toISOString(),
                pointsAwarded: result.data.mediPointsAwarded || 200,
                finalProgress: result.data.consecutiveDays || 7,
                rewards: result.data.enterpriseCompletion?.rewards || { mediPoints: result.data.mediPointsAwarded || 200 },
                achievements: achievements,
                enterpriseCompletion: result.data.enterpriseCompletion || {}
              };
              
              // Update state immediately
              setUserActiveChallenges(prev => prev.filter(c => c.challengeId !== challengeId));
              setUserCompletedChallenges(prev => [...prev, completedChallenge]);
              
              console.log('🏆 Challenge moved to completed challenges with enterprise data');
            }
            
            return result.data;
          }
          
          // Update progress state with consecutive days data
          setChallengeProgress(prev => ({
            ...prev,
            [challengeId]: {
              ...prev[challengeId],
              currentProgress: result.data.consecutiveDays || 0,
              consecutiveDays: result.data.consecutiveDays || 0,
              sleepHours: result.data.sleepHours || sleepHours,
              lastValidation: new Date().toISOString(),
              message: result.data.message || `${sleepHours.toFixed(1)} hours sleep`,
              progressPercentage: result.data.progressPercentage || 0,
              // Preserve the higher of existing vs returned, and avoid downgrading to 0 on no-data
              dailyProgressPercentage: (typeof result.data.dailyProgressPercentage === 'number')
                ? Math.max(prev[challengeId]?.dailyProgressPercentage || 0, result.data.dailyProgressPercentage)
                : (prev[challengeId]?.dailyProgressPercentage || 0)
            }
          }));

          console.log('🔄 Updated challengeProgress state for sleep-starter:', {
            challengeId,
            consecutiveDays: result.data.consecutiveDays,
            currentProgress: result.data.consecutiveDays
          });

          // Enhanced completion detection and auto-processing
          await detectAndProcessCompletedChallenges();
          
          console.log('✅ Sleep validation completed - UI will update from state changes');

          return result.data;
        }
        // If the backend responded success without data, refresh local progress from storage
        if (result.success && !result.data) {
          console.log('ℹ️ Sleep Starter: success without data, refreshing local progress snapshot');
          // No explicit refresh endpoint, but ensure state reflects latest challengeProgress already on server write cycle
          // We keep current state; UI will continue using existing progress values.
        }
      // 🌙 SLEEP CHAMPION: Use dedicated endpoint (60 nights)
      } else if (challengeId === 'sleep-champion') {
        console.log('✅ SLEEP CHAMPION validation path selected');
        const sleepHours = 
          healthData?.healthMetrics?.lifestyle?.sleep?.value ||
          healthData?.realTimeMetrics?.sleep ||
          healthData?.wellness?.sleep?.totalSleep?.value ||
          healthData?.wellness?.sleep?.duration ||
          healthData?.lifestyle?.sleep?.value ||
          healthData?.sleep?.value ||
          0;
        const endpoint = `${apiConfig.backendUrl}/api/challenges-manager?code=${apiConfig.functionKeys.challengesManager}`;
        const response = await fetch(endpoint, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({
            userId, action: 'validate-sleep-champion', challengeId, challengeType: 'Sleep Champion', sleepHours, healthData, date: new Date().toISOString().split('T')[0]
          })
        });
        const result = await response.json();
        console.log('🔄 Sleep Champion validation API Response:', result);
        if (result.success && result.data) {
          setChallengeProgress(prev => ({
            ...prev,
            [challengeId]: {
              ...prev[challengeId],
              currentProgress: result.data.consecutiveDays || 0,
              consecutiveDays: result.data.consecutiveDays || 0,
              sleepHours: result.data.sleepHours || sleepHours,
              lastValidation: new Date().toISOString(),
              message: result.data.message || `${sleepHours.toFixed(1)} hours sleep`,
              progressPercentage: result.data.progressPercentage || 0,
              dailyProgressPercentage: (typeof result.data.dailyProgressPercentage === 'number')
                ? Math.max(prev[challengeId]?.dailyProgressPercentage || 0, result.data.dailyProgressPercentage)
                : (prev[challengeId]?.dailyProgressPercentage || 0)
            }
          }));
          await detectAndProcessCompletedChallenges();
          return result.data;
        }
        return null;
      // 🌙 SLEEP RHYTHM RESET: Use dedicated endpoint (90 nights)
      } else if (challengeId === 'sleep-rhythm-reset') {
        console.log('✅ SLEEP RHYTHM RESET validation path selected');
        const sleepHours = 
          healthData?.healthMetrics?.lifestyle?.sleep?.value ||
          healthData?.realTimeMetrics?.sleep ||
          healthData?.wellness?.sleep?.totalSleep?.value ||
          healthData?.wellness?.sleep?.duration ||
          healthData?.lifestyle?.sleep?.value ||
          healthData?.sleep?.value ||
          0;
        const endpoint = `${apiConfig.backendUrl}/api/challenges-manager?code=${apiConfig.functionKeys.challengesManager}`;
        const response = await fetch(endpoint, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({
            userId, action: 'validate-sleep-rhythm-reset', challengeId, challengeType: 'Sleep Rhythm Reset', sleepHours, healthData, date: new Date().toISOString().split('T')[0]
          })
        });
        const result = await response.json();
        console.log('🔄 Sleep Rhythm Reset validation API Response:', result);
        if (result.success && result.data) {
          setChallengeProgress(prev => ({
            ...prev,
            [challengeId]: {
              ...prev[challengeId],
              currentProgress: result.data.consecutiveDays || 0,
              consecutiveDays: result.data.consecutiveDays || 0,
              sleepHours: result.data.sleepHours || sleepHours,
              lastValidation: new Date().toISOString(),
              message: result.data.message || `${sleepHours.toFixed(1)} hours sleep`,
              progressPercentage: result.data.progressPercentage || 0,
              dailyProgressPercentage: (typeof result.data.dailyProgressPercentage === 'number')
                ? Math.max(prev[challengeId]?.dailyProgressPercentage || 0, result.data.dailyProgressPercentage)
                : (prev[challengeId]?.dailyProgressPercentage || 0)
            }
          }));
          await detectAndProcessCompletedChallenges();
          return result.data;
        }
        return null;
      // 🌙 CENTURY SLEEP RESET: Use dedicated endpoint (100 nights)
      } else if (challengeId === 'century-sleep-reset') {
        console.log('✅ CENTURY SLEEP RESET validation path selected');
        const sleepHours = 
          healthData?.healthMetrics?.lifestyle?.sleep?.value ||
          healthData?.realTimeMetrics?.sleep ||
          healthData?.wellness?.sleep?.totalSleep?.value ||
          healthData?.wellness?.sleep?.duration ||
          healthData?.lifestyle?.sleep?.value ||
          healthData?.sleep?.value ||
          0;
        const endpoint = `${apiConfig.backendUrl}/api/challenges-manager?code=${apiConfig.functionKeys.challengesManager}`;
        const response = await fetch(endpoint, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({
            userId, action: 'validate-century-sleep-reset', challengeId, challengeType: 'Century Sleep Reset', sleepHours, healthData, date: new Date().toISOString().split('T')[0]
          })
        });
        const result = await response.json();
        console.log('🔄 Century Sleep Reset validation API Response:', result);
        if (result.success && result.data) {
          setChallengeProgress(prev => ({
            ...prev,
            [challengeId]: {
              ...prev[challengeId],
              currentProgress: result.data.consecutiveDays || 0,
              consecutiveDays: result.data.consecutiveDays || 0,
              sleepHours: result.data.sleepHours || sleepHours,
              lastValidation: new Date().toISOString(),
              message: result.data.message || `${sleepHours.toFixed(1)} hours sleep`,
              progressPercentage: result.data.progressPercentage || 0,
              dailyProgressPercentage: (typeof result.data.dailyProgressPercentage === 'number')
                ? Math.max(prev[challengeId]?.dailyProgressPercentage || 0, result.data.dailyProgressPercentage)
                : (prev[challengeId]?.dailyProgressPercentage || 0)
            }
          }));
          await detectAndProcessCompletedChallenges();
          return result.data;
        }
        return null;
      // 🌙 SLEEP ROOKIE: Use dedicated challenges-manager endpoint (21 nights)
      } else if (challengeId === 'sleep-rookie') {
        console.log('✅ SLEEP ROOKIE validation path selected');
        const sleepHours = 
          healthData?.healthMetrics?.lifestyle?.sleep?.value ||
          healthData?.realTimeMetrics?.sleep ||
          healthData?.wellness?.sleep?.totalSleep?.value ||
          healthData?.wellness?.sleep?.duration ||
          healthData?.lifestyle?.sleep?.value ||
          healthData?.sleep?.value ||
          0;

        console.log('🌙 SLEEP ROOKIE DATA EXTRACTION DEBUG:', {
          healthMetrics: healthData?.healthMetrics?.lifestyle?.sleep?.value,
          realtime: healthData?.realTimeMetrics?.sleep,
          wellness: healthData?.wellness?.sleep?.totalSleep?.value,
          final: sleepHours
        });
        if (sleepHours === 0) {
          console.log('ℹ️ Sleep hours is 0 - still calling API to fetch snapshot/streak for Rookie');
        }
        const endpoint = `${apiConfig.backendUrl}/api/challenges-manager?code=${apiConfig.functionKeys.challengesManager}`;
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: userId,
            action: 'validate-sleep-rookie',
            challengeId: challengeId,
            challengeType: 'Sleep Rookie',
            sleepHours: sleepHours,
            healthData: healthData,
            date: new Date().toISOString().split('T')[0]
          }),
        });
        const result = await response.json();
        console.log('🔄 Sleep Rookie validation API Response:', result);
        if (result.success && result.data) {
          // Completion flow
          if (result.data.completed) {
            console.log('🎉 SLEEP ROOKIE COMPLETED!', result.data);
            alert(`🎉 Congratulations! You completed "Sleep Rookie"!\n\n🏆 Rewards Earned:\n• ${result.data.mediPointsAwarded || 600} MediPoints\n• Sleep Consistency Achievement\n\n21 consecutive nights of great sleep achieved!\nFinal night: ${result.data.finalSleepHours} hours\nChallenge completion date: ${new Date().toLocaleDateString()}\n\nAmazing habit building! 💤✨`);
            const currentChallenge = userActiveChallenges.find(c => c.challengeId === challengeId);
            if (currentChallenge) {
              const completedChallenge = {
                ...currentChallenge,
                status: 'completed',
                completedAt: new Date().toISOString(),
                pointsAwarded: result.data.mediPointsAwarded || 600,
                finalProgress: result.data.consecutiveDays || 21
              };
              setUserActiveChallenges(prev => prev.filter(c => c.challengeId !== challengeId));
              setUserCompletedChallenges(prev => [...prev, completedChallenge]);
            }
            return result.data;
          }
          // Update progress state
          setChallengeProgress(prev => ({
            ...prev,
            [challengeId]: {
              ...prev[challengeId],
              currentProgress: result.data.consecutiveDays || 0,
              consecutiveDays: result.data.consecutiveDays || 0,
              sleepHours: result.data.sleepHours || sleepHours,
              lastValidation: new Date().toISOString(),
              message: result.data.message || `${sleepHours.toFixed(1)} hours sleep`,
              progressPercentage: result.data.progressPercentage || 0,
              dailyProgressPercentage: (typeof result.data.dailyProgressPercentage === 'number')
                ? Math.max(prev[challengeId]?.dailyProgressPercentage || 0, result.data.dailyProgressPercentage)
                : (prev[challengeId]?.dailyProgressPercentage || 0)
            }
          }));
          await detectAndProcessCompletedChallenges();
          return result.data;
        }
        return null;
        
      // 💧 HYDRATION STREAKS (Initiate 21, Strider 60, Master 90, Hero 7) unified handler
      } else if (['hydration-hero','hydration-initiate','hydration-strider','hydration-master'].includes(challengeId)) {
        console.log('✅ HYDRATION STREAK validation path selected:', challengeId);

        // Normalize water intake from multiple possible sources (ml + glasses)
        const waterIntakeML =
          healthData?.healthMetrics?.lifestyle?.waterIntake?.value ??
          healthData?.realTimeMetrics?.waterIntake ??
          healthData?.wellness?.hydration?.ml ??
          healthData?.lifestyle?.waterIntake?.value ??
          healthData?.water?.value;
        const waterGlassesRaw = healthData?.realTimeMetrics?.water;

        let waterML = 0;
        let waterGlasses = 0;
        if (typeof waterIntakeML === 'number' && waterIntakeML > 0) waterML = waterIntakeML;
        if (typeof waterGlassesRaw === 'number' && waterGlassesRaw > 0) waterGlasses = waterGlassesRaw;
        if (waterGlasses === 0 && waterML > 0) waterGlasses = Math.floor(waterML / 240);
        if (waterML === 0 && waterGlasses > 0) waterML = waterGlasses * 240;

        console.log('💧 HYDRATION STREAK DATA DEBUG:', { challengeId, waterML, waterGlasses });
        if (waterML === 0 && waterGlasses === 0) {
          console.log('⚠️ Water intake is 0 (ml and glasses) - skipping API call for hydration streak');
          return;
        }

        // Map action / label / defaultPoints / required days
        const hydrationConfig = {
          'hydration-hero': { action: 'validate-hydration', label: 'Hydration Hero', requiredDays: 7, defaultPoints: 120 },
          'hydration-initiate': { action: 'validate-hydration-initiate', label: 'Hydration Initiate', requiredDays: 21, defaultPoints: 420 },
          'hydration-strider': { action: 'validate-hydration-strider', label: 'Hydration Strider', requiredDays: 60, defaultPoints: 1600 },
          'hydration-master': { action: 'validate-hydration-master', label: 'Hydration Master', requiredDays: 90, defaultPoints: 2400 }
        };
        const cfg = hydrationConfig[challengeId];
        const endpoint = `${apiConfig.backendUrl}/api/challenges-manager?code=${apiConfig.functionKeys.challengesManager}`;
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId,
            action: cfg.action,
            challengeId,
            challengeType: cfg.label,
            waterGlasses,
            waterML,
            healthData,
            date: new Date().toISOString().split('T')[0]
          })
        });
        const result = await response.json();
        console.log('🔄 Hydration streak validation API Response:', { challengeId, result });
        if (result.success && result.data) {
          const data = result.data;

            // Failure (streak reset) notice
          if (data.failed && data.needsRestart) {
            const previousStreak = data.previousStreak || 0;
            alert(`💧 ${cfg.label} Challenge Reset\n\n❌ Target Not Met: Only ${data.waterGlasses} glasses (need 7+ glasses)\n\n📊 Previous Progress: ${previousStreak} consecutive day${previousStreak !== 1 ? 's' : ''}\n🔄 Starting Over: Challenge has been reset to Day 1\n\n💪 Stay consistent! Tomorrow is a new opportunity. 💦`);
          }

          // Completion flow
          if (data.completed) {
            console.log(`🎉 ${cfg.label.toUpperCase()} COMPLETED!`, data);
            alert(`🎉 Congratulations! You completed "${cfg.label}"!\n\n🏆 Rewards Earned:\n• ${data.mediPointsAwarded || cfg.defaultPoints} MediPoints\n• Hydration Streak Achievement\n\n${cfg.requiredDays} consecutive days of excellent hydration achieved!\nFinal water intake: ${data.finalWaterGlasses} glasses\nCompletion date: ${new Date().toLocaleDateString()}\n\nAmazing consistency! 💦🚀`);
            const currentChallenge = userActiveChallenges.find(c => c.challengeId === challengeId);
            if (currentChallenge) {
              const completedChallenge = {
                ...currentChallenge,
                status: 'completed',
                completedAt: new Date().toISOString(),
                pointsAwarded: data.mediPointsAwarded || cfg.defaultPoints,
                finalProgress: data.consecutiveDays || cfg.requiredDays
              };
              setUserActiveChallenges(prev => prev.filter(c => c.challengeId !== challengeId));
              setUserCompletedChallenges(prev => [...prev, completedChallenge]);
            }
            return data;
          }

          // Progress update (preserve higher Today%)
          setChallengeProgress(prev => ({
            ...prev,
            [challengeId]: {
              ...prev[challengeId],
              currentProgress: data.consecutiveDays || 0,
              consecutiveDays: data.consecutiveDays || 0,
              waterGlasses: data.waterGlasses || waterGlasses,
              lastValidation: new Date().toISOString(),
              message: data.message || `${waterGlasses} glasses`,
              progressPercentage: data.progressPercentage || 0,
              dailyProgressPercentage: (typeof data.dailyProgressPercentage === 'number')
                ? Math.max(prev[challengeId]?.dailyProgressPercentage || 0, data.dailyProgressPercentage)
                : (prev[challengeId]?.dailyProgressPercentage || 0)
            }
          }));
          console.log('✅ Hydration streak validation completed - UI state updated');
          return data;
        }
        return null;
        
      // ❤️ HEART RATE CALM: Use dedicated challenges-manager endpoint  
      } else if (challengeId === 'heart-rate-calm') {
        console.log('✅ HEART RATE CALM validation path selected');
        
        // ❤️ ENHANCED HEART RATE DATA EXTRACTION
        const heartRate = 
          healthData?.healthMetrics?.vitalSigns?.heartRate?.value ||
          healthData?.realTimeMetrics?.heartRate ||
          healthData?.vitals?.heartRate ||
          healthData?.wellness?.heartRate?.resting ||
          healthData?.vitalSigns?.heartRate?.value ||
          healthData?.heartRate?.value ||
          0;
        
        console.log('❤️ HEART RATE DATA EXTRACTION DEBUG:');
        console.log('- healthData?.healthMetrics?.vitalSigns?.heartRate?.value:', healthData?.healthMetrics?.vitalSigns?.heartRate?.value);
        console.log('- healthData?.realTimeMetrics?.heartRate:', healthData?.realTimeMetrics?.heartRate);
        console.log('- healthData?.vitals?.heartRate:', healthData?.vitals?.heartRate);
        console.log('- Final heartRate extracted:', heartRate);
        
        console.log('🔄 Auto-validating Heart Rate Calm:', { challengeId, heartRate, healthData: !!healthData });
        
        if (heartRate === 0) {
          console.log('⚠️ Heart rate is 0 - skipping API call');
          return;
        }
        
        console.log('🚀 Making API call to challenges-manager with heart rate:', heartRate);
        
        const endpoint = `${apiConfig.backendUrl}/api/challenges-manager?code=${apiConfig.functionKeys.challengesManager}`;
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: userId,
            action: 'validate-heartrate',
            challengeId: challengeId,
            challengeType: challengeId === 'heart-rate-cadet' ? 'Heart Rate Cadet' : 'Heart Rate Calm',
            heartRate: heartRate,
            healthData: healthData,
            date: new Date().toISOString().split('T')[0]
          }),
        });

        const result = await response.json();
        console.log('🔄 Heart Rate validation API Response:', result);
        
        if (result.success && result.data) {
          console.log('🔄 Auto-validation SUCCESS for Heart Rate Calm:', {
            consecutiveDays: result.data.consecutiveDays,
            heartRate: result.data.heartRate,
            message: result.data.message,
            progressPercentage: result.data.progressPercentage,
            completed: result.data.completed,
            failed: result.data.failed,
            fullResult: result.data
          });
          
          // 🚨 CHECK FOR CHALLENGE FAILURE FIRST - NOTIFY USER OF RESET
          if (result.data.failed && result.data.needsRestart) {
            const previousStreak = result.data.previousStreak || 0;
            const failureReason = result.data.heartRate === 0 ? 'No heart rate data' : `${result.data.heartRate} BPM (need <70 BPM)`;
            alert(`❤️ ${challengeId === 'heart-rate-cadet' ? 'Heart Rate Cadet' : 'Heart Rate Calm'} Challenge Reset

❌ Target Not Met: ${failureReason}

📊 Previous Progress: ${previousStreak} consecutive day${previousStreak !== 1 ? 's' : ''}
🔄 Starting Over: Challenge has been reset to Day 1

💪 Don't give up! Try relaxation techniques tomorrow to keep your heart rate below 70 BPM.
Stress management takes practice! ❤️✨`);
          }
          
          // Check for completion
          if (result.data.completed) {
            console.log('🎉 HEART RATE CHALLENGE COMPLETED!', result.data);
            alert(`🎉 Congratulations! You completed "${challengeId === 'heart-rate-cadet' ? 'Heart Rate Cadet' : 'Heart Rate Calm'}"!

🏆 Rewards Earned:
• ${result.data.mediPointsAwarded || 180} MediPoints
• Calm Heart Achievement

${challengeId === 'heart-rate-cadet' ? '21' : '7'} consecutive days of maintaining calm heart rate achieved! 
Final heart rate: ${result.data.finalHeartRate} BPM
Challenge completion date: ${new Date().toLocaleDateString()}

Your stress management skills are now mastered! ❤️✨`);

            // Move challenge to completed list
            const currentChallenge = userActiveChallenges.find(c => c.challengeId === challengeId);
            if (currentChallenge) {
              const completedChallenge = {
                ...currentChallenge,
                status: 'completed',
                completedAt: new Date().toISOString(),
                pointsAwarded: result.data.mediPointsAwarded || 180,
                finalProgress: result.data.consecutiveDays || (challengeId === 'heart-rate-cadet' ? 21 : 7)
              };
              
              setUserActiveChallenges(prev => prev.filter(c => c.challengeId !== challengeId));
              setUserCompletedChallenges(prev => [...prev, completedChallenge]);
            }
            
            return result.data;
          }
          
          // Update progress state
          setChallengeProgress(prev => ({
            ...prev,
            [challengeId]: {
              ...prev[challengeId],
              currentProgress: result.data.consecutiveDays || 0,
              consecutiveDays: result.data.consecutiveDays || 0,
              heartRate: result.data.heartRate || heartRate,
              lastValidation: new Date().toISOString(),
              message: result.data.message || `${heartRate} BPM`,
              progressPercentage: result.data.progressPercentage || 0,
              // Preserve better of existing and returned
              dailyProgressPercentage: (typeof result.data.dailyProgressPercentage === 'number')
                ? Math.max(prev[challengeId]?.dailyProgressPercentage || 0, result.data.dailyProgressPercentage)
                : (prev[challengeId]?.dailyProgressPercentage || 0)
            }
          }));

          console.log('🔄 Updated challengeProgress state for heart-rate challenge:', {
            challengeId,
            consecutiveDays: result.data.consecutiveDays,
            currentProgress: result.data.consecutiveDays
          });

          console.log('✅ Heart Rate validation completed - UI will update from state changes');
          return result.data;
        }
        return null;
        
      // 🚶‍♂️ GENTLE MORNING WALKER: Use dedicated challenges-manager endpoint
      } else if (challengeId === 'step-streak') {
        console.log('✅ STEP STREAK validation path selected');
        // Extract steps from healthData
        const steps = 
          healthData?.realTimeMetrics?.steps ||
          healthData?.healthMetrics?.activity?.steps?.value ||
          healthData?.activity?.steps?.value ||
          0;
        const endpoint = `${apiConfig.backendUrl}/api/challenges-manager?code=${apiConfig.functionKeys.challengesManager}`;
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId,
            action: 'validate-step-streak',
            challengeId,
            steps,
            healthData,
            date: new Date().toISOString().split('T')[0]
          })
        });
        const result = await response.json();
        console.log('🔄 Step Streak validation API Response:', result);
        if (result?.success && result?.data) {
          const data = result.data;
          // Handle completion
          if (data.completed) {
            console.log('🎉 STEP STREAK COMPLETED!', data);
            const currentChallenge = userActiveChallenges.find(c => c.challengeId === challengeId);
            if (currentChallenge) {
              const completedChallenge = {
                ...currentChallenge,
                status: 'completed',
                completedAt: new Date().toISOString(),
                pointsAwarded: data.mediPointsAwarded || 1800,
                finalProgress: 60,
                enterpriseCompletion: data.enterpriseCompletion || {}
              };
              setUserActiveChallenges(prev => prev.filter(c => c.challengeId !== challengeId));
              setUserCompletedChallenges(prev => [...prev, completedChallenge]);
            }
            return data;
          }
          // Update snapshot for UI (avoid zeros)
          const computedDailyPct = Math.min(100, Math.round((steps / 6510) * 100)) || 0;
          setChallengeProgress(prev => ({
            ...prev,
            [challengeId]: {
              ...prev[challengeId],
              currentProgress: data.currentProgress ?? data.consecutiveDays ?? data.daysElapsed ?? 0,
              consecutiveDays: data.currentProgress ?? data.consecutiveDays ?? data.daysElapsed ?? 0,
              steps: typeof data.steps === 'number' ? data.steps : (prev[challengeId]?.steps || steps || 0),
              totalSteps: typeof data.totalSteps === 'number' ? data.totalSteps : (prev[challengeId]?.totalSteps || 0),
              lastValidation: new Date().toISOString(),
              progressPercentage: typeof data.progressPercentage === 'number' 
                ? data.progressPercentage 
                : (Number.isFinite(data.daysElapsed) 
                    ? Math.min(100, Math.round(((data.daysElapsed || 0) / 60) * 100)) 
                    : (prev[challengeId]?.progressPercentage || 0)),
              dailyProgressPercentage: typeof data.dailyProgressPercentage === 'number' 
                ? Math.max(prev[challengeId]?.dailyProgressPercentage || 0, data.dailyProgressPercentage) 
                : Math.max(prev[challengeId]?.dailyProgressPercentage || 0, computedDailyPct)
            }
          }));
          await detectAndProcessCompletedChallenges();
          return data;
        }
        return null;

      } else if (challengeId === 'step-commander') {
        console.log('✅ STEP COMMANDER validation path selected');
        // Extract steps from healthData
        const steps = 
          healthData?.realTimeMetrics?.steps ||
          healthData?.healthMetrics?.activity?.steps?.value ||
          healthData?.activity?.steps?.value ||
          0;
        const endpoint = `${apiConfig.backendUrl}/api/challenges-manager?code=${apiConfig.functionKeys.challengesManager}`;
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId,
            action: 'validate-step-commander',
            challengeId,
            steps,
            healthData,
            date: new Date().toISOString().split('T')[0]
          })
        });
        const result = await response.json();
        console.log('🔄 Step Commander validation API Response:', result);
        if (result?.success && result?.snapshot) {
          const snap = result.snapshot;
          // Completion may be handled server-side via processChallengeCompletion; UI will refresh on GET
          setChallengeProgress(prev => ({
            ...prev,
            [challengeId]: {
              ...prev[challengeId],
              currentProgress: snap.currentProgress ?? 0,
              consecutiveDays: snap.currentProgress ?? 0,
              steps: typeof snap.steps === 'number' ? snap.steps : (prev[challengeId]?.steps || steps || 0),
              totalSteps: typeof snap.totalSteps === 'number' ? snap.totalSteps : (prev[challengeId]?.totalSteps || 0),
              lastValidation: new Date().toISOString(),
              progressPercentage: Math.min(100, Math.round(((snap.currentProgress ?? 0) / 90) * 100)),
              dailyProgressPercentage: typeof snap.dailyProgressPercentage === 'number' ? Math.max(prev[challengeId]?.dailyProgressPercentage || 0, snap.dailyProgressPercentage) : (prev[challengeId]?.dailyProgressPercentage || 0)
            }
          }));
          await detectAndProcessCompletedChallenges();
          return result.snapshot;
        }
        return null;

      } else if (challengeId === 'million-step-challenge') {
        console.log('✅ MILLION STEP validation path selected');
        const steps = 
          healthData?.realTimeMetrics?.steps ||
          healthData?.healthMetrics?.activity?.steps?.value ||
          healthData?.activity?.steps?.value ||
          0;
        const endpoint = `${apiConfig.backendUrl}/api/challenges-manager?code=${apiConfig.functionKeys.challengesManager}`;
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId,
            action: 'validate-million-step-challenge',
            challengeId,
            steps,
            healthData,
            date: new Date().toISOString().split('T')[0]
          })
        });
        const result = await response.json();
        console.log('🔄 Million Step validation API Response:', result);
        if (result?.success && result?.snapshot) {
          const snap = result.snapshot;
          setChallengeProgress(prev => ({
            ...prev,
            [challengeId]: {
              ...prev[challengeId],
              currentProgress: snap.currentProgress ?? 0,
              consecutiveDays: snap.currentProgress ?? 0,
              steps: typeof snap.steps === 'number' ? snap.steps : (prev[challengeId]?.steps || steps || 0),
              totalSteps: typeof snap.totalSteps === 'number' ? snap.totalSteps : (prev[challengeId]?.totalSteps || 0),
              lastValidation: new Date().toISOString(),
              progressPercentage: Math.min(100, Math.round(((snap.currentProgress ?? 0) / 90) * 100)),
              dailyProgressPercentage: typeof snap.dailyProgressPercentage === 'number' ? Math.max(prev[challengeId]?.dailyProgressPercentage || 0, snap.dailyProgressPercentage) : (prev[challengeId]?.dailyProgressPercentage || 0)
            }
          }));
          await detectAndProcessCompletedChallenges();
          return result.snapshot;
        }
        return null;

      } else if (challengeId === 'gentle-morning-walker') {
        console.log('✅ GENTLE MORNING WALK validation path selected');

        // Prefer backend derivation; if front-end has an estimation, pass it as a hint
        // Note: Frontend may not have precise steps-before-9am; backend computes it reliably
        const stepsBeforeNineHint = 0;

        const endpoint = `${apiConfig.backendUrl}/api/challenges-manager?code=${apiConfig.functionKeys.challengesManager}`;
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: userId,
            action: 'validate-morning-walk',
            challengeId: challengeId,
            challengeType: 'Gentle Morning Walk',
            stepsBeforeNine: stepsBeforeNineHint,
            healthData: healthData,
            date: new Date().toISOString().split('T')[0]
          }),
        });

        const result = await response.json();
        console.log('🔄 Morning Walk validation API Response:', result);

        if (result.success && result.data) {
          const data = result.data;

          // Handle completion
          if (data.completed) {
            console.log('🎉 GENTLE MORNING WALK COMPLETED!', data);

            // Move challenge to completed list
            const currentChallenge = userActiveChallenges.find(c => c.challengeId === challengeId);
            if (currentChallenge) {
              const completedChallenge = {
                ...currentChallenge,
                status: 'completed',
                completedAt: new Date().toISOString(),
                pointsAwarded: data.mediPointsAwarded || 150,
                finalProgress: data.totalDays || 7,
                enterpriseCompletion: data.enterpriseCompletion || {}
              };
              setUserActiveChallenges(prev => prev.filter(c => c.challengeId !== challengeId));
              setUserCompletedChallenges(prev => [...prev, completedChallenge]);
            }

            alert(`🎉 Congratulations! You completed "Gentle Morning Walker"!\n\n🏆 Rewards Earned:\n• ${(data.mediPointsAwarded || 150)} MediPoints\n• Morning Consistency Achievement\n\n7 consecutive mornings achieved!\nFinal day steps before 9AM: ${data.finalStepsBeforeNine || data.stepsBeforeNine || 0}\nChallenge completion date: ${new Date().toLocaleDateString()}`);
            return data;
          }

          // Update progress snapshot with backend-provided metrics (avoid zeros)
          setChallengeProgress(prev => ({
            ...prev,
            [challengeId]: {
              ...prev[challengeId],
              currentProgress: data.consecutiveDays || 0,
              consecutiveDays: data.consecutiveDays || 0,
              stepsBeforeNine: typeof data.stepsBeforeNine === 'number' ? data.stepsBeforeNine : (prev[challengeId]?.stepsBeforeNine || 0),
              lastValidation: new Date().toISOString(),
              message: data.message || `Day ${data.consecutiveDays || 0} of 7` ,
              progressPercentage: typeof data.progressPercentage === 'number' ? data.progressPercentage : (prev[challengeId]?.progressPercentage || 0),
              // Preserve the higher of existing vs returned; never downgrade to 0
              dailyProgressPercentage: (typeof data.dailyProgressPercentage === 'number')
                ? Math.max(prev[challengeId]?.dailyProgressPercentage || 0, data.dailyProgressPercentage)
                : (prev[challengeId]?.dailyProgressPercentage || 0)
            }
          }));

          console.log('🔄 Updated challengeProgress state for gentle-morning-walker:', {
            consecutiveDays: data.consecutiveDays,
            dailyProgressPercentage: data.dailyProgressPercentage
          });

          // Detect any global completions and update UI
          await detectAndProcessCompletedChallenges();
          return data;
        }
        return null;
        
      } else {
        console.log('❌ UNKNOWN CHALLENGE - using legacy validation for:', challengeId);
      }

      // 🏥 OTHER CHALLENGES: Use legacy validation endpoint
      const response = await fetch(apiConfig.challenges.validateProgress, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-functions-key': apiConfig.functionKeys.challenges
        },
        body: JSON.stringify({
          userId: userId,
          challengeId: challengeId,
          healthData: healthData,
          timestamp: new Date().toISOString(),
          deviceType: healthData.deviceType || 'unknown'
        })
      });

      if (response.ok) {
        const result = await response.json();
        
        // Update progress in state with enhanced tracking
        const totalProgressData = calculateTotalProgress(userActiveChallenges, challengeProgress);
        setChallengeProgress(prev => ({
          ...prev,
          [challengeId]: {
            ...prev[challengeId],
            currentProgress: result.progress,
            lastValidation: new Date().toISOString(),
            antiGamingFlags: result.antiGamingFlags || [],
            // NEW: Enhanced progress tracking
            totalProgress: totalProgressData.totalProgress,
            overallCompletionPercentage: totalProgressData.overallCompletionPercentage,
            completionPercentage: result.completionPercentage || 0
          }
        }));

        // Enhanced completion detection and auto-processing
        await detectAndProcessCompletedChallenges();

        return result;
      }
    } catch (error) {
      console.error('❌ Challenge validation error:', error);
      return null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, healthData, userActiveChallenges, challengeProgress, calculateTotalProgress, detectAndProcessCompletedChallenges]);

  // Auto-validate active challenges when health data updates (removed validateChallengeProgress dependency to prevent infinite loop)
  useEffect(() => {
    if (healthData && userActiveChallenges.length > 0) {
      // Enhanced sleep data extraction - same as validateChallengeProgress
      const sleepData = healthData?.healthMetrics?.lifestyle?.sleep?.value ||
                       healthData?.realTimeMetrics?.sleep ||
                       healthData?.wellness?.sleep?.totalSleep?.value ||
                       healthData?.wellness?.sleep?.duration ||
                       healthData?.lifestyle?.sleep?.value ||
                       healthData?.sleep?.value;
                       
      console.log('🔄 Auto-validation triggered:', {
        healthDataExists: !!healthData,
        activeChallenges: userActiveChallenges.length,
        challenges: userActiveChallenges.map(c => ({ id: c.challengeId, status: c.status })),
        sleepData: sleepData
      });
      
      userActiveChallenges.forEach(challenge => {
        if (challenge.status === 'active' && !challenge.paused) {
          console.log(`🔄 Auto-validating challenge: ${challenge.challengeId}`);
          validateChallengeProgress(challenge.challengeId);
        }
      });
      
      // 🚀 AUTO-LIFECYCLE: Detect completions and failures
      detectAndProcessCompletedChallenges();
      detectAndProcessFailedChallenges(); // 🔥 NEW: Auto-detect expired challenges
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [healthData, userActiveChallenges]);

  // 🔥 NEW: Periodic challenge lifecycle check (every 5 minutes)
  useEffect(() => {
    const lifecycleCheck = setInterval(() => {
      if (userActiveChallenges.length > 0) {
        console.log('⏰ Periodic lifecycle check for expired challenges...');
        detectAndProcessFailedChallenges();
        detectAndProcessCompletedChallenges();
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(lifecycleCheck);
  }, [userActiveChallenges, detectAndProcessFailedChallenges, detectAndProcessCompletedChallenges]);

  // 🚀 NEW: Real-time progress sync with backend
  const syncProgressWithBackend = useCallback(async (challengeId, currentProgress, totalTarget, metadata = {}) => {
    if (!userId) return;
    
    try {
      const response = await fetch(apiConfig.challenges.updateProgress || `${apiConfig.backendUrl}/challenges-manager`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-functions-key': apiConfig.functionKeys.challenges
        },
        body: JSON.stringify({
          userId: userId,
          challengeId: challengeId,
          action: 'updateProgress',
          currentProgress: currentProgress,
          totalTarget: totalTarget,
          metadata: {
            ...metadata,
            timestamp: new Date().toISOString(),
            source: 'frontend_realtime_sync'
          }
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        // Update local state with backend response
        setChallengeProgress(prev => ({
          ...prev,
          [challengeId]: {
            ...prev[challengeId],
            currentProgress: result.data?.currentProgress || currentProgress,
            completionPercentage: result.data?.completionPercentage || 0,
            isCompleted: result.data?.isCompleted || false,
            lastBackendSync: new Date().toISOString()
          }
        }));
        
        return result.data;
      }
    } catch (error) {
      console.error('❌ Error syncing progress with backend:', error);
    }
  }, [userId]);

  // 🔧 ENHANCED Auto-save with ROBUST Data Validation - Prevent Stale Data Multiplication
  useEffect(() => {
    if (healthData?.realTimeMetrics?.steps && userActiveChallenges.length > 0) {
      const today = new Date();
      const todayKey = today.toISOString().split('T')[0]; // Format: YYYY-MM-DD
      const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const lastUpdate = healthData?.lastSync ? new Date(healthData.lastSync) : null;
      
      // 🚨 CRITICAL: Validate data freshness - be more lenient for valid step data
      const dataAge = lastUpdate ? (Date.now() - lastUpdate.getTime()) / (1000 * 60 * 60) : 999; // hours
      const hasValidSteps = healthData.realTimeMetrics.steps > 0;
      
      // Accept data if we have valid steps AND either:
      // 1. No lastSync timestamp (meaning new/fresh data), OR
      // 2. LastSync is from today within 24 hours
      const isDataFresh = hasValidSteps && (
        !lastUpdate || // No timestamp = accept as fresh
        (lastUpdate >= todayStart && dataAge < 24) // OR recent timestamp from today
      );
      console.log('- Last health data update:', lastUpdate?.toLocaleString() || 'Unknown');
      console.log('- Data age (hours):', dataAge.toFixed(1));
      console.log('- lastUpdate >= todayStart:', lastUpdate ? (lastUpdate >= todayStart) : 'N/A (no lastUpdate)');
      // Only process if health data is ACTUALLY from today (not stale from yesterday)
      if (isDataFresh) {
        const todaysSteps = healthData.realTimeMetrics.steps;
        let hasChanges = false;
        // Update daily progress for ALL active step challenges using comprehensive identification
        const updatedActiveChallenges = userActiveChallenges.map(challenge => {
          if (isStepChallenge(challenge.challengeId) && challenge.status === 'active' && !challenge.paused) {
            const currentStepsForToday = challenge.dailyProgress?.[todayKey] || 0;
            
            // Only update if steps have changed significantly to prevent unnecessary re-renders
            if (todaysSteps > currentStepsForToday + 100) { // Update every 100 steps
              hasChanges = true;
              const updatedDailyProgress = {
                ...challenge.dailyProgress,
                [todayKey]: todaysSteps
              };
              
              // 🚀 ENHANCED: Calculate cumulative progress with SMART historical + today logic
              let cumulativeSteps = 0;
              let validDaysCount = 0;
              
              // Process historical days (excluding today)
              Object.entries(updatedDailyProgress).forEach(([dateKey, steps]) => {
                if (dateKey !== todayKey) { // Historical days only
                  if (steps !== undefined && steps !== null && steps > 0) {
                    cumulativeSteps += steps;
                    validDaysCount++;
                  }
                }
              });
              
              // Add today's fresh data
              if (todaysSteps > 0) {
                cumulativeSteps += todaysSteps;
                validDaysCount++;
              }
              
              const target = challenge.targetValue || challenge.total || 10000;
              
              // Sync with backend for real-time progress tracking
              syncProgressWithBackend(challenge.challengeId, cumulativeSteps, target, {
                todaysSteps: todaysSteps,
                dailyProgress: updatedDailyProgress,
                challengeType: 'steps',
                lastUpdate: new Date().toISOString(),
                dataFreshness: {
                  isDataFresh: true,
                  validDays: validDaysCount,
                  dataAge: dataAge
                }
              });
              return {
                ...challenge,
                dailyProgress: updatedDailyProgress
              };
            }
          }
          return challenge;
        });
        
        // Update state and Azure Data Lake only if there were actual changes
        if (hasChanges) {
          setUserActiveChallenges(updatedActiveChallenges);
          // Remove saveChallengeDataToStorage from dependency to break circular updates
          saveChallengeDataToStorage(updatedActiveChallenges, userCompletedChallenges, challengeProgress);
        }
      } else {
        console.warn('⚠️ STALE DATA DETECTED: Not updating daily progress with potentially outdated step count');
        if (lastUpdate) {
          console.warn(`   - Last update was ${dataAge.toFixed(1)} hours ago (${lastUpdate.toLocaleString()})`);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [healthData?.realTimeMetrics?.steps, healthData?.lastSync, userActiveChallenges.length, syncProgressWithBackend]);
  
  // Expose comprehensive MediPoints data for parent components (LandingPage, Dashboard)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Make comprehensive MediPoints data globally available for LandingPage
      window.userMediPoints = {
        currentBalance: mediPointsBalance.currentBalance,
        totalEarned: mediPointsBalance.totalEarned,
        totalSpent: mediPointsBalance.totalSpent,
        tier: mediPointsBalance.tier,
        pointsToNextTier: mediPointsBalance.pointsToNextTier,
        pointsHistory: pointsHistory,
        lastUpdated: new Date().toISOString()
      };
      
      // Dispatch custom event when points change
      window.dispatchEvent(new CustomEvent('mediPointsUpdated', {
        detail: {
          currentBalance: mediPointsBalance.currentBalance,
          totalEarned: mediPointsBalance.totalEarned,
          totalSpent: mediPointsBalance.totalSpent,
          tier: mediPointsBalance.tier,
          pointsToNextTier: mediPointsBalance.pointsToNextTier,
          pointsHistory: pointsHistory
        }
      }));
    }
  }, [mediPointsBalance, pointsHistory]);
  
  // Update popup data when user joins/leaves challenges (fixes popup closing issue)
  useEffect(() => {
    if (showDetailsPopup && selectedChallengeDetails?.challenge) {
      // Find updated user challenge data
      const updatedUserChallenge = userActiveChallenges.find(
        c => c.challengeId === selectedChallengeDetails.challenge.id
      );
      
      // Update popup data with latest user challenge state
      setSelectedChallengeDetails(prev => ({
        ...prev,
        userChallenge: updatedUserChallenge
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userActiveChallenges, showDetailsPopup, selectedChallengeDetails?.challenge?.id]);
  
  // 🌙 SLEEP STARTER: Auto-validate sleep progress when health data updates (DISABLED to prevent infinite loop)
  // TODO: Re-enable after fixing infinite loop caused by syncProgressWithBackend dependency
  /*
  useEffect(() => {
    // Auto-validation temporarily disabled to prevent shimmer effect
    // Enterprise validation now handled automatically via health data processing
  }, []);
  */
  
  // Production-Ready Beginner Challenges (Senior Friendly - 7 days)
  // 🚀 PHASE 1 LAUNCH: 4 Active + 1 Future Challenges Card
  const productionChallenges = {
    beginner: [
      // ===== ACTIVE CHALLENGES (Phase 1 Launch) =====
      {
        id: 'gentle-morning-walker',
        name: 'Gentle Morning Walker',
        description: 'Walk 500 steps before 9AM daily for 7 days',
        icon: '🌅',
        difficulty: 'beginner',
        duration: '7 days',
        points: 150,
        total: 7,
        targetValue: 7,
        unit: 'days',
        participants: 15420,
        gradient: 'from-green-400 to-emerald-600',
        seniorFriendly: true,
        deviceTracked: ['Apple Health', 'Fitbit', 'Garmin'],
        trackingMethod: 'steps_before_time',
        verificationRequired: true,
        category: 'movement',
        healthBenefit: 'Improves morning energy and metabolism',
        isActive: true,
        productionReady: true
      },
      {
        id: 'hydration-hero',
        name: 'Hydration Hero',
        description: 'Drink 7+ glasses of water before 6PM daily for 7 days',
        icon: '💧',
        difficulty: 'beginner',
        duration: '7 days',
        points: 120,
        total: 7,
        targetValue: 7,
        unit: 'glasses',
        participants: 23150,
        gradient: 'from-blue-400 to-cyan-600',
        seniorFriendly: true,
        deviceTracked: ['Samsung Health', 'Fitbit (hydration log)'],
        trackingMethod: 'hydration_log',
        verificationRequired: true,
        category: 'nutrition',
        healthBenefit: 'Maintains optimal hydration for cellular health',
        isActive: true,
        productionReady: true
      },
      {
        id: 'sleep-starter',
        name: 'Sleep Starter',
        description: 'Sleep 7+ hours for 7 nights consecutively',
        icon: '😴',
        difficulty: 'beginner',
        duration: '7 days',
        points: 200,
        total: 7,
        targetValue: 7,
        unit: 'nights',
        participants: 18760,
        gradient: 'from-purple-400 to-indigo-600',
        seniorFriendly: true,
        deviceTracked: ['Oura Ring', 'Apple Health', 'Withings'],
        trackingMethod: 'sleep_duration',
        verificationRequired: true,
        category: 'recovery',
        healthBenefit: 'Enhances immune system and cognitive function',
        isActive: true,
        productionReady: true
      },
      {
        id: 'heart-rate-calm',
        name: 'Heart Rate Calm',
        description: 'Maintain resting HR < 70 BPM for 7 days',
        icon: '❤️',
        difficulty: 'beginner',
        duration: '7 days',
        points: 180,
        total: 7,
        targetValue: 70,
        unit: 'days',
        participants: 12340,
        gradient: 'from-rose-400 to-red-600',
        seniorFriendly: true,
        deviceTracked: ['Fitbit', 'Garmin', 'Withings'],
        trackingMethod: 'resting_heart_rate',
        verificationRequired: true,
        category: 'cardiovascular',
        healthBenefit: 'Reduces cardiovascular risk and stress levels',
        isActive: true,
        productionReady: true
      }
    ],
    easy: [
      {
        id: 'step-scout',
        name: 'Step Scout',
        description: 'Walk 3,000 steps daily for 21 days',
        icon: '👟',
        difficulty: 'easy',
        duration: '21 days',
        points: 450,
        total: 21,
        targetValue: 63000,
        unit: 'steps',
        participants: 28450,
        gradient: 'from-blue-400 to-cyan-600',
        seniorFriendly: false,
        deviceTracked: ['Fitbit', 'Garmin', 'Apple'],
        trackingMethod: 'daily_steps',
        verificationRequired: true,
        category: 'movement',
        healthBenefit: 'Builds cardiovascular endurance and daily movement habits'
      },
      {
        id: 'sleep-rookie',
        name: 'Sleep Rookie',
        description: 'Sleep 7+ hours for 21 nights',
        icon: '🌙',
        difficulty: 'easy',
        duration: '21 days',
        points: 600,
        total: 21,
        targetValue: 21,
        unit: 'nights',
        participants: 26780,
        gradient: 'from-purple-400 to-indigo-600',
        seniorFriendly: false,
        deviceTracked: ['Oura', 'Apple', 'Withings'],
        trackingMethod: 'sleep_duration',
        verificationRequired: true,
        category: 'recovery',
        healthBenefit: 'Establishes healthy sleep patterns for better recovery and mental clarity'
      },
      {
        id: 'hydration-initiate',
        name: 'Hydration Initiate',
        description: 'Hit water goal for 21 days',
        icon: '💦',
        difficulty: 'easy',
        duration: '21 days',
        points: 420,
        total: 21,
        targetValue: 21,
        unit: 'days',
        participants: 24590,
        gradient: 'from-cyan-400 to-blue-600',
        seniorFriendly: false,
        deviceTracked: ['Fitbit', 'Samsung'],
        trackingMethod: 'hydration_goal',
        verificationRequired: true,
        category: 'nutrition',
        healthBenefit: 'Maintains optimal hydration for cellular function and energy levels'
      },
      {
        id: 'heart-rate-cadet',
        name: 'Heart Rate Cadet',
        description: 'Maintain resting HR < 75 BPM for 21 days',
        icon: '💓',
        difficulty: 'easy',
        duration: '21 days',
        points: 540,
        total: 21,
        targetValue: 21,
        unit: 'days',
        participants: 18250,
        gradient: 'from-rose-400 to-red-600',
        seniorFriendly: false,
        deviceTracked: ['Fitbit', 'Garmin', 'Withings', 'Apple Watch'],
        trackingMethod: 'resting_heart_rate',
        verificationRequired: true,
        category: 'cardiovascular',
        healthBenefit: 'Improves cardiovascular health and monitors heart rate consistency'
      }
    ],
    medium: [
      {
        id: 'step-streak',
        name: 'Step Streak',
        description: '5K marathon (3.1 miles) daily for 60 days',
        icon: '🏃‍♂️',
        difficulty: 'medium',
        duration: '60 days',
        points: 2500,
        total: 60,
        // Challenge catalog uses total steps goal across the timeline for step-based challenges
        targetValue: 390600, // 6,510 steps/day × 60 days
        unit: 'steps',
        participants: 12340,
        gradient: 'from-orange-400 to-red-600',
        seniorFriendly: false,
        deviceTracked: ['Fitbit', 'Garmin', 'Apple'],
        trackingMethod: 'daily_steps',
        verificationRequired: true,
        category: 'movement',
        healthBenefit: 'Builds exceptional cardiovascular endurance and marathon-level daily distance consistency'
      },
      {
        id: 'sleep-champion',
        name: 'Sleep Champion',
        description: 'Sleep 7+ hrs/night for 60 days',
        icon: '🌙',
        difficulty: 'medium',
        duration: '60 days',
        points: 2500,
        total: 60,
        targetValue: 60,
        unit: 'nights',
        participants: 9630,
        gradient: 'from-indigo-400 to-purple-600',
        seniorFriendly: false,
        deviceTracked: ['Oura', 'Withings', 'Apple'],
        trackingMethod: 'consistent_sleep_optimization',
        verificationRequired: true,
        category: 'recovery',
        healthBenefit: 'Optimizes recovery, immune function and cognitive performance through consistent sleep'
      },
      {
        id: 'heart-rate-mover',
        name: 'Heart Rate Mover',
        description: 'Resting HR below 70 for 60 days',
        icon: '❤️',
        difficulty: 'medium',
        duration: '60 days',
        points: 2500,
        total: 60,
        targetValue: 60,
        unit: 'days',
        participants: 6740,
        gradient: 'from-rose-400 to-red-600',
        seniorFriendly: false,
        deviceTracked: ['Fitbit', 'Garmin', 'Withings', 'Apple'],
        trackingMethod: 'resting_heart_rate_optimization',
        verificationRequired: true,
        category: 'cardiovascular',
        healthBenefit: 'Achieves elite cardiovascular fitness and reduced disease risk'
      },
      {
        id: 'hydration-strider',
        name: 'Hydration Strider',
        description: '7+ glasses of water daily for 60 days',
        icon: '💧',
        difficulty: 'medium',
        duration: '60 days',
        points: 2500,
        total: 60,
        targetValue: 60,
        unit: 'days',
        participants: 8420,
        gradient: 'from-cyan-400 to-blue-600',
        seniorFriendly: false,
        deviceTracked: ['Fitbit', 'Samsung', 'Apple Health'],
        trackingMethod: 'premium_hydration_goal',
        verificationRequired: true,
        category: 'nutrition',
        healthBenefit: 'Achieves optimal hydration for peak cellular function and enhanced athletic performance'
      },
      {
        id: 'weight-warrior',
        name: 'Weight Warrior',
        description: 'Lose 15+ lbs over 60 days',
        icon: '⚖️',
        difficulty: 'medium',
        duration: '60 days',
        points: 2500,
        total: 60,
        targetValue: 15,
        unit: 'lbs_lost',
        participants: 7890,
        gradient: 'from-emerald-400 to-teal-600',
        seniorFriendly: false,
        deviceTracked: ['Withings Scale', 'Fitbit Aria'],
        trackingMethod: 'daily_weight_tracking',
        verificationRequired: true,
        category: 'body_composition',
        healthBenefit: 'Builds consistent weight monitoring habits and awareness for long-term health management',
        // Future Phase indicator for investor/demo visibility
        isActive: false,
        futurePhase: true,
        comingSoonMessage: 'This advanced weight program will be available in a future release.'
      }
    ],
    hard: [
      {
        id: 'step-commander',
        name: 'Step Commander',
        description: '7K marathon (4.35 miles) daily for 90 days',
        icon: '🏃‍♂️',
        difficulty: 'hard',
        duration: '90 days',
  points: 4700,
        total: 90,
  targetValue: 822150, // 9,135 steps × 90 days (4.35 miles/day @ 2100 steps/mile)
  unit: 'steps',
        participants: 4280,
        gradient: 'from-blue-400 to-cyan-600',
        seniorFriendly: false,
        deviceTracked: ['Fitbit', 'Apple'],
  trackingMethod: 'daily_steps',
        verificationRequired: true,
        category: 'movement',
        healthBenefit: 'Builds elite cardiovascular fitness and establishes permanent daily movement habits',
        isActive: true,
        futurePhase: false
      },
      {
        id: 'sleep-rhythm-reset',
        name: 'Sleep Rhythm Reset',
        description: 'Sleep 7.5 hrs/day for 90 days',
        icon: '😴',
        difficulty: 'hard',
        duration: '90 days',
        points: 4400,
        total: 90,
        targetValue: 90,
        unit: 'nights',
        participants: 3120,
        gradient: 'from-indigo-400 to-purple-600',
        seniorFriendly: false,
        deviceTracked: ['Oura', 'Apple', 'Withings'],
        trackingMethod: 'precision_sleep_optimization',
        verificationRequired: true,
        category: 'recovery',
        healthBenefit: 'Achieves optimal recovery and cognitive performance through precision sleep timing',
        isActive: true,
        futurePhase: false
      },
      {
        id: 'weight-commander',
        name: 'Weight Commander',
        description: 'Lose 15+ lbs over 90 days',
        icon: '⚔️',
        difficulty: 'hard',
        duration: '90 days',
        points: 4600,
        total: 90,
        targetValue: 15,
        unit: 'lbs_lost',
        participants: 2850,
        gradient: 'from-emerald-400 to-teal-600',
        seniorFriendly: false,
        deviceTracked: ['Withings Scale', 'Fitbit Aria', 'Apple Health'],
        trackingMethod: 'advanced_weight_management',
        verificationRequired: true,
        category: 'body_composition',
        healthBenefit: 'Achieves significant body transformation through sustained weight management and lifestyle optimization',
        // Future Phase indicator for investor/demo visibility
        isActive: false,
        futurePhase: true,
        comingSoonMessage: 'This advanced weight program will be available in a future release.'
      },
      {
        id: 'hydration-master',
        name: 'Hydration Master',
        description: '7+ glasses of water daily for 90 days',
        icon: '💧',
        duration: '90 days',
        points: 4500,
        total: 90,
        // FIX: targetValue previously set to total glasses (630) caused progressPercentage rounding to 0% early.
        // Align with other hydration challenges: targetValue reflects required streak days (90) and use a separate daily target.
        targetValue: 90, // required consecutive days
        unit: 'days',
        dailyTargetGlasses: 7, // per-day hydration goal (informational)
        totalGlassesGoal: 630, // optional aggregate goal (not used in % calc)
        participants: 3190,
        gradient: 'from-cyan-400 to-blue-600',
        seniorFriendly: false,
        deviceTracked: ['Fitbit', 'Samsung', 'Apple Health'],
        trackingMethod: 'premium_hydration_mastery',
        verificationRequired: true,
        category: 'nutrition',
        healthBenefit: 'Achieves optimal cellular hydration and peak physical performance through consistent daily water intake',
        isActive: true,
        futurePhase: false
      }
    ],
    extreme: [
      {
        id: 'million-step-challenge',
        name: 'Million Step Challenge',
        description: '1 Million steps in 90 days',
        icon: '🏃‍♂️',
        difficulty: 'extreme',
        duration: '90 days',
        points: 12000,
        total: 90,
        targetValue: 1000000,
        unit: 'steps',
        participants: 890,
        gradient: 'from-purple-500 to-pink-600',
        seniorFriendly: false,
        deviceTracked: ['Fitbit', 'Apple', 'Withings'],
        trackingMethod: 'mega_step_challenge',
        verificationRequired: true,
        category: 'epic_movement',
        healthBenefit: 'Achieves legendary cardiovascular fitness and establishes permanent elite movement patterns',
        isActive: true,
        futurePhase: false
      },
      {
        id: 'weight-warrior',
        name: 'Weight Warrior',
        description: 'Lose 33+ lbs (15 kg) in 120 days',
        icon: '⚔️',
        difficulty: 'extreme',
        duration: '120 days',
        points: 11500,
        total: 120,
        targetValue: 33,
        unit: 'lbs_lost',
        participants: 420,
        gradient: 'from-red-500 to-orange-600',
        seniorFriendly: false,
        deviceTracked: ['Withings Scale', 'Fitbit Aria'],
        trackingMethod: 'precision_weight_loss',
        verificationRequired: true,
        category: 'body_transformation',
        healthBenefit: 'Achieves dramatic body transformation with significant health improvements and disease risk reduction',
        // Future Phase indicator for investor/demo visibility
        isActive: false,
        futurePhase: true,
        comingSoonMessage: 'This extreme weight program will be available in a future release.'
      },
      {
        id: 'meditation-master',
        name: 'Meditation Master',
        description: '180 sessions of 20+ min in 90 days',
        icon: '🧘‍♂️',
        difficulty: 'extreme',
        duration: '90 days',
        points: 10800,
        total: 180,
        targetValue: 3600, // 180 sessions × 20 minutes
        unit: 'minutes',
        participants: 280,
        gradient: 'from-indigo-500 to-purple-600',
        seniorFriendly: false,
        deviceTracked: ['Oura', 'Apple', 'Headspace', 'Calm'],
        trackingMethod: 'meditation_mastery',
        verificationRequired: true,
        category: 'mindfulness_mastery',
        healthBenefit: 'Develops profound mental mastery, exceptional stress resilience and advanced consciousness',
        // Future Phase indicator for investor/demo visibility
        isActive: false,
        futurePhase: true,
        comingSoonMessage: 'This elite mindfulness program will be available in a future release.'
      },
      {
        id: 'heart-rate-legend',
        name: 'Low HR Legend',
        description: 'Maintain resting HR < 70 BPM for 120 days',
        icon: '❤️',
        difficulty: 'extreme',
        duration: '120 days',
        points: 10300,
        total: 120,
        targetValue: 120,
        unit: 'days',
        participants: 190,
        gradient: 'from-pink-500 to-red-600',
        seniorFriendly: false,
        deviceTracked: ['Oura', 'Garmin', 'Withings', 'Apple'],
        trackingMethod: 'elite_cardiovascular_optimization',
        verificationRequired: true,
        category: 'cardiovascular_mastery',
        healthBenefit: 'Achieves elite athlete-level cardiovascular fitness and exceptional heart health',
        isActive: true,
        futurePhase: false
      }
    ],
    century: [
      {
        id: 'century-step-master',
        name: 'Century Step Master',
        description: 'Walk 1.5 million steps in 100 days (~15,000 steps/day) - Marathon Ready Training!',
        icon: '👑',
        difficulty: 'century',
        duration: '100 days',
        points: 15000,
        bonusPoints: 2000, // Bonus for consistent weekly targets
        total: 100,
        targetValue: 1500000, // 1.5M steps - Marathon Ready!
        unit: 'steps',
        participants: 42,
        gradient: 'from-black to-gray-900',
        seniorFriendly: false,
        deviceTracked: ['Apple Watch', 'Fitbit', 'Garmin', 'Withings'],
        trackingMethod: 'century_step_mastery',
        verificationRequired: true,
        category: 'century_movement',
        exclusiveAccess: ['Gold', 'Platinum'],
        brandedChallenge: true,
        weeklyTargets: true,
        healthBenefit: 'Achieves marathon-level cardiovascular endurance and creates elite athlete movement habits through century conquest - prepares you for any marathon challenge!',
        isActive: true,
        futurePhase: false
      },
      {
        id: 'century-calm-mind',
        name: 'Century Calm Mind',
        description: '200 meditation sessions of 20+ minutes within 100 days',
        icon: '🧘‍♀️',
        difficulty: 'century',
        duration: '100 days',
        points: 12000,
        specialAchievement: 'Mental Wellness Master',
        total: 200,
        targetValue: 4000, // 200 sessions × 20 minutes
        unit: 'minutes',
        participants: 28,
        gradient: 'from-black to-gray-900',
        seniorFriendly: false,
        deviceTracked: ['Oura', 'Apple Watch mindfulness', 'Garmin Stress tracking'],
        trackingMethod: 'century_mindfulness_mastery',
        verificationRequired: true,
        category: 'century_mindfulness',
        exclusiveAccess: ['Gold', 'Platinum'],
        brandedChallenge: true,
        meditationIntensity: 'advanced',
        healthBenefit: 'Achieves profound mental mastery and advanced consciousness through century mindfulness conquest',
        // Future Phase indicator for investor/demo visibility
        isActive: false,
        futurePhase: true,
        comingSoonMessage: 'This Century-level mindfulness challenge will be available in a future release.'
      },
      {
        id: 'century-body-reset',
        name: 'Century Body Reset',
        description: 'Lose 22-23 kg (50-55 lbs) over 100 days with smart scale verification',
        icon: '⚖️',
        difficulty: 'century',
        duration: '100 days',
        points: 15000,
        bonusPoints: 2000, // Extra for healthy BMI maintenance
        total: 100,
        targetValue: 22500, // 22.5kg average in grams
        unit: 'grams_lost',
        participants: 15,
        gradient: 'from-black to-gray-900',
        seniorFriendly: false,
        deviceTracked: ['Withings Scale', 'Fitbit smart scales', 'MediCureOn nutrition plans'],
        trackingMethod: 'century_body_transformation',
        verificationRequired: true,
        category: 'century_transformation',
        exclusiveAccess: ['Gold', 'Platinum'],
        brandedChallenge: true,
        nutritionPlans: true,
        healthBenefit: 'Achieves dramatic body transformation with significant health improvements and permanent lifestyle change',
        // Future Phase indicator for investor/demo visibility
        isActive: false,
        futurePhase: true,
        comingSoonMessage: 'This Century-level body transformation will be available in a future release.'
      },
      {
        id: 'century-sleep-reset',
        name: 'Century Sleep Reset',
        description: 'Sleep 7.5+ hours every night for 100 days - Perfect sleep mastery',
        icon: '🌙',
        difficulty: 'century',
        duration: '100 days',
        points: 13000,
        specialAchievement: 'Sleep Mastery Champion',
        total: 100,
        targetValue: 100,
        unit: 'nights',
        participants: 22,
        gradient: 'from-black to-gray-900',
        seniorFriendly: false,
        deviceTracked: ['Oura Ring', 'Apple Watch', 'Withings Sleep', 'Garmin'],
        trackingMethod: 'century_sleep_optimization',
        verificationRequired: true,
        category: 'century_recovery',
        exclusiveAccess: ['Gold', 'Platinum'],
        brandedChallenge: true,
        sleepOptimization: true,
        healthBenefit: 'Achieves perfect sleep mastery and optimal recovery through century sleep discipline - transforms overall health and cognitive performance',
        isActive: true,
        futurePhase: false
      }
    ]
  };

  // 🚀 SUBSCRIPTION-BASED CHALLENGE LIMITS - Global Scale Protection!
  const getSubscriptionLimits = () => {
    const subscription = userSubscription?.tier?.toLowerCase() || 'standard';
    
    // 🐛 DEBUG: Log subscription detection
    console.log('🔍 SUBSCRIPTION DEBUG:', {
      rawUserSubscription: userSubscription,
      tierFromSubscription: userSubscription?.tier,
      normalizedSubscription: subscription
    });
    
    const limits = {
      standard: { 
        activeChallenges: 2, 
        name: 'Standard',
        color: 'text-gray-600',
        bgColor: 'bg-gray-100',
        icon: '🥉'
      },
      silver: { 
        activeChallenges: 4, 
        name: 'Silver',
        color: 'text-gray-700',
        bgColor: 'bg-gray-200',
        icon: '🥈'
      },
      gold: { 
        activeChallenges: 6, 
        name: 'Gold',
        color: 'text-yellow-700',
        bgColor: 'bg-yellow-100',
        icon: '🥇'
      },
      platinum: { 
        activeChallenges: 8, 
        name: 'Platinum',
        color: 'text-purple-700',
        bgColor: 'bg-purple-100',
        icon: '💎'
      }
    };
    
    const result = limits[subscription] || limits.standard;
    console.log('🔍 SUBSCRIPTION LIMITS RESULT:', result);
    return result;
  };

  const subscriptionLimits = getSubscriptionLimits();
  const currentActiveChallenges = userActiveChallenges?.length || 0;

  // 👑 CENTURY CHALLENGE PROTECTION - Only ONE active Century challenge at a time!
  const getActiveCenturyChallenges = () => {
    if (!userActiveChallenges) return [];
    return userActiveChallenges.filter(challenge => {
      const diff = (challenge.difficulty || '').toString().toLowerCase();
      const cid = (challenge.challengeId || '').toString().toLowerCase();
      const id = (challenge.id || '').toString().toLowerCase();
      return diff === 'century' || cid.includes('century') || id.includes('century');
    });
  };

  const activeCenturyChallenges = getActiveCenturyChallenges();
  const hasActiveCenturyChallenge = activeCenturyChallenges.length > 0;

  // PRODUCTION-READY: Comprehensive step challenge identification function
  const isStepChallenge = (challengeId) => {
    if (!challengeId) return false;
    
    // Convert to lowercase for consistent matching
    const id = challengeId.toLowerCase();
    
    console.log(`🔍 isStepChallenge checking: "${challengeId}" -> "${id}"`);
    
    // List of ALL step-based challenge IDs for production (extensive list)
    const stepChallengeIds = [
      'century-step-master',
      'centurystepmaster',
      'century_step_master',
      'century step master',
      'million-step-challenge',
      'millionstepchallenge', 
      'million_step_challenge',
      'million step challenge',
  'step-commander',
      'stepmastery',
      'step_mastery',
      'step mastery',
      'step-streak',
      'stepstreak', 
      'step_streak',
      'step streak',
  'step-scout',
      'stepstarter',
      'step_starter',
      'step starter',
      // Note: 'gentle-morning-walker' is NOT included here as it's a daily consistency challenge with trackingMethod: 'steps_before_time'
      'light-activity-streak',
      'lightactivitystreak',
      'light_activity_streak',
      'light activity streak'
    ];
    
    // Check multiple patterns:
    const isExactMatch = stepChallengeIds.includes(id);
    const hasStep = id.includes('step');
    const hasWalk = id.includes('walk');
    const isCenturyStep = id.includes('century') && id.includes('step');
    const isMillionStep = id.includes('million') && id.includes('step');
    
    // EXCLUDE specific daily consistency challenges that use special tracking methods
    const isDailyConsistencyChallenge = id.includes('gentle-morning-walker') || id.includes('gentle morning walker');
    
    const result = (isExactMatch || hasStep || hasWalk || isCenturyStep || isMillionStep) && !isDailyConsistencyChallenge;
    console.log(`  - Has 'step': ${hasStep}`);
    console.log(`  - Has 'walk': ${hasWalk}`);
    return result;
  };

  // Use the passed getUserChallengeStatus function or create a wrapper
  const getUserChallengeStatusLocal = getUserChallengeStatus || ((challengeId) => {
    // 🔧 DEBUG: Log every call to this function
    // 🔧 CRITICAL FIX: Check user's actual challenge data first
    const isActiveChallenge = userActiveChallenges.some(c => c.challengeId === challengeId);
    const isCompletedChallenge = userCompletedChallenges.some(c => c.challengeId === challengeId);
    
    // 🚨 SLEEP SCHEDULE SPECIFIC FIX: Never show as completed if not in user data
  if ((challengeId === 'sleep-rookie' || challengeId === 'sleep-schedule') && !isActiveChallenge && !isCompletedChallenge) {
      console.warn(`🛑 SLEEP SCHEDULE BUG FIX: Forcing status to 'available' - challenge never started by user`);
      return { status: 'available' };
    }
    
    if (isActiveChallenge) {
      return { status: 'active' };
    } else if (isCompletedChallenge) {
      return { status: 'completed' };
    }
    
    // Only call getChallengeStatus as fallback for other logic
    if (getChallengeStatus) {
      const fallbackStatus = getChallengeStatus(challengeId);
      // 🚨 HEALTHCARE BUG FIX: Never return 'completed' for challenges that user never started
      if (fallbackStatus.status === 'completed' && !isCompletedChallenge) {
        console.warn(`🚨 BUG PREVENTED: ${challengeId} was returning 'completed' but user never started this challenge`);
        return { status: 'available' };
      }
      return fallbackStatus;
    }
    
    console.log(`🔍 ${challengeId} defaulting to 'available'`);
    // Default: challenge is available to join
    return { status: 'available' };
  });

  // � CHALLENGE DETAILS POPUP - Comprehensive Challenge Information
  const ChallengeDetailsPopup = ({ challenge, userChallenge, onClose }) => {
    if (!challenge) return null;
    
    // ISSUE #2 & #3 FIX: Use enhanced progress calculation and accurate days
    const progressData = getUserChallengeProgress(challenge.id);
    const currentSteps = progressData?.cumulativeSteps || progressData?.currentProgress || 0;
    const totalDays = challenge.total || 100;
    const dailyTarget = challenge.unit === 'steps' ? (challenge.targetValue || 0) / totalDays : challenge.targetValue;
    
    // 🔧 FIXED: Accurate day calculation - Day counting starts from 1 on start date
    const getAccurateDaysElapsed = () => {
      if (!userChallenge?.startDate) return 0;
      
      const startDate = new Date(userChallenge.startDate);
      const today = new Date();
      
      // Calculate using calendar days, not 24-hour intervals
      const startDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
      const currentDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      
      const timeDifference = currentDay.getTime() - startDay.getTime();
      const daysDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
      
      // 🎯 CORRECTED: Start day = Day 1, next day = Day 2, etc.
      return daysDifference + 1;
    };
    
    const daysElapsed = getAccurateDaysElapsed();
    // 🔧 FIXED: Remaining days calculation - if on Day 2 of 21, then 19 days remaining
    const daysRemaining = Math.max(0, totalDays - daysElapsed);
    const progressPercentage = challenge.unit === 'steps' 
      ? ((currentSteps / (challenge.targetValue || 1)) * 100)
      : ((userChallenge?.currentProgress || 0) / totalDays * 100);
    
    const dailyProgressPercentage = challenge.unit === 'steps' 
      ? Math.min(((progressData?.todaysSteps || 0) / dailyTarget) * 100, 100)
      : 0;
    
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
        <div className="relative w-full max-w-2xl max-h-[90vh] overflow-auto bg-gray-900 border border-gray-700 rounded-xl shadow-2xl">
          {/* Header */}
          <div className="sticky top-0 z-10 px-6 py-4 border-b border-gray-700 bg-gradient-to-r from-gray-800 to-gray-900">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{challenge.icon}</span>
                <div>
                  <h2 className="text-xl font-bold text-white">{challenge.name}</h2>
                  <span className="px-2 py-1 text-xs font-semibold tracking-wide text-gray-300 uppercase bg-gray-700 rounded-full">
                    {challenge.difficulty} • {challenge.duration}
                  </span>
                </div>
              </div>
              <button 
                onClick={onClose}
                className="p-2 text-gray-400 transition-colors rounded-lg hover:text-white hover:bg-gray-700"
              >
                ✕
              </button>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Description */}
            <div>
              <h3 className="mb-2 text-lg font-semibold text-white">Challenge Description</h3>
              <p className="leading-relaxed text-gray-300">{challenge.description}</p>
            </div>
            
            {/* Enhanced Progress Overview with Today's and Total Progress */}
            {userChallenge && (
              <div className="p-4 border border-gray-700 rounded-lg bg-gray-800/50">
                <h3 className="mb-4 text-lg font-semibold text-white">📊 Progress Tracking</h3>
                
                {/* Step Streak (Medium, 60 days) - miles-first rendering from backend snapshot */}
                {challenge.id === 'step-streak' && (() => {
                  const backendSnapshot = (challengeProgress && challengeProgress['step-streak']) || {};
                  const stepsToday = Number(
                    backendSnapshot.steps ?? progressData?.todaysSteps ?? 0
                  );
                  const milesToday = typeof backendSnapshot.milesToday === 'number' 
                    ? backendSnapshot.milesToday 
                    : (stepsToday / 2100);
                  const dailyPct = Math.min(100, Number(backendSnapshot.dailyProgressPercentage ?? Math.round((stepsToday / 6510) * 100)) || 0);
                  const totalSteps = Number(backendSnapshot.totalSteps || 0);
                  const totalMiles = typeof backendSnapshot.totalMiles === 'number' 
                    ? backendSnapshot.totalMiles 
                    : (totalSteps / 2100);
                  const daysElapsed = Number(backendSnapshot.currentProgress || userChallenge?.currentProgress || 0);
                  const totalPct = Math.min(100, Number(backendSnapshot.progressPercentage ?? Math.round((daysElapsed / 60) * 100)) || 0);
                  return (
                    <div className="mb-6 space-y-4">
                      {/* Today's Progress */}
                      <div className="p-3 border border-orange-600 rounded-lg bg-orange-900/20">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-orange-300">📅 Today's Progress</span>
                          <span className="text-sm text-orange-200">{new Date().toLocaleDateString()}</span>
                        </div>
                        <div className="text-2xl font-bold text-orange-300">{milesToday.toFixed(1)} miles</div>
                        <div className="mt-1 text-sm text-orange-300">({stepsToday.toLocaleString()} steps)</div>
                        <div className="mt-1 text-sm text-orange-300">Today: {stepsToday.toLocaleString()} / 6,510 steps</div>
                        <div className="mt-1 text-sm text-orange-300">Target: ≈ 3.1 miles (6,510 steps)</div>
                        <div className="w-full h-3 mt-2 bg-orange-900 rounded-full">
                          <div
                            className="h-3 transition-all duration-500 rounded-full bg-gradient-to-r from-orange-400 to-red-400"
                            style={{ width: `${dailyPct}%` }}
                          ></div>
                        </div>
                        <div className="mt-1 text-xs text-orange-300">{dailyPct.toFixed(1)}% of today's goal</div>
                      </div>

                      {/* Total Progress */}
                      <div className="p-3 border border-yellow-600 rounded-lg bg-yellow-900/20">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-yellow-300">🏆 Total Progress</span>
                          <span className="text-sm text-yellow-200">Days elapsed</span>
                        </div>
                        <div className="text-2xl font-bold text-yellow-300">Day {daysElapsed} / 60</div>
                        <div className="mt-1 text-sm text-yellow-300">
                          Total: {totalMiles.toFixed(1)} miles ({totalSteps.toLocaleString()} steps)
                        </div>
                        <div className="mt-1 text-xs text-yellow-300">Goal: 390,600 steps total (≈186 miles)</div>
                        <div className="w-full h-3 mt-2 bg-yellow-900 rounded-full">
                          <div
                            className="h-3 transition-all duration-700 ease-out rounded-full bg-gradient-to-r from-yellow-400 via-amber-400 to-orange-400"
                            style={{ width: `${totalPct}%` }}
                          ></div>
                        </div>
                        <div className="mt-1 text-xs text-yellow-300">{totalPct.toFixed(1)}% of 60-day timeline</div>
                      </div>
                    </div>
                  );
                })()}

                {/* Step Commander (Hard, 90 days) - miles-first rendering from backend snapshot */}
                {challenge.id === 'step-commander' && (() => {
                  const backendSnapshot = (challengeProgress && challengeProgress['step-commander']) || {};
                  const stepsToday = Number(
                    backendSnapshot.steps ?? progressData?.todaysSteps ?? 0
                  );
                  const milesToday = typeof backendSnapshot.milesToday === 'number' 
                    ? backendSnapshot.milesToday 
                    : (stepsToday / 2100);
                  const dailyPct = Math.min(100, Number(backendSnapshot.dailyProgressPercentage ?? Math.round((stepsToday / 9135) * 100)) || 0);
                  const totalSteps = Number(backendSnapshot.totalSteps || 0);
                  const totalMiles = typeof backendSnapshot.totalMiles === 'number' 
                    ? backendSnapshot.totalMiles 
                    : (totalSteps / 2100);
                  const daysElapsed = Number(backendSnapshot.currentProgress || userChallenge?.currentProgress || 0);
                  const totalPct = Math.min(100, Number(backendSnapshot.progressPercentage ?? Math.round((daysElapsed / 90) * 100)) || 0);
                  return (
                    <div className="mb-6 space-y-4">
                      {/* Today's Progress */}
                      <div className="p-3 border border-indigo-600 rounded-lg bg-indigo-900/20">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-indigo-300">📅 Today's Progress</span>
                          <span className="text-sm text-indigo-200">{new Date().toLocaleDateString()}</span>
                        </div>
                        <div className="text-2xl font-bold text-indigo-300">{milesToday.toFixed(1)} miles</div>
                        <div className="mt-1 text-sm text-indigo-300">({stepsToday.toLocaleString()} steps)</div>
                        <div className="mt-1 text-sm text-indigo-300">Today: {stepsToday.toLocaleString()} / 9,135 steps</div>
                        <div className="mt-1 text-sm text-indigo-300">Target: ≈ 4.35 miles (9,135 steps)</div>
                        <div className="w-full h-3 mt-2 bg-indigo-900 rounded-full">
                          <div
                            className="h-3 transition-all duration-500 rounded-full bg-gradient-to-r from-indigo-400 to-blue-400"
                            style={{ width: `${dailyPct}%` }}
                          ></div>
                        </div>
                        <div className="mt-1 text-xs text-indigo-300">{dailyPct.toFixed(1)}% of today's goal</div>
                      </div>

                      {/* Total Progress */}
                      <div className="p-3 border border-purple-600 rounded-lg bg-purple-900/20">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-purple-300">🏆 Total Progress</span>
                          <span className="text-sm text-purple-200">Days elapsed</span>
                        </div>
                        <div className="text-2xl font-bold text-purple-300">Day {daysElapsed} / 90</div>
                        <div className="mt-1 text-sm text-purple-300">
                          Total: {totalMiles.toFixed(1)} miles ({totalSteps.toLocaleString()} steps)
                        </div>
                        <div className="mt-1 text-xs text-purple-300">Goal: 822,150 steps total (≈391 miles)</div>
                        <div className="w-full h-3 mt-2 bg-purple-900 rounded-full">
                          <div
                            className="h-3 transition-all duration-700 ease-out rounded-full bg-gradient-to-r from-purple-400 via-fuchsia-400 to-pink-400"
                            style={{ width: `${totalPct}%` }}
                          ></div>
                        </div>
                        <div className="mt-1 text-xs text-purple-300">{totalPct.toFixed(1)}% of 90-day timeline</div>
                      </div>
                    </div>
                  );
                })()}

                {/* Step Scout (Easy, 21 days) - windowed totals with daily steps gauge */}
                {challenge.id === 'step-scout' && (() => {
                  const backendSnapshot = (challengeProgress && challengeProgress['step-scout']) || {};
                  const stepsToday = Number(
                    backendSnapshot.steps ?? progressData?.todaysSteps ?? 0
                  );
                  const dailyPct = Math.min(100, Number(backendSnapshot.dailyProgressPercentage ?? Math.round((stepsToday / 3000) * 100)) || 0);
                  const totalSteps = Number(backendSnapshot.totalSteps || 0);
                  const daysElapsed = Number(backendSnapshot.currentProgress || userChallenge?.currentProgress || 0);
                  const totalPct = Math.min(100, Number(backendSnapshot.progressPercentage ?? Math.round((daysElapsed / 21) * 100)) || 0);
                  return (
                    <div className="mb-6 space-y-4">
                      {/* Today's Progress */}
                      <div className="p-3 border border-blue-600 rounded-lg bg-blue-900/20">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-blue-300">📅 Today's Progress</span>
                          <span className="text-sm text-blue-200">{new Date().toLocaleDateString()}</span>
                        </div>
                        <div className="text-2xl font-bold text-blue-400">{stepsToday.toLocaleString()} steps</div>
                        <div className="mt-1 text-sm text-blue-300">Target: 3,000 steps/day</div>
                        <div className="w-full h-3 mt-2 bg-blue-900 rounded-full">
                          <div 
                            className="h-3 transition-all duration-500 rounded-full bg-gradient-to-r from-blue-400 to-cyan-400"
                            style={{ width: `${dailyPct}%` }}
                          ></div>
                        </div>
                        <div className="mt-1 text-xs text-blue-300">{dailyPct.toFixed(1)}% of today's goal</div>
                      </div>

                      {/* Total Progress */}
                      <div className="p-3 border border-green-600 rounded-lg bg-green-900/20">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-green-300">🏆 Total Progress</span>
                          <span className="text-sm text-green-200">Days elapsed</span>
                        </div>
                        <div className="text-2xl font-bold text-green-400">Day {daysElapsed} / 21</div>
                        <div className="mt-1 text-sm text-green-300">Total steps: {totalSteps.toLocaleString()} (windowed)</div>
                        <div className="mt-1 text-xs text-green-300">Goal: 63,000 steps total</div>
                        <div className="w-full h-3 mt-2 bg-green-900 rounded-full">
                          <div 
                            className="h-3 transition-all duration-700 ease-out rounded-full bg-gradient-to-r from-green-400 via-emerald-400 to-green-500"
                            style={{ width: `${totalPct}%` }}
                          ></div>
                        </div>
                        <div className="mt-1 text-xs text-green-300">{totalPct.toFixed(1)}% of 21-day timeline</div>
                      </div>
                    </div>
                  );
                })()}

                {/* Million Step Challenge (Extreme, 90 days) - million total, daily 11,111 */}
                {challenge.id === 'million-step-challenge' && (() => {
                  const backendSnapshot = (challengeProgress && challengeProgress['million-step-challenge']) || {};
                  const stepsToday = Number(
                    backendSnapshot.todaysSteps ?? backendSnapshot.steps ?? progressData?.todaysSteps ?? 0
                  );
                  const milesToday = typeof backendSnapshot.milesToday === 'number' 
                    ? backendSnapshot.milesToday 
                    : (stepsToday / 2100);
                  const dailyPct = Math.min(100, Number(backendSnapshot.dailyProgressPercentage ?? Math.round((stepsToday / 11111) * 100)) || 0);
                  const totalSteps = Number(backendSnapshot.totalSteps || 0);
                  const totalMiles = typeof backendSnapshot.totalMiles === 'number' 
                    ? backendSnapshot.totalMiles 
                    : (totalSteps / 2100);
                  const daysElapsed = Number(backendSnapshot.currentProgress ?? progressData?.currentProgress ?? 0);
                  const totalPct = Math.min(100, Number(backendSnapshot.progressPercentage ?? Math.round((daysElapsed / 90) * 100)) || 0);
                  return (
                    <div className="mb-6 space-y-4">
                      {/* Today's Progress */}
                      <div className="p-3 border border-pink-600 rounded-lg bg-pink-900/20">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-pink-300">📅 Today's Progress</span>
                          <span className="text-sm text-pink-200">{new Date().toLocaleDateString()}</span>
                        </div>
                        <div className="text-2xl font-bold text-pink-300">{stepsToday.toLocaleString()} steps</div>
                        <div className="mt-1 text-sm text-pink-300">≈ {milesToday.toFixed(1)} miles (at 2100 steps/mile)</div>
                        <div className="mt-1 text-sm text-pink-300">Today: {stepsToday.toLocaleString()} / 11,111 steps</div>
                        <div className="w-full h-3 mt-2 bg-pink-900 rounded-full">
                          <div
                            className="h-3 transition-all duration-500 rounded-full bg-gradient-to-r from-pink-400 to-fuchsia-400"
                            style={{ width: `${dailyPct}%` }}
                          ></div>
                        </div>
                        <div className="mt-1 text-xs text-pink-300">{dailyPct.toFixed(1)}% of today's goal</div>
                      </div>

                      {/* Total Progress */}
                      <div className="p-3 border border-purple-600 rounded-lg bg-purple-900/20">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-purple-300">🏆 Total Progress</span>
                          <span className="text-sm text-purple-200">Days elapsed</span>
                        </div>
                        <div className="text-2xl font-bold text-purple-300">Day {daysElapsed} / 90</div>
                        <div className="mt-1 text-sm text-purple-300">
                          Total: {totalSteps.toLocaleString()} / 1,000,000 steps (≈ {totalMiles.toFixed(0)} miles)
                        </div>
                        <div className="w-full h-3 mt-2 bg-purple-900 rounded-full">
                          <div
                            className="h-3 transition-all duration-700 ease-out rounded-full bg-gradient-to-r from-purple-400 via-fuchsia-400 to-pink-400"
                            style={{ width: `${totalPct}%` }}
                          ></div>
                        </div>
                        <div className="mt-1 text-xs text-purple-300">{totalPct.toFixed(1)}% of 90-day timeline</div>
                      </div>
                    </div>
                  );
                })()}

                {/* Today's and Total Progress - Enhanced Display for other step challenges */}
                {isStepChallenge(challenge.id) && challenge.id !== 'step-streak' && challenge.id !== 'step-scout' && (
                  <div className="mb-6 space-y-4">
                    {/* Today's Progress */}
                    <div className="p-3 border border-blue-600 rounded-lg bg-blue-900/20">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-blue-300">📅 Today's Progress</span>
                        <span className="text-sm text-blue-200">
                          {new Date().toLocaleDateString()}
                        </span>
                      </div>
                      <div className="text-2xl font-bold text-blue-400">
                        {(progressData?.todaysSteps || 0).toLocaleString()} steps
                      </div>
                      <div className="mt-1 text-sm text-blue-300">
                        Target: {dailyTarget.toLocaleString()} steps/day
                      </div>
                      <div className="w-full h-3 mt-2 bg-blue-900 rounded-full">
                        <div 
                          className="h-3 transition-all duration-500 rounded-full bg-gradient-to-r from-blue-400 to-cyan-400"
                          style={{ width: `${Math.min(dailyProgressPercentage, 100)}%` }}
                        ></div>
                      </div>
                      <div className="mt-1 text-xs text-blue-300">
                        {dailyProgressPercentage.toFixed(1)}% of today's goal
                      </div>
                    </div>
                    
                    {/* Total Progress */}
                    <div className="p-3 border border-green-600 rounded-lg bg-green-900/20">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-green-300">🏆 Total Progress</span>
                        <span className="text-sm text-green-200">
                          Cumulative
                        </span>
                      </div>
                      <div className="text-2xl font-bold text-green-400">
                        {(progressData?.cumulativeSteps || 0).toLocaleString()} steps
                      </div>
                      <div className="mt-1 text-sm text-green-300">
                        Goal: {(challenge.targetValue || 0).toLocaleString()} steps total
                      </div>
                      <div className="w-full h-3 mt-2 bg-green-900 rounded-full">
                        <div 
                          className="h-3 transition-all duration-700 ease-out rounded-full bg-gradient-to-r from-green-400 via-emerald-400 to-green-500"
                          style={{ width: `${Math.min(progressPercentage, 100)}%` }}
                        ></div>
                      </div>
                      <div className="mt-1 text-xs text-green-300">
                        {progressPercentage.toFixed(1)}% of total challenge goal
                      </div>
                    </div>
                  </div>
                )}

                {/* Gentle Morning Walker - detailed progress using steps-before-9AM */}
                {challenge.id === 'gentle-morning-walker' && (
                  <div className="mb-6 space-y-4">
                    {(() => {
                      const backendSnapshot = challengeProgress['gentle-morning-walker'] || {};
                      const todaysPct = Math.min(backendSnapshot.dailyProgressPercentage || 0, 100);
                      const todaysStepsBeforeNine = backendSnapshot.stepsBeforeNine || 0;
                      const consecutiveDays = Number(backendSnapshot.consecutiveDays || userChallenge?.consecutiveDays || 0);
                      const requiredDays = 7;
                      const totalPct = Math.min((consecutiveDays / requiredDays) * 100, 100);
                      return (
                        <>
                          {/* Today's Progress */}
                          <div className="p-3 border rounded-lg border-emerald-600 bg-emerald-900/20">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-semibold text-emerald-300">📅 Today's Progress</span>
                              <span className="text-sm text-emerald-200">{new Date().toLocaleDateString()}</span>
                            </div>
                            <div className="text-2xl font-bold text-emerald-300">{todaysStepsBeforeNine.toLocaleString()} steps before 9AM</div>
                            <div className="mt-1 text-sm text-emerald-300">Target: 500 steps before 9AM</div>
                            <div className="w-full h-3 mt-2 rounded-full bg-emerald-900">
                              <div
                                className="h-3 transition-all duration-500 rounded-full bg-gradient-to-r from-emerald-400 to-green-400"
                                style={{ width: `${todaysPct}%` }}
                              ></div>
                            </div>
                            <div className="mt-1 text-xs text-emerald-300">{todaysPct.toFixed(1)}% of today's goal</div>
                          </div>

                          {/* Total Progress */}
                          <div className="p-3 border border-yellow-600 rounded-lg bg-yellow-900/20">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-semibold text-yellow-300">🏆 Total Progress</span>
                              <span className="text-sm text-yellow-200">Consecutive mornings</span>
                            </div>
                            <div className="text-2xl font-bold text-yellow-300">{consecutiveDays} / {requiredDays} days</div>
                            <div className="w-full h-3 mt-2 bg-yellow-900 rounded-full">
                              <div
                                className="h-3 transition-all duration-700 ease-out rounded-full bg-gradient-to-r from-yellow-400 via-amber-400 to-orange-400"
                                style={{ width: `${totalPct}%` }}
                              ></div>
                            </div>
                            <div className="mt-1 text-xs text-yellow-300">{totalPct.toFixed(1)}% of total challenge goal</div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Hydration Hero - detailed progress like Gentle Morning Walker */}
                {challenge.id === 'hydration-hero' && (
                  <div className="mb-6 space-y-4">
                    {(() => {
                      const backendSnapshot = challengeProgress['hydration-hero'] || {};
                      const todaysPct = Math.min(backendSnapshot.dailyProgressPercentage || 0, 100);
                      const waterGlasses = backendSnapshot.waterGlasses || progressData?.waterGlasses || 0;
                      const consecutiveDays = Number(backendSnapshot.consecutiveDays || userChallenge?.consecutiveDays || 0);
                      const requiredDays = 7;
                      const totalPct = Math.min((consecutiveDays / requiredDays) * 100, 100);
                      return (
                        <>
                          {/* Today's Progress */}
                          <div className="p-3 border rounded-lg border-cyan-600 bg-cyan-900/20">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-semibold text-cyan-300">📅 Today's Progress</span>
                              <span className="text-sm text-cyan-200">{new Date().toLocaleDateString()}</span>
                            </div>
                            <div className="text-2xl font-bold text-cyan-300">{waterGlasses} glasses</div>
                            <div className="mt-1 text-sm text-cyan-300">Target: 7 glasses</div>
                            <div className="w-full h-3 mt-2 rounded-full bg-cyan-900">
                              <div className="h-3 transition-all duration-500 rounded-full bg-gradient-to-r from-cyan-400 to-blue-400" style={{ width: `${todaysPct}%` }}></div>
                            </div>
                            <div className="mt-1 text-xs text-cyan-300">{todaysPct.toFixed(1)}% of today's goal</div>
                          </div>

                          {/* Total Progress */}
                          <div className="p-3 border border-blue-600 rounded-lg bg-blue-900/20">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-semibold text-blue-300">🏆 Total Progress</span>
                              <span className="text-sm text-blue-200">Consecutive days</span>
                            </div>
                            <div className="text-2xl font-bold text-blue-300">{consecutiveDays} / {requiredDays} days</div>
                            <div className="w-full h-3 mt-2 bg-blue-900 rounded-full">
                              <div className="h-3 transition-all duration-700 ease-out rounded-full bg-gradient-to-r from-blue-400 via-sky-400 to-cyan-400" style={{ width: `${totalPct}%` }}></div>
                            </div>
                            <div className="mt-1 text-xs text-blue-300">{totalPct.toFixed(1)}% of total challenge goal</div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Hydration Initiate - 21 day version */}
                {challenge.id === 'hydration-initiate' && (
                  <div className="mb-6 space-y-4">
                    {(() => {
                      const backendSnapshot = challengeProgress['hydration-initiate'] || {};
                      const todaysPct = Math.min(backendSnapshot.dailyProgressPercentage || 0, 100);
                      const waterGlasses = backendSnapshot.waterGlasses || progressData?.waterGlasses || 0;
                      const consecutiveDays = Number(backendSnapshot.consecutiveDays || userChallenge?.consecutiveDays || 0);
                      const requiredDays = 21;
                      const totalPct = Math.min((consecutiveDays / requiredDays) * 100, 100);
                      return (
                        <>
                          {/* Today's Progress */}
                          <div className="p-3 border rounded-lg border-cyan-600 bg-cyan-900/20">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-semibold text-cyan-300">📅 Today's Progress</span>
                              <span className="text-sm text-cyan-200">{new Date().toLocaleDateString()}</span>
                            </div>
                            <div className="text-2xl font-bold text-cyan-300">{waterGlasses} glasses</div>
                            <div className="mt-1 text-sm text-cyan-300">Target: 7 glasses</div>
                            <div className="w-full h-3 mt-2 rounded-full bg-cyan-900">
                              <div className="h-3 transition-all duration-500 rounded-full bg-gradient-to-r from-cyan-400 to-blue-400" style={{ width: `${todaysPct}%` }}></div>
                            </div>
                            <div className="mt-1 text-xs text-cyan-300">{todaysPct.toFixed(1)}% of today's goal</div>
                          </div>

                          {/* Total Progress */}
                          <div className="p-3 border border-blue-600 rounded-lg bg-blue-900/20">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-semibold text-blue-300">🏆 Total Progress</span>
                              <span className="text-sm text-blue-200">Consecutive days</span>
                            </div>
                            <div className="text-2xl font-bold text-blue-300">{consecutiveDays} / {requiredDays} days</div>
                            <div className="w-full h-3 mt-2 bg-blue-900 rounded-full">
                              <div className="h-3 transition-all duration-700 ease-out rounded-full bg-gradient-to-r from-blue-400 via-sky-400 to-cyan-400" style={{ width: `${totalPct}%` }}></div>
                            </div>
                            <div className="mt-1 text-xs text-blue-300">{totalPct.toFixed(1)}% of total challenge goal</div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Hydration Strider - 60 day version */}
                {challenge.id === 'hydration-strider' && (
                  <div className="mb-6 space-y-4">
                    {(() => {
                      const backendSnapshot = challengeProgress['hydration-strider'] || {};
                      const todaysPct = Math.min(backendSnapshot.dailyProgressPercentage || 0, 100);
                      const waterGlasses = backendSnapshot.waterGlasses || progressData?.waterGlasses || 0;
                      const consecutiveDays = Number(backendSnapshot.consecutiveDays || userChallenge?.consecutiveDays || 0);
                      const requiredDays = 60;
                      const totalPct = Math.min((consecutiveDays / requiredDays) * 100, 100);
                      return (
                        <>
                          {/* Today's Progress */}
                          <div className="p-3 border rounded-lg border-cyan-600 bg-cyan-900/20">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-semibold text-cyan-300">📅 Today's Progress</span>
                              <span className="text-sm text-cyan-200">{new Date().toLocaleDateString()}</span>
                            </div>
                            <div className="text-2xl font-bold text-cyan-300">{waterGlasses} glasses</div>
                            <div className="mt-1 text-sm text-cyan-300">Target: 7 glasses</div>
                            <div className="w-full h-3 mt-2 rounded-full bg-cyan-900">
                              <div className="h-3 transition-all duration-500 rounded-full bg-gradient-to-r from-cyan-400 to-blue-400" style={{ width: `${todaysPct}%` }}></div>
                            </div>
                            <div className="mt-1 text-xs text-cyan-300">{todaysPct.toFixed(1)}% of today's goal</div>
                          </div>

                          {/* Total Progress */}
                          <div className="p-3 border border-blue-600 rounded-lg bg-blue-900/20">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-semibold text-blue-300">🏆 Total Progress</span>
                              <span className="text-sm text-blue-200">Consecutive days</span>
                            </div>
                            <div className="text-2xl font-bold text-blue-300">{consecutiveDays} / {requiredDays} days</div>
                            <div className="w-full h-3 mt-2 bg-blue-900 rounded-full">
                              <div className="h-3 transition-all duration-700 ease-out rounded-full bg-gradient-to-r from-blue-400 via-sky-400 to-cyan-400" style={{ width: `${totalPct}%` }}></div>
                            </div>
                            <div className="mt-1 text-xs text-blue-300">{totalPct.toFixed(1)}% of total challenge goal</div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Hydration Master - 90 day version */}
                {challenge.id === 'hydration-master' && (
                  <div className="mb-6 space-y-4">
                    {(() => {
                      const backendSnapshot = challengeProgress['hydration-master'] || {};
                      const todaysPct = Math.min(backendSnapshot.dailyProgressPercentage || 0, 100);
                      const waterGlasses = backendSnapshot.waterGlasses || progressData?.waterGlasses || 0;
                      const consecutiveDays = Number(backendSnapshot.consecutiveDays || userChallenge?.consecutiveDays || 0);
                      const requiredDays = 90;
                      const totalPct = Math.min((consecutiveDays / requiredDays) * 100, 100);
                      return (
                        <>
                          {/* Today's Progress */}
                          <div className="p-3 border rounded-lg border-cyan-600 bg-cyan-900/20">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-semibold text-cyan-300">📅 Today's Progress</span>
                              <span className="text-sm text-cyan-200">{new Date().toLocaleDateString()}</span>
                            </div>
                            <div className="text-2xl font-bold text-cyan-300">{waterGlasses} glasses</div>
                            <div className="mt-1 text-sm text-cyan-300">Target: 7 glasses</div>
                            <div className="w-full h-3 mt-2 rounded-full bg-cyan-900">
                              <div className="h-3 transition-all duration-500 rounded-full bg-gradient-to-r from-cyan-400 to-blue-400" style={{ width: `${todaysPct}%` }}></div>
                            </div>
                            <div className="mt-1 text-xs text-cyan-300">{todaysPct.toFixed(1)}% of today's goal</div>
                          </div>

                          {/* Total Progress */}
                          <div className="p-3 border border-blue-600 rounded-lg bg-blue-900/20">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-semibold text-blue-300">🏆 Total Progress</span>
                              <span className="text-sm text-blue-200">Consecutive days</span>
                            </div>
                            <div className="text-2xl font-bold text-blue-300">{consecutiveDays} / {requiredDays} days</div>
                            <div className="w-full h-3 mt-2 bg-blue-900 rounded-full">
                              <div className="h-3 transition-all duration-700 ease-out rounded-full bg-gradient-to-r from-blue-400 via-sky-400 to-cyan-400" style={{ width: `${totalPct}%` }}></div>
                            </div>
                            <div className="mt-1 text-xs text-blue-300">{totalPct.toFixed(1)}% of total challenge goal</div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Heart Rate Calm - 7 day version */}
                {challenge.id === 'heart-rate-calm' && (
                  <div className="mb-6 space-y-4">
                    {(() => {
                      const backendSnapshot = challengeProgress['heart-rate-calm'] || {};
                      const targetHR = Number(userChallenge?.targetValue || challenge.targetValue || 70);
                      const backendDaily = typeof backendSnapshot.dailyProgressPercentage === 'number' ? backendSnapshot.dailyProgressPercentage : (typeof progressData?.dailyProgressPercentage === 'number' ? progressData.dailyProgressPercentage : (typeof progressData?.todaysPercentage === 'number' ? progressData.todaysPercentage : 0));
                      const todaysHR = Number(
                        backendSnapshot.todaysProgress ??
                        progressData?.restingHeartRate ??
                        progressData?.todaysProgress ??
                        healthData?.healthMetrics?.vitalSigns?.heartRate?.value ??
                        healthData?.realTimeMetrics?.heartRate ??
                        healthData?.realTimeMetrics?.restingHeartRate ??
                        healthData?.vitals?.heartRate?.resting ??
                        0
                      );
                      const computedDaily = todaysHR > 0 ? (todaysHR <= targetHR ? 100 : Math.max(0, 100 - ((todaysHR - targetHR) * 5))) : 0;
                      const todaysPct = Math.min(Math.max(backendDaily, computedDaily), 100);
                      const consecutiveDays = Number(backendSnapshot.consecutiveDays || userChallenge?.consecutiveDays || 0);
                      const requiredDays = 7;
                      const todaySuccess = (todaysHR > 0 && todaysHR <= targetHR) || (todaysPct >= 100);
                      // Robust today key + validation date comparison (timezone safe) to avoid accidental double count
                      const todayKey = new Date().toISOString().split('T')[0];
                      const lastValidationRaw = backendSnapshot.lastValidation || backendSnapshot.lastValidated || userChallenge?.lastValidated;
                      let lastValIsToday = false;
                      if (lastValidationRaw) {
                        try {
                          const lastValKey = new Date(lastValidationRaw).toISOString().split('T')[0];
                          lastValIsToday = (lastValKey === todayKey);
                        } catch (e) { /* ignore parse errors */ }
                      }
                      const recordedToday = (backendSnapshot.todaysProgress && backendSnapshot.todaysProgress > 0) ||
                                             (backendSnapshot.dailyProgress && backendSnapshot.dailyProgress[todayKey] !== undefined) ||
                                             (backendSnapshot.dailyProgressPercentage >= 100); // fallback if percentage only
                      const alreadyCounted = lastValIsToday || recordedToday;
                      const displayDays = Math.min(consecutiveDays + (!alreadyCounted && todaySuccess ? 1 : 0), requiredDays);
                      const totalPct = Math.min((displayDays / requiredDays) * 100, 100);
                      return (
                        <>
                          {/* Today's Progress */}
                          <div className="p-3 border rounded-lg border-rose-600 bg-rose-900/20">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-semibold text-rose-300">📅 Today's Progress</span>
                              <span className="text-sm text-rose-200">{new Date().toLocaleDateString()}</span>
                            </div>
                            <div className="text-2xl font-bold text-rose-300">{todaysHR > 0 ? `${todaysHR} bpm` : 'No reading today'}</div>
                            <div className="mt-1 text-sm text-rose-300">Target: ≤ {targetHR} bpm (lower is better)</div>
                            <div className="w-full h-3 mt-2 rounded-full bg-rose-900">
                              <div className="h-3 transition-all duration-500 rounded-full bg-gradient-to-r from-rose-400 to-pink-400" style={{ width: `${todaysPct}%` }}></div>
                            </div>
                            <div className="mt-1 text-xs text-rose-300">{todaysPct.toFixed(1)}% of today's goal</div>
                          </div>

                          {/* Total Progress */}
                          <div className="p-3 border border-yellow-600 rounded-lg bg-yellow-900/20">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-semibold text-yellow-300">🏆 Total Progress</span>
                              <span className="text-sm text-yellow-200">Consecutive days</span>
                            </div>
                            <div className="text-2xl font-bold text-yellow-300">{displayDays} / {requiredDays} days</div>
                            <div className="w-full h-3 mt-2 bg-yellow-900 rounded-full">
                              <div className="h-3 transition-all duration-700 ease-out rounded-full bg-gradient-to-r from-yellow-400 via-amber-400 to-orange-400" style={{ width: `${totalPct}%` }}></div>
                            </div>
                            <div className="mt-1 text-xs text-yellow-300">{totalPct.toFixed(1)}% of total challenge goal</div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Heart Rate Cadet - 21 day version */}
                {challenge.id === 'heart-rate-cadet' && (
                  <div className="mb-6 space-y-4">
                    {(() => {
                      const backendSnapshot = challengeProgress['heart-rate-cadet'] || {};
                      const targetHR = Number(userChallenge?.targetValue || challenge.targetValue || 70);
                      const backendDaily = typeof backendSnapshot.dailyProgressPercentage === 'number' ? backendSnapshot.dailyProgressPercentage : (typeof progressData?.dailyProgressPercentage === 'number' ? progressData.dailyProgressPercentage : (typeof progressData?.todaysPercentage === 'number' ? progressData.todaysPercentage : 0));
                      const todaysHR = Number(
                        backendSnapshot.todaysProgress ??
                        progressData?.restingHeartRate ??
                        progressData?.todaysProgress ??
                        healthData?.healthMetrics?.vitalSigns?.heartRate?.value ??
                        healthData?.realTimeMetrics?.heartRate ??
                        healthData?.realTimeMetrics?.restingHeartRate ??
                        healthData?.vitals?.heartRate?.resting ??
                        0
                      );
                      const computedDaily = todaysHR > 0 ? (todaysHR <= targetHR ? 100 : Math.max(0, 100 - ((todaysHR - targetHR) * 5))) : 0;
                      const todaysPct = Math.min(Math.max(backendDaily, computedDaily), 100);
                      const consecutiveDays = Number(backendSnapshot.consecutiveDays || userChallenge?.consecutiveDays || 0);
                      const requiredDays = 21;
                      const todaySuccess = (todaysHR > 0 && todaysHR <= targetHR) || (todaysPct >= 100);
                      // Robust today key + validation date comparison (timezone safe) to avoid accidental double count
                      const todayKey = new Date().toISOString().split('T')[0];
                      const lastValidationRaw = backendSnapshot.lastValidation || backendSnapshot.lastValidated || userChallenge?.lastValidated;
                      let lastValIsToday = false;
                      if (lastValidationRaw) {
                        try {
                          const lastValKey = new Date(lastValidationRaw).toISOString().split('T')[0];
                          lastValIsToday = (lastValKey === todayKey);
                        } catch (e) { /* ignore parse errors */ }
                      }
                      const recordedToday = (backendSnapshot.todaysProgress && backendSnapshot.todaysProgress > 0) ||
                                             (backendSnapshot.dailyProgress && backendSnapshot.dailyProgress[todayKey] !== undefined) ||
                                             (backendSnapshot.dailyProgressPercentage >= 100); // fallback if percentage only
                      const alreadyCounted = lastValIsToday || recordedToday;
                      const displayDays = Math.min(consecutiveDays + (!alreadyCounted && todaySuccess ? 1 : 0), requiredDays);
                      const totalPct = Math.min((displayDays / requiredDays) * 100, 100);
                      return (
                        <>
                          {/* Today's Progress */}
                          <div className="p-3 border rounded-lg border-rose-600 bg-rose-900/20">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-semibold text-rose-300">📅 Today's Progress</span>
                              <span className="text-sm text-rose-200">{new Date().toLocaleDateString()}</span>
                            </div>
                            <div className="text-2xl font-bold text-rose-300">{todaysHR > 0 ? `${todaysHR} bpm` : 'No reading today'}</div>
                            <div className="mt-1 text-sm text-rose-300">Target: ≤ {targetHR} bpm (lower is better)</div>
                            <div className="w-full h-3 mt-2 rounded-full bg-rose-900">
                              <div className="h-3 transition-all duration-500 rounded-full bg-gradient-to-r from-rose-400 to-pink-400" style={{ width: `${todaysPct}%` }}></div>
                            </div>
                            <div className="mt-1 text-xs text-rose-300">{todaysPct.toFixed(1)}% of today's goal</div>
                          </div>

                          {/* Total Progress */}
                          <div className="p-3 border border-yellow-600 rounded-lg bg-yellow-900/20">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-semibold text-yellow-300">🏆 Total Progress</span>
                              <span className="text-sm text-yellow-200">Consecutive days</span>
                            </div>
                            <div className="text-2xl font-bold text-yellow-300">{displayDays} / {requiredDays} days</div>
                            <div className="w-full h-3 mt-2 bg-yellow-900 rounded-full">
                              <div className="h-3 transition-all duration-700 ease-out rounded-full bg-gradient-to-r from-yellow-400 via-amber-400 to-orange-400" style={{ width: `${totalPct}%` }}></div>
                            </div>
                            <div className="mt-1 text-xs text-yellow-300">{totalPct.toFixed(1)}% of total challenge goal</div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
                
                {/* Heart Rate Mover - 60 day version */}
                {challenge.id === 'heart-rate-mover' && (
                  <div className="mb-6 space-y-4">
                    {(() => {
                      const backendSnapshot = challengeProgress['heart-rate-mover'] || {};
                      const targetHR = Number(userChallenge?.targetValue || challenge.targetValue || 70);
                      const backendDaily = typeof backendSnapshot.dailyProgressPercentage === 'number' ? backendSnapshot.dailyProgressPercentage : (typeof progressData?.dailyProgressPercentage === 'number' ? progressData.dailyProgressPercentage : (typeof progressData?.todaysPercentage === 'number' ? progressData.todaysPercentage : 0));
                      const todaysHR = Number(
                        backendSnapshot.todaysProgress ??
                        progressData?.restingHeartRate ??
                        progressData?.todaysProgress ??
                        healthData?.healthMetrics?.vitalSigns?.heartRate?.value ??
                        healthData?.realTimeMetrics?.heartRate ??
                        healthData?.realTimeMetrics?.restingHeartRate ??
                        healthData?.vitals?.heartRate?.resting ??
                        0
                      );
                      const computedDaily = todaysHR > 0 ? (todaysHR <= targetHR ? 100 : Math.max(0, 100 - ((todaysHR - targetHR) * 5))) : 0;
                      const todaysPct = Math.min(Math.max(backendDaily, computedDaily), 100);
                      const consecutiveDays = Number(backendSnapshot.consecutiveDays || userChallenge?.consecutiveDays || 0);
                      const requiredDays = 60;
                      const todaySuccess = (todaysHR > 0 && todaysHR <= targetHR) || (todaysPct >= 100);
                      // Robust today key + validation date comparison (timezone safe) to avoid accidental double count
                      const todayKey = new Date().toISOString().split('T')[0];
                      const lastValidationRaw = backendSnapshot.lastValidation || backendSnapshot.lastValidated || userChallenge?.lastValidated;
                      let lastValIsToday = false;
                      if (lastValidationRaw) {
                        try {
                          const lastValKey = new Date(lastValidationRaw).toISOString().split('T')[0];
                          lastValIsToday = (lastValKey === todayKey);
                        } catch (e) { /* ignore parse errors */ }
                      }
                      const recordedToday = (backendSnapshot.todaysProgress && backendSnapshot.todaysProgress > 0) ||
                                             (backendSnapshot.dailyProgress && backendSnapshot.dailyProgress[todayKey] !== undefined) ||
                                             (backendSnapshot.dailyProgressPercentage >= 100); // fallback if percentage only
                      const alreadyCounted = lastValIsToday || recordedToday;
                      const displayDays = Math.min(consecutiveDays + (!alreadyCounted && todaySuccess ? 1 : 0), requiredDays);
                      const totalPct = Math.min((displayDays / requiredDays) * 100, 100);
                      return (
                        <>
                          {/* Today's Progress */}
                          <div className="p-3 border rounded-lg border-rose-600 bg-rose-900/20">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-semibold text-rose-300">📅 Today's Progress</span>
                              <span className="text-sm text-rose-200">{new Date().toLocaleDateString()}</span>
                            </div>
                            <div className="text-2xl font-bold text-rose-300">{todaysHR > 0 ? `${todaysHR} bpm` : 'No reading today'}</div>
                            <div className="mt-1 text-sm text-rose-300">Target: ≤ {targetHR} bpm (lower is better)</div>
                            <div className="w-full h-3 mt-2 rounded-full bg-rose-900">
                              <div className="h-3 transition-all duration-500 rounded-full bg-gradient-to-r from-rose-400 to-pink-400" style={{ width: `${todaysPct}%` }}></div>
                            </div>
                            <div className="mt-1 text-xs text-rose-300">{todaysPct.toFixed(1)}% of today's goal</div>
                          </div>

                          {/* Total Progress */}
                          <div className="p-3 border border-yellow-600 rounded-lg bg-yellow-900/20">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-semibold text-yellow-300">🏆 Total Progress</span>
                              <span className="text-sm text-yellow-200">Consecutive days</span>
                            </div>
                            <div className="text-2xl font-bold text-yellow-300">{displayDays} / {requiredDays} days</div>
                            <div className="w-full h-3 mt-2 bg-yellow-900 rounded-full">
                              <div className="h-3 transition-all duration-700 ease-out rounded-full bg-gradient-to-r from-yellow-400 via-amber-400 to-orange-400" style={{ width: `${totalPct}%` }}></div>
                            </div>
                            <div className="mt-1 text-xs text-yellow-300">{totalPct.toFixed(1)}% of total challenge goal</div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
                
                {/* Heart Rate Legend - 120 day version */}
                {challenge.id === 'heart-rate-legend' && (
                  <div className="mb-6 space-y-4">
                    {(() => {
                      const backendSnapshot = challengeProgress['heart-rate-legend'] || {};
                      const targetHR = Number(userChallenge?.targetValue || challenge.targetValue || 70);
                      const backendDaily = typeof backendSnapshot.dailyProgressPercentage === 'number' ? backendSnapshot.dailyProgressPercentage : (typeof progressData?.dailyProgressPercentage === 'number' ? progressData.dailyProgressPercentage : (typeof progressData?.todaysPercentage === 'number' ? progressData.todaysPercentage : 0));
                      const todaysHR = Number(
                        backendSnapshot.todaysProgress ??
                        progressData?.restingHeartRate ??
                        progressData?.todaysProgress ??
                        healthData?.healthMetrics?.vitalSigns?.heartRate?.value ??
                        healthData?.realTimeMetrics?.heartRate ??
                        healthData?.realTimeMetrics?.restingHeartRate ??
                        healthData?.vitals?.heartRate?.resting ??
                        0
                      );
                      const computedDaily = todaysHR > 0 ? (todaysHR <= targetHR ? 100 : Math.max(0, 100 - ((todaysHR - targetHR) * 5))) : 0;
                      const todaysPct = Math.min(Math.max(backendDaily, computedDaily), 100);
                      const consecutiveDays = Number(backendSnapshot.consecutiveDays || userChallenge?.consecutiveDays || 0);
                      const requiredDays = 120;
                      const todaySuccess = (todaysHR > 0 && todaysHR <= targetHR) || (todaysPct >= 100);
                      // Robust today key + validation date comparison (timezone safe) to avoid accidental double count
                      const todayKey = new Date().toISOString().split('T')[0];
                      const lastValidationRaw = backendSnapshot.lastValidation || backendSnapshot.lastValidated || userChallenge?.lastValidated;
                      let lastValIsToday = false;
                      if (lastValidationRaw) {
                        try {
                          const lastValKey = new Date(lastValidationRaw).toISOString().split('T')[0];
                          lastValIsToday = (lastValKey === todayKey);
                        } catch (e) { /* ignore parse errors */ }
                      }
                      const recordedToday = (backendSnapshot.todaysProgress && backendSnapshot.todaysProgress > 0) ||
                                             (backendSnapshot.dailyProgress && backendSnapshot.dailyProgress[todayKey] !== undefined) ||
                                             (backendSnapshot.dailyProgressPercentage >= 100); // fallback if percentage only
                      const alreadyCounted = lastValIsToday || recordedToday;
                      const displayDays = Math.min(consecutiveDays + (!alreadyCounted && todaySuccess ? 1 : 0), requiredDays);
                      const totalPct = Math.min((displayDays / requiredDays) * 100, 100);
                      return (
                        <>
                          {/* Today's Progress */}
                          <div className="p-3 border rounded-lg border-rose-600 bg-rose-900/20">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-semibold text-rose-300">📅 Today's Progress</span>
                              <span className="text-sm text-rose-200">{new Date().toLocaleDateString()}</span>
                            </div>
                            <div className="text-2xl font-bold text-rose-300">{todaysHR > 0 ? `${todaysHR} bpm` : 'No reading today'}</div>
                            <div className="mt-1 text-sm text-rose-300">Target: ≤ {targetHR} bpm (lower is better)</div>
                            <div className="w-full h-3 mt-2 rounded-full bg-rose-900">
                              <div className="h-3 transition-all duration-500 rounded-full bg-gradient-to-r from-rose-400 to-pink-400" style={{ width: `${todaysPct}%` }}></div>
                            </div>
                            <div className="mt-1 text-xs text-rose-300">{todaysPct.toFixed(1)}% of today's goal</div>
                          </div>

                          {/* Total Progress */}
                          <div className="p-3 border border-yellow-600 rounded-lg bg-yellow-900/20">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-semibold text-yellow-300">🏆 Total Progress</span>
                              <span className="text-sm text-yellow-200">Consecutive days</span>
                            </div>
                            <div className="text-2xl font-bold text-yellow-300">{displayDays} / {requiredDays} days</div>
                            <div className="w-full h-3 mt-2 bg-yellow-900 rounded-full">
                              <div className="h-3 transition-all duration-700 ease-out rounded-full bg-gradient-to-r from-yellow-400 via-amber-400 to-orange-400" style={{ width: `${totalPct}%` }}></div>
                            </div>
                            <div className="mt-1 text-xs text-yellow-300">{totalPct.toFixed(1)}% of total challenge goal</div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
                
                {/* Sleep Starter / Sleep Rookie / Sleep Champion / Sleep Rhythm Reset / Century Sleep Reset - detailed progress like cards */}
                {(challenge.id === 'sleep-starter' || challenge.id === 'sleep-rookie' || challenge.id === 'sleep-champion' || challenge.id === 'sleep-rhythm-reset' || challenge.id === 'century-sleep-reset') && (
                  <div className="mb-6 space-y-4">
                    {/* Today's Progress */}
                    {(() => {
                      const backendSnapshot = (challengeProgress && challengeProgress[challenge.id]) || {};
                      const dailySleepTarget = Number(backendSnapshot.targetDailyHours || 7);
                      // Take the best available source for "today" sleep to avoid 0 from nullish coalescing
                      const altSleepA = Number(challengeProgress?.['sleep-rookie']?.sleepHours || 0);
                      const altSleepB = Number(challengeProgress?.['sleep-starter']?.sleepHours || 0);
                      const altSleepC = Number(challengeProgress?.['sleep-champion']?.sleepHours || 0);
                      const altSleepD = Number(challengeProgress?.['sleep-rhythm-reset']?.sleepHours || 0);
                      const altSleepE = Number(challengeProgress?.['century-sleep-reset']?.sleepHours || 0);
                      const todaysSleep = Math.max(
                        Number(healthData?.healthMetrics?.lifestyle?.sleep?.value || 0),
                        Number(healthData?.realTimeMetrics?.sleep || 0),
                        Number(progressData?.sleepHours || 0),
                        Number(progressData?.currentProgress || 0),
                        Number(backendSnapshot.sleepHours || 0),
                        altSleepA,
                        altSleepB,
                        altSleepC,
                        altSleepD,
                        altSleepE
                      );
                      // Respect backend daily percentage if provided, but never regress below computed
                      const backendDaily = (typeof backendSnapshot.dailyProgressPercentage === 'number')
                        ? backendSnapshot.dailyProgressPercentage
                        : (typeof progressData?.dailyProgressPercentage === 'number'
                            ? progressData.dailyProgressPercentage
                            : (typeof progressData?.todaysPercentage === 'number' ? progressData.todaysPercentage : 0));
                      const computedDaily = todaysSleep > 0 ? Math.round((todaysSleep / (dailySleepTarget || 1)) * 100) : 0;
                      const dailyPct = Math.min(100, Math.max(backendDaily, computedDaily));
                      return (
                        <>
                          <div className="p-3 border border-purple-600 rounded-lg bg-purple-900/20">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-semibold text-purple-300">📅 Today's Progress</span>
                              <span className="text-sm text-purple-200">{new Date().toLocaleDateString()}</span>
                            </div>
                            <div className="text-2xl font-bold text-purple-300">{todaysSleep.toFixed(1)} hours</div>
                            <div className="mt-1 text-sm text-purple-300">Target: {dailySleepTarget} hours</div>
                            <div className="w-full h-3 mt-2 bg-purple-900 rounded-full">
                              <div
                                className="h-3 transition-all duration-500 rounded-full bg-gradient-to-r from-purple-400 to-indigo-400"
                                style={{ width: `${dailyPct}%` }}
                              ></div>
                            </div>
                            <div className="mt-1 text-xs text-purple-300">{dailyPct.toFixed(1)}% of today's goal</div>
                          </div>
                        </>
                      );
                    })()}
                    {/* Total Progress */}
                    <div className="p-3 border border-yellow-600 rounded-lg bg-yellow-900/20">
                      {(() => {
                        const backendSnapshot = (challengeProgress && challengeProgress[challenge.id]) || {};
                        const requiredNights = Number(
                          backendSnapshot.requiredDays || (challenge.id === 'sleep-champion' ? 60 : (challenge.id === 'sleep-rookie' ? 21 : (challenge.id === 'sleep-rhythm-reset' ? 90 : (challenge.id === 'century-sleep-reset' ? 100 : 7))))
                        );
                        const consecutiveDays = Number(
                          backendSnapshot.consecutiveDays ?? userChallenge?.consecutiveDays ?? progressData?.consecutiveDays ?? 0
                        );
                        const dailySleepTarget = Number(backendSnapshot.targetDailyHours || 7);
                        const backendDaily = typeof backendSnapshot.dailyProgressPercentage === 'number'
                          ? backendSnapshot.dailyProgressPercentage
                          : (typeof progressData?.dailyProgressPercentage === 'number'
                              ? progressData.dailyProgressPercentage
                              : (typeof progressData?.todaysPercentage === 'number' ? progressData.todaysPercentage : 0));
                        const todayKey = new Date().toISOString().split('T')[0];
                        const altSleepA = Number(challengeProgress?.['sleep-rookie']?.sleepHours || 0);
                        const altSleepB = Number(challengeProgress?.['sleep-starter']?.sleepHours || 0);
                        const altSleepC = Number(challengeProgress?.['sleep-champion']?.sleepHours || 0);
                        const altSleepD = Number(challengeProgress?.['sleep-rhythm-reset']?.sleepHours || 0);
                        const altSleepE = Number(challengeProgress?.['century-sleep-reset']?.sleepHours || 0);
                        const todaysSleep = Math.max(
                          Number(healthData?.healthMetrics?.lifestyle?.sleep?.value || 0),
                          Number(healthData?.realTimeMetrics?.sleep || 0),
                          Number(progressData?.sleepHours || 0),
                          Number(progressData?.currentProgress || 0),
                          Number(backendSnapshot.sleepHours || 0),
                          altSleepA,
                          altSleepB,
                          altSleepC,
                          altSleepD,
                          altSleepE
                        );
                        const todayRecorded = (backendSnapshot?.dailyProgress && backendSnapshot.dailyProgress[todayKey]) || (progressData?.dailyProgress && progressData.dailyProgress[todayKey]);
                        const todayRecordedHours = Number(todayRecorded ?? 0);
                        const backendAlreadyValidated = ((backendSnapshot?.message || progressData?.message || '')).toLowerCase().includes('already validated');
                        const backendCountsToday = (backendDaily >= 100) || (todayRecordedHours >= dailySleepTarget);
                        const alreadyCountedSuccess = backendAlreadyValidated || backendCountsToday;
                        const todaySuccess = (todaysSleep >= dailySleepTarget) || (backendDaily >= 100);
                        const displayDays = Math.min(consecutiveDays + (todaySuccess && !alreadyCountedSuccess ? 1 : 0), requiredNights);
                        const totalPct = Math.min(((displayDays / requiredNights) * 100), 100);
                        return (
                          <>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-semibold text-yellow-300">🏆 Total Progress</span>
                              <span className="text-sm text-yellow-200">Consecutive nights</span>
                            </div>
                            <div className="text-2xl font-bold text-yellow-300">{displayDays} / {requiredNights} nights</div>
                            <div className="w-full h-3 mt-2 bg-yellow-900 rounded-full">
                              <div
                                className="h-3 transition-all duration-700 ease-out rounded-full bg-gradient-to-r from-yellow-400 via-amber-400 to-orange-400"
                                style={{ width: `${totalPct}%` }}
                              ></div>
                            </div>
                            <div className="mt-1 text-xs text-yellow-300">{totalPct.toFixed(1)}% of total challenge goal</div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {/* Other non-step challenges - show basic progress (exclude hydration, sleep, heart rate handled above) */}
                {!isStepChallenge(challenge.id) && !(challenge.id === 'sleep-starter' || challenge.id === 'sleep-rookie' || challenge.id === 'sleep-champion' || challenge.id === 'sleep-rhythm-reset' || challenge.id === 'century-sleep-reset' || challenge.id === 'hydration-hero' || challenge.id === 'hydration-initiate' || challenge.id === 'hydration-strider' || challenge.id === 'hydration-master' || challenge.id === 'heart-rate-calm' || challenge.id === 'heart-rate-cadet' || challenge.id === 'heart-rate-mover' || challenge.id === 'heart-rate-legend') && (
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-400">{progressPercentage.toFixed(1)}%</div>
                      <div className="text-sm text-gray-400">Overall Progress</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-400">{daysElapsed}/{totalDays}</div>
                      <div className="text-sm text-gray-400">Days Completed</div>
                    </div>
                  </div>
                )}
                
                {/* Challenge Status and Timing */}
                <div className="p-3 border border-gray-600 rounded-lg bg-gray-800/30">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <div className="text-lg font-bold text-blue-400">{daysElapsed}</div>
                      <div className="text-xs text-gray-400">Days Active</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-orange-400">{daysRemaining}</div>
                      <div className="text-xs text-gray-400">Days Remaining</div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-3 text-sm text-center text-gray-400">
                  {daysRemaining > 0 ? `${daysRemaining} days remaining to complete this challenge` : 'Challenge completed! 🎉'}
                </div>
              </div>
            )}
            
            {/* Challenge Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 border border-gray-700 rounded-lg bg-gray-800/30">
                <div className="flex items-center gap-2 mb-2">
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  <span className="font-semibold text-white">Rewards</span>
                </div>
                <div className="text-2xl font-bold text-yellow-400">{challenge.points?.toLocaleString()}</div>
                <div className="text-sm text-gray-400">Medi Points</div>
                {challenge.bonusPoints && (
                  <div className="mt-2 text-sm text-green-400">+{challenge.bonusPoints} bonus points</div>
                )}
              </div>
              
              <div className="p-4 border border-gray-700 rounded-lg bg-gray-800/30">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-5 h-5 text-blue-500" />
                  <span className="font-semibold text-white">Community</span>
                </div>
                <div className="text-2xl font-bold text-blue-400">{challenge.participants?.toLocaleString()}</div>
                <div className="text-sm text-gray-400">Active participants</div>
              </div>
            </div>
            
            {/* Device Tracking */}
            {challenge.deviceTracked && challenge.deviceTracked.length > 0 && (
              <div>
                <h3 className="mb-3 text-lg font-semibold text-white">Device Tracking</h3>
                <div className="flex flex-wrap gap-2">
                  {challenge.deviceTracked.map((device, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 text-sm font-medium text-blue-300 border rounded-full bg-blue-900/30 border-blue-700/50"
                    >
                      {device}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Health Benefits */}
            {challenge.healthBenefit && (
              <div>
                <h3 className="mb-3 text-lg font-semibold text-white">Health Benefits</h3>
                <div className="p-4 border rounded-lg border-green-700/30 bg-green-900/20">
                  <p className="leading-relaxed text-green-300">{challenge.healthBenefit}</p>
                </div>
              </div>
            )}
            
            {/* Challenge Requirements */}
            <div>
              <h3 className="mb-3 text-lg font-semibold text-white">Requirements</h3>
              <div className="space-y-2 text-sm text-gray-300">
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-gray-400" />
                  <span>Target: {challenge.targetValue?.toLocaleString()} {challenge.unit}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span>Duration: {challenge.duration}</span>
                </div>
                {challenge.unit === 'steps' && (
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-gray-400" />
                    <span>Daily Target: {dailyTarget.toLocaleString()} steps/day</span>
                  </div>
                )}
                {challenge.verificationRequired && (
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span>Device verification required</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // �🏆 CENTURY CARD COMPONENT - Beautiful Premium Design Matching Screenshot!
  const CenturyCard = ({ challenge, userStatus, onJoin }) => {
    const hasGoldAccess = subscriptionLimits.name === 'Gold' || subscriptionLimits.name === 'Platinum';
    
    // Get device tracking list
    const deviceList = challenge.deviceTracked || [];
    const healthBenefit = challenge.healthBenefit || "Transforms your health through century-level commitment";
    
    return (
      <div className="relative overflow-hidden transition-all duration-300 border border-gray-700 shadow-xl bg-gradient-to-br from-gray-900 via-black to-gray-800 rounded-xl hover:shadow-2xl hover:scale-105">
        {/* CENTURY Header */}
        <div className="relative px-4 py-3 border-b border-gray-700 bg-gradient-to-r from-gray-800 to-gray-900">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-lg">{challenge.icon}</span>
              <span className="px-2 py-1 text-xs font-bold tracking-wider text-gray-300 uppercase bg-gray-700 rounded-full">
                CENTURY
              </span>
            </div>
            <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-gradient-to-r from-yellow-500 to-yellow-600">
              <span className="text-xs">🔒</span>
              <span className="text-xs font-bold tracking-wide text-black uppercase">Verified Only</span>
            </div>
          </div>
        </div>

        {/* Challenge Content */}
        <div className="p-4">
          {/* Challenge Title */}
          <h3 className="mb-2 text-lg font-bold leading-tight text-white">{challenge.name}</h3>
          
          {/* Challenge Description */}
          <p className="mb-3 text-xs leading-relaxed text-gray-300">
            {challenge.description}
          </p>

          {/* Device Verified Tracking Section */}
          <div className="p-2 mb-3 bg-gray-800 border border-gray-700 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs">📱</span>
              <span className="text-xs font-semibold tracking-wide text-blue-400 uppercase">Device Verified Tracking:</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {deviceList.slice(0, 3).map((device, index) => (
                <span key={index} className="px-2 py-1 text-xs font-medium text-blue-300 rounded bg-blue-900/30">
                  {device}
                </span>
              ))}
            </div>
          </div>

          {/* Health Impact Section */}
          <div className="p-2 mb-3 border rounded-lg bg-green-900/20 border-green-700/30">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs">💚</span>
              <span className="text-xs font-semibold tracking-wide text-green-400 uppercase">Health Impact:</span>
            </div>
            <p className="text-xs leading-relaxed text-green-300 line-clamp-2">
              {healthBenefit}
            </p>
          </div>

          {/* Century Conquest Series Branding */}
          <div className="p-3 mb-3 border-2 border-yellow-500 rounded-lg bg-gradient-to-r from-yellow-600 to-orange-600">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-sm">👑</span>
                <span className="text-xs font-bold tracking-wide text-black uppercase">Century Conquest Series</span>
              </div>
              <span className="px-2 py-1 text-xs font-bold tracking-wide text-yellow-900 uppercase bg-yellow-300 rounded-full">
                BRANDED
              </span>
            </div>
            
            {/* Unlock & Bonus Section */}
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs">🏆</span>
              <span className="text-xs font-medium text-black">
                {challenge.specialBadge || challenge.specialAchievement || 'Unlock: Beast Mode Badge'}
              </span>
            </div>
            
            {/* Gold+ Requirement */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1 px-2 py-1 bg-yellow-400 rounded-full">
                <span className="text-xs">💎</span>
                <span className="text-xs font-bold text-black">Gold+ Only</span>
              </div>
              <span className="text-xs font-bold text-black">100-Day Epic Challenge</span>
            </div>
            
            {/* Bonus Points */}
            {challenge.bonusPoints && (
              <div className="flex items-center gap-1 mt-2">
                <span className="text-xs">💰</span>
                <span className="text-xs font-bold text-black">+{challenge.bonusPoints} Bonus Points Available</span>
              </div>
            )}
          </div>

          {/* Stats Section */}
          <div className="flex items-center justify-between mb-3 text-xs text-gray-400">
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>{challenge.duration}</span>
            </div>
            <div className="flex items-center gap-1">
              <Users className="w-3 h-3" />
              <span>{challenge.participants}</span>
            </div>
          </div>

          {/* Points Display */}
          <div className="flex items-center justify-center mb-3">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-yellow-600 to-yellow-500">
              <Trophy className="w-4 h-4 text-yellow-900" />
              <span className="text-sm font-bold text-yellow-900">
                {challenge.points.toLocaleString()} Medi Points
              </span>
            </div>
          </div>

          {/* Action Button */}
          {!hasGoldAccess ? (
            <button
              disabled
              className="w-full py-2 text-xs font-bold tracking-wide text-yellow-600 uppercase bg-blue-100 border-2 border-blue-300 rounded-lg cursor-not-allowed"
            >
              👑 Upgrade to Gold/Platinum to Unlock
            </button>
          ) : (
            (challenge.isActive === false || challenge.futurePhase) ? (
              <button
                disabled
                className="w-full py-2 text-sm font-medium text-gray-600 bg-gray-200 border border-gray-300 rounded-lg cursor-not-allowed"
                title={challenge.comingSoonMessage || 'This challenge will be available in upcoming platform phases.'}
              >
                Coming Soon
              </button>
            ) : (
              <button
                onClick={onJoin}
                className="relative w-full py-2 overflow-hidden text-xs font-bold tracking-wide text-white uppercase transition-all border border-gray-600 rounded-lg shadow-lg bg-gradient-to-r from-gray-900 to-black hover:from-gray-800 hover:to-gray-900"
              >
                {(() => {
                  const challengeKey = `${challenge.id}-daily`;
                  const isJoining = joiningStates[challengeKey];
                  const hasJoined = isUserChallengeActive(challenge.id);
                  const isPaused = isUserChallengePaused(challenge.id);
                  
                  if (isJoining === 'success') {
                    return (
                      <>
                        <div className="absolute inset-0 bg-green-400/30"></div>
                        ✅ Joined!
                      </>
                    );
                  } else if (isJoining === 'error') {
                    return (
                      <>
                        <div className="absolute inset-0 bg-red-400/30"></div>
                        ❌ Error
                      </>
                    );
                  } else if (isJoining) {
                    return (
                      <>
                        <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                        ⏳ Joining...
                      </>
                    );
                  } else if (isPaused) {
                    return (
                      <>
                        <div className="absolute inset-0 bg-orange-400/30"></div>
                        ▶️ Resume
                      </>
                    );
                  } else if (hasJoined) {
                  // Get challenge status directly from userActiveChallenges array
                  const userChallenge = userActiveChallenges.find(uc => uc.challengeId === challenge.id);
                  const progressData = getUserChallengeProgress(challenge.id);
                  
                  if (userChallenge) {
                    // 🔧 FIXED: Accurate day calculation for Century challenges
                    const getAccurateDaysElapsed = () => {
                      if (!userChallenge?.startDate) return 0;
                      
                      const startDate = new Date(userChallenge.startDate);
                      const today = new Date();
                      
                      // Calculate using calendar days, not 24-hour intervals
                      const startDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
                      const currentDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                      
                      const timeDifference = currentDay.getTime() - startDay.getTime();
                      const daysDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
                      
                      // 🎯 CORRECTED: Start day = Day 1, next day = Day 2, etc.
                      return daysDifference + 1;
                    };
                    
                    const daysElapsed = getAccurateDaysElapsed();
                    const totalDays = challenge.total || 100;
                    // 🔧 FIXED: Remaining days - if on Day 2 of 100, then 98 days remaining
                    const daysRemaining = Math.max(0, totalDays - daysElapsed);
                    
                    // Show enhanced interface for any joined challenge
                    return (
                      <div className="w-full space-y-3">
                        {/* Active Status Header - ISSUE #3 FIX: Show both elapsed and remaining */}
                        <div className="flex items-center justify-between p-2 border rounded-lg bg-green-900/30 border-green-400/30">
                          <span className="text-xs font-bold tracking-wide text-green-300 uppercase">✅ ACTIVE CENTURY</span>
                          <span className="text-xs text-green-200">Day {daysElapsed}/{totalDays} • {daysRemaining} left</span>
                        </div>
                        
                        {/* Today's Progress Display (sleep streaks vs steps vs others) */}
                        {(() => {
                          const isSleepStreak = (
                            challenge.id === 'sleep-starter' ||
                            challenge.id === 'sleep-rookie' ||
                            challenge.id === 'sleep-champion' ||
                            challenge.id === 'sleep-rhythm-reset' ||
                            challenge.id === 'century-sleep-reset'
                          );
                          if (isSleepStreak) {
                            const backendSnapshot = (challengeProgress && challengeProgress[challenge.id]) || {};
                            const dailySleepTarget = Number(backendSnapshot.targetDailyHours || 7);
                            const altSleepA = Number(challengeProgress?.['sleep-rookie']?.sleepHours || 0);
                            const altSleepB = Number(challengeProgress?.['sleep-starter']?.sleepHours || 0);
                            const altSleepC = Number(challengeProgress?.['sleep-champion']?.sleepHours || 0);
                            const altSleepD = Number(challengeProgress?.['sleep-rhythm-reset']?.sleepHours || 0);
                            const altSleepE = Number(challengeProgress?.['century-sleep-reset']?.sleepHours || 0);
                            const todaysSleep = Math.max(
                              Number(healthData?.healthMetrics?.lifestyle?.sleep?.value || 0),
                              Number(healthData?.realTimeMetrics?.sleep || 0),
                              Number(progressData?.sleepHours || 0),
                              Number(progressData?.currentProgress || 0),
                              Number(backendSnapshot.sleepHours || 0),
                              altSleepA, altSleepB, altSleepC, altSleepD, altSleepE
                            );
                            const backendDaily = (typeof backendSnapshot.dailyProgressPercentage === 'number')
                              ? backendSnapshot.dailyProgressPercentage
                              : (typeof progressData?.dailyProgressPercentage === 'number'
                                  ? progressData.dailyProgressPercentage
                                  : (typeof progressData?.todaysPercentage === 'number' ? progressData.todaysPercentage : 0));
                            const computedDaily = todaysSleep > 0 ? Math.round((todaysSleep / (dailySleepTarget || 1)) * 100) : 0;
                            const todaysPct = Math.min(100, Math.max(backendDaily, computedDaily));
                            return (
                              <div className="space-y-2">
                                <div className="flex justify-between text-xs">
                                  <span className="text-gray-300">Today's Progress</span>
                                  <span className="font-medium text-blue-300">{todaysPct.toFixed(1)}%</span>
                                </div>
                                <div className="w-full h-2 bg-gray-700 rounded-full">
                                  <div
                                    className="h-2 transition-all duration-500 rounded-full bg-gradient-to-r from-blue-400 to-cyan-400"
                                    style={{ width: `${todaysPct}%` }}
                                  ></div>
                                </div>
                                <div className="text-xs text-center text-gray-300">
                                  Today: {todaysSleep.toFixed(1)} / {dailySleepTarget} hours
                                </div>
                              </div>
                            );
                          }
                          // Steps or other types (fallback to existing behavior)
                          return (
                            <div className="space-y-2">
                              <div className="flex justify-between text-xs">
                                <span className="text-gray-300">Today's Progress</span>
                                <span className="font-medium text-blue-300">{`${challenge.unit === 'steps' 
                                  ? (((progressData.todaysSteps || 0) / ((challenge.targetValue || 1) / (challenge.total || 100))) * 100).toFixed(1)
                                  : 0
                                }%`}</span>
                              </div>
                              <div className="w-full h-2 bg-gray-700 rounded-full">
                                <div 
                                  className="h-2 transition-all duration-500 rounded-full bg-gradient-to-r from-blue-400 to-cyan-400"
                                  style={{ width: `${Math.min((challenge.unit === 'steps' 
                                    ? (((progressData.todaysSteps || 0) / ((challenge.targetValue || 1) / (challenge.total || 100))) * 100)
                                    : 0
                                  ), 100)}%` }}
                                ></div>
                              </div>
                              <div className="text-xs text-center text-gray-300">
                                {challenge.unit === 'steps' 
                                  ? `Today: ${(progressData.todaysSteps || 0).toLocaleString()} / ${Math.round((challenge.targetValue || 0) / (challenge.total || 100)).toLocaleString()} steps`
                                  : `Today's progress tracking`
                                }
                              </div>
                            </div>
                          );
                        })()}
                        
                        {/* Total Progress Display (sleep streaks vs steps vs others) */}
                        {(() => {
                          const isSleepStreak = (
                            challenge.id === 'sleep-starter' ||
                            challenge.id === 'sleep-rookie' ||
                            challenge.id === 'sleep-champion' ||
                            challenge.id === 'sleep-rhythm-reset' ||
                            challenge.id === 'century-sleep-reset'
                          );
                          if (isSleepStreak) {
                            const backendSnapshot = (challengeProgress && challengeProgress[challenge.id]) || {};
                            const requiredNights = Number(
                              backendSnapshot.requiredDays || (challenge.id === 'sleep-champion' ? 60 : (challenge.id === 'sleep-rookie' ? 21 : (challenge.id === 'sleep-rhythm-reset' ? 90 : (challenge.id === 'century-sleep-reset' ? 100 : 7))))
                            );
                            const consecutiveDays = Number(
                              backendSnapshot.consecutiveDays ?? userChallenge?.consecutiveDays ?? progressData?.consecutiveDays ?? 0
                            );
                            const dailySleepTarget = Number(backendSnapshot.targetDailyHours || 7);
                            const backendDaily = (typeof backendSnapshot.dailyProgressPercentage === 'number')
                              ? backendSnapshot.dailyProgressPercentage
                              : (typeof progressData?.dailyProgressPercentage === 'number'
                                  ? progressData.dailyProgressPercentage
                                  : (typeof progressData?.todaysPercentage === 'number' ? progressData.todaysPercentage : 0));
                            const todayKey = new Date().toISOString().split('T')[0];
                            const altSleepA = Number(challengeProgress?.['sleep-rookie']?.sleepHours || 0);
                            const altSleepB = Number(challengeProgress?.['sleep-starter']?.sleepHours || 0);
                            const altSleepC = Number(challengeProgress?.['sleep-champion']?.sleepHours || 0);
                            const altSleepD = Number(challengeProgress?.['sleep-rhythm-reset']?.sleepHours || 0);
                            const altSleepE = Number(challengeProgress?.['century-sleep-reset']?.sleepHours || 0);
                            const todaysSleep = Math.max(
                              Number(healthData?.healthMetrics?.lifestyle?.sleep?.value || 0),
                              Number(healthData?.realTimeMetrics?.sleep || 0),
                              Number(progressData?.sleepHours || 0),
                              Number(progressData?.currentProgress || 0),
                              Number(backendSnapshot.sleepHours || 0),
                              altSleepA, altSleepB, altSleepC, altSleepD, altSleepE
                            );
                            const todayRecorded = (backendSnapshot?.dailyProgress && backendSnapshot.dailyProgress[todayKey]) || (progressData?.dailyProgress && progressData.dailyProgress[todayKey]);
                            const todayRecordedHours = Number(todayRecorded ?? 0);
                            const backendAlreadyValidated = ((backendSnapshot?.message || progressData?.message || '')).toLowerCase().includes('already validated');
                            const backendCountsToday = (backendDaily >= 100) || (todayRecordedHours >= dailySleepTarget);
                            const alreadyCountedSuccess = backendAlreadyValidated || backendCountsToday;
                            const todaySuccess = (todaysSleep >= dailySleepTarget) || (backendDaily >= 100);
                            const displayDays = Math.min(consecutiveDays + (todaySuccess && !alreadyCountedSuccess ? 1 : 0), requiredNights);
                            const totalPct = Math.min(((displayDays / requiredNights) * 100), 100);
                            return (
                              <div className="space-y-2">
                                <div className="flex justify-between text-xs">
                                  <span className="text-gray-300">Total Progress</span>
                                  <span className="font-medium text-green-300">{totalPct.toFixed(1)}%</span>
                                </div>
                                <div className="w-full h-2 bg-gray-700 rounded-full">
                                  <div
                                    className="h-2 transition-all duration-500 rounded-full bg-gradient-to-r from-green-400 to-emerald-400"
                                    style={{ width: `${totalPct}%` }}
                                  ></div>
                                </div>
                                <div className="text-xs text-center text-gray-300">
                                  {displayDays} / {requiredNights} consecutive nights
                                </div>
                              </div>
                            );
                          }
                          // Steps or other types
                          return (
                            <div className="space-y-2">
                              <div className="flex justify-between text-xs">
                                <span className="text-gray-300">Total Progress</span>
                                <span className="font-medium text-green-300">{`${(challenge.unit === 'steps' 
                                  ? ((progressData.cumulativeSteps || progressData.currentProgress || 0) / (challenge.targetValue || 1) * 100)
                                  : ((userChallenge.currentProgress || 0) / (challenge.total || 100) * 100)
                                ).toFixed(1)}%`}</span>
                              </div>
                              <div className="w-full h-2 bg-gray-700 rounded-full">
                                <div 
                                  className="h-2 transition-all duration-500 rounded-full bg-gradient-to-r from-green-400 to-emerald-400"
                                  style={{ width: `${Math.min((challenge.unit === 'steps' 
                                    ? ((progressData.cumulativeSteps || progressData.currentProgress || 0) / (challenge.targetValue || 1) * 100)
                                    : ((userChallenge.currentProgress || 0) / (challenge.total || 100) * 100)
                                  ), 100)}%` }}
                                ></div>
                              </div>
                              <div className="text-xs text-center text-gray-300">
                                {challenge.unit === 'steps' 
                                  ? `Total: ${(progressData.cumulativeSteps || progressData.currentProgress || 0).toLocaleString()} / ${(challenge.targetValue || challenge.total || 100).toLocaleString()} ${challenge.unit}`
                                  : `${userChallenge.currentProgress || 0} / ${challenge.total || 100} days completed`
                                }
                              </div>
                            </div>
                          );
                        })()}
                        
                        {/* Achievement Status */}
                        {(() => {
                          if (challenge.unit === 'steps') {
                            // Calculate daily step target for step-based challenges
                            const totalDays = challenge.total || 100;
                            const dailyStepTarget = (challenge.targetValue || 0) / totalDays;
                            const currentSteps = progressData?.todaysSteps || 0; // FIXED: Use today's steps from enhanced progress
                            const dailyGoalAchieved = currentSteps >= dailyStepTarget;
                            
                            return dailyGoalAchieved && (
                              <div className="p-2 text-xs text-center text-yellow-300 border rounded animate-pulse bg-yellow-900/20 border-yellow-400/30">
                                🎉 Today's goal achieved! ({dailyStepTarget.toLocaleString()} steps)
                              </div>
                            );
                          } else {
                            // For non-step challenges, use original completion logic
                            return progressData?.completed && (
                              <div className="p-2 text-xs text-center text-yellow-300 border rounded animate-pulse bg-yellow-900/20 border-yellow-400/30">
                                🎉 Today's goal achieved!
                              </div>
                            );
                          }
                        })()}
                        
                        {/* Action Buttons */}
                        <div className="grid grid-cols-3 gap-1 text-xs">
                          <button 
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedChallengeDetails({ challenge, userChallenge });
                              setShowDetailsPopup(true);
                            }}
                            className="px-2 py-1 text-blue-300 transition-colors border rounded bg-blue-900/30 hover:bg-blue-900/50 border-blue-400/30"
                          >
                            📊 Details
                          </button>
                          <button 
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handlePauseChallenge(challenge.id);
                            }}
                            className="px-2 py-1 text-orange-300 transition-colors border rounded bg-orange-900/30 hover:bg-orange-900/50 border-orange-400/30"
                          >
                            ⏸️ Pause
                          </button>
                          <button 
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (window.confirm('Are you sure you want to stop this Century challenge? This is a premium 100-day commitment.')) {
                                handleStopChallenge(challenge.id);
                              }
                            }}
                            disabled={stoppingStates[challenge.id]}
                            className={`px-2 py-1 text-red-300 transition-colors border rounded ${
                              stoppingStates[challenge.id] 
                                ? 'bg-gray-500/30 border-gray-400/30 cursor-not-allowed' 
                                : 'bg-red-900/30 hover:bg-red-900/50 border-red-400/30'
                            }`}
                          >
                            {stoppingStates[challenge.id] ? '⏳ Stopping...' : '🛑 Stop'}
                          </button>
                        </div>
                      </div>
                    );
                  } else {
                    // Fallback for other statuses
                    return (
                      <>
                        <div className="absolute inset-0 bg-green-400/30"></div>
                        ✅ Active
                      </>
                    );
                  }
                } else {
                  return "🚀 Start Earning";
                }
              })()}
            </button>
          ))}
        </div>
      </div>
    );
  };

  // ChallengeCard component - Enhanced with device tracking and points rewards + Subscription Limits + Backend Integration
  const ChallengeCard = ({ challenge, userStatus, difficultyColor, onJoin }) => {
    // Get real-time health context for this challenge
  const getHealthContext = (challenge) => {
      if (!healthData) return null;
      
      const target = challenge.target;
      if (!target) return null;
      
      switch (target.metric) {
        case 'steps':
          // 🚨 FIX: Use today's steps from progress data, not raw health data
          const progressData = getUserChallengeProgress(challenge.id);
          const todaysSteps = progressData?.todaysSteps || 0;
          return {
            current: todaysSteps.toLocaleString(),
            target: target.value.toLocaleString(),
            unit: 'steps',
            percentage: Math.min(100, (todaysSteps / target.value) * 100)
          };
        case 'sleep':
          const sleep = healthData?.lifestyle?.sleep?.value || 0;
          return {
            current: sleep.toFixed(1),
            target: target.value,
            unit: 'hours',
            percentage: Math.min(100, (sleep / target.value) * 100)
          };
        case 'water':
          const water = healthData?.lifestyle?.waterIntake?.value || 0;
          const glasses = Math.round(water / 240);
          return {
            current: glasses,
            target: target.value,
            unit: 'glasses',
            percentage: Math.min(100, (glasses / target.value) * 100)
          };
        default:
      return null;
    }
  };

    // Get real-time validation status
    const progressData = getUserChallengeProgress(challenge.id);
    const healthContext = getHealthContext(challenge);

    // Backend challenge status display
    const getBackendChallengeStatus = () => {
      
      if (userStatus?.status === 'active' && progressData?.completed) {
        return {
          text: 'Target Met Today!',
          color: 'text-green-600',
          icon: CheckCircle,
          animate: false
        };
      }
      
      return null;
    };

    const backendStatus = getBackendChallengeStatus();
    // Use the gradient from the challenge data directly
    const gradientClass = challenge.gradient || 'from-gray-400 to-gray-600';

    // 🚨 COMPREHENSIVE CHALLENGE CONFLICT DETECTION SYSTEM - Prevent ALL Gaming Abuse!
    // 
    // PROBLEM SOLVED: Users could game the system by joining multiple challenges of the same type
    // and automatically complete all of them with the same activity.
    //
    // COMPREHENSIVE SOLUTION: Detect ALL overlapping challenge categories and prevent simultaneous joining
    // 
    // CONFLICT CATEGORIES PROTECTED:
    // - Steps: century-step-master, million-step-challenge, step-mastery, step-streak, step-starter, gentle-morning-walker, light-activity-streak
  // - Hydration: hydration-hero, hydration-initiate, hydration-strider, hydration-master
  // - Sleep: sleep-starter, sleep-rookie, sleep-reset, sleep-rhythm-reset
    // - Nutrition: meal-tracker-rookie, macro-mastery, nutrition-snapshot, low-sugar-champion
    // - Mindfulness: breathe-easy, mindful-minutes, mindfulness-month, meditation-master
    // - Cardiovascular: heart-rate-calm, cardio-commitment, bp-logger
    // - Fitness: workout-warrior, fitness-focus, fitness-streak-beast
    // - Strength: stretch-session, workout-warrior
    // - Logging: medication-logger, mood-tracker, healthy-start
    //
    // RESULT: Complete fair play protection - users must finish current challenges before starting conflicting ones
    const detectChallengeConflicts = (newChallenge) => {
      // Define comprehensive challenge conflict categories
      const conflictCategories = {
        // Step-based challenges (same walking/movement activity)
        steps: {
          challenges: ['century-step-master', 'million-step-challenge', 'step-commander', 'step-streak', 'step-scout', 'gentle-morning-walker', 'light-activity-streak'],
          message: '🚨 STEP CHALLENGE CONFLICT!\n\nYou cannot join multiple step-based challenges simultaneously as they all track the same walking activity.\n\nComplete your current step challenges before joining this one.'
        },
        
        // Hydration tracking challenges (same water intake logging)
        hydration: {
          challenges: ['hydration-hero', 'hydration-initiate', 'hydration-strider', 'hydration-master'],
          message: '💧 HYDRATION CONFLICT!\n\nYou cannot track multiple hydration challenges simultaneously as they measure the same water intake.\n\nComplete your current hydration challenge before starting another.'
        },
        
        // Sleep tracking challenges (same sleep duration/quality tracking)
        sleep: {
          challenges: ['sleep-starter', 'sleep-rookie', 'sleep-champion', 'sleep-rhythm-reset', 'century-sleep-reset'],
          message: '😴 SLEEP CHALLENGE CONFLICT!\n\nYou cannot join multiple sleep challenges simultaneously as they all track the same sleep patterns.\n\nComplete your current sleep challenge before joining another.'
        },
        
        // Nutrition tracking challenges (same meal/nutrition logging)
        nutrition_tracking: {
          challenges: ['meal-tracker-rookie', 'macro-mastery', 'nutrition-snapshot', 'low-sugar-champion'],
          message: '🍎 NUTRITION TRACKING CONFLICT!\n\nYou cannot join multiple nutrition tracking challenges simultaneously as they measure the same dietary logging.\n\nComplete your current nutrition challenge before starting another.'
        },
        
        // Mindfulness/meditation challenges (same meditation/mindfulness activity)
        mindfulness: {
          challenges: ['breathe-easy', 'mindful-minutes', 'mindfulness-month', 'meditation-master'],
          message: '🧘 MINDFULNESS CONFLICT!\n\nYou cannot join multiple mindfulness challenges simultaneously as they track the same meditation/breathing activities.\n\nComplete your current mindfulness challenge before joining another.'
        },
        
        // Cardiovascular monitoring challenges (same heart/BP tracking)
        cardiovascular: {
          challenges: ['heart-rate-calm', 'cardio-commitment', 'bp-logger'],
          message: '❤️ CARDIOVASCULAR CONFLICT!\n\nYou cannot join multiple cardiovascular challenges simultaneously as they track overlapping heart health metrics.\n\nComplete your current cardiovascular challenge before starting another.'
        },
        
        // Fitness/workout challenges (same exercise/workout activity)
        fitness_workout: {
          challenges: ['workout-warrior', 'fitness-focus', 'fitness-streak-beast'],
          message: '💪 FITNESS WORKOUT CONFLICT!\n\nYou cannot join multiple fitness challenges simultaneously as they track the same workout activities.\n\nComplete your current fitness challenge before joining another.'
        },
        
        // Strength training challenges (same strength exercise activity)
        strength: {
          challenges: ['stretch-session', 'workout-warrior'],
          message: '🏋️ STRENGTH TRAINING CONFLICT!\n\nYou cannot join multiple strength challenges simultaneously as they track overlapping resistance training.\n\nComplete your current strength challenge before starting another.'
        },
        
        // Health logging consistency challenges (same daily logging activity)
        logging_consistency: {
          challenges: ['medication-logger', 'mood-tracker', 'healthy-start'],
          message: '📝 LOGGING CONSISTENCY CONFLICT!\n\nYou cannot join multiple daily logging challenges simultaneously as they require the same consistent tracking behavior.\n\nComplete your current logging challenge before joining another.'
        }
      };

      // Create consolidated challenge list
      const allChallenges = [
        ...productionChallenges.beginner,
        ...productionChallenges.easy,
        ...productionChallenges.medium,
        ...productionChallenges.hard,
        ...productionChallenges.extreme,
        ...productionChallenges.century
      ];

      // Check each conflict category
      for (const [categoryName, categoryData] of Object.entries(conflictCategories)) {
        if (categoryData.challenges.includes(newChallenge.id)) {
          // Check for active challenges in the same category
          const activeConflictingChallenges = allChallenges
            .filter(activeChallenge => 
              activeChallenge.id !== newChallenge.id && // Not the same challenge
              categoryData.challenges.includes(activeChallenge.id) && // In same conflict category
              getUserChallengeStatus(activeChallenge.id).status === 'active' // Currently active
            );

          if (activeConflictingChallenges.length > 0) {
            return {
              hasConflict: true,
              conflictType: `${categoryName}_overlap`,
              conflictingChallenges: activeConflictingChallenges,
              message: `${categoryData.message}\n\nActive conflicting challenges: ${activeConflictingChallenges.map(c => c.name).join(', ')}`
            };
          }
        }
      }

      return { hasConflict: false };
    };

    // Check for conflicts when user tries to join
    const challengeConflict = detectChallengeConflicts(challenge);
    
    const renderChallengeStatus = () => {
      // ✋ PRODUCTION LAUNCH: Check if challenge is disabled (future phase)
      if (challenge.isActive === false || challenge.futurePhase) {
        // Preview or regular future-phase: keep it minimal for consistent card height
        return (
          <button
            disabled
            className="w-full py-2 mt-2 text-sm font-medium text-gray-600 bg-gray-200 border border-gray-300 rounded-lg cursor-not-allowed"
            title={challenge.comingSoonMessage || 'This challenge will be available in upcoming platform phases.'}
          >
            Coming Soon
          </button>
        );
      }

      // 🚨 PRIORITY 1: Challenge Conflict Detection - Prevent Gaming Abuse!
      if (challengeConflict.hasConflict && !userStatus?.active) {
        return (
          <div className="mt-3">
            <div className="p-4 border-2 border-red-500 rounded-lg bg-gradient-to-r from-red-900 to-red-700">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xl">🚨</span>
                <p className="text-sm font-bold text-red-100">CHALLENGE CONFLICT DETECTED!</p>
              </div>
              <div className="p-3 mb-3 bg-red-800 border border-red-600 rounded">
                <p className="text-xs font-semibold text-red-100">
                  ⚠️ ANTI-GAMING PROTECTION ACTIVE
                </p>
                <p className="mt-1 text-xs text-red-200">
                  You cannot join multiple {challengeConflict.conflictType === 'step_overlap' ? 'step-based' : 'activity-based'} challenges simultaneously.
                </p>
              </div>
              {challengeConflict.conflictingChallenges.length > 0 && (
                <div className="mb-3">
                  <p className="mb-2 text-xs font-semibold text-red-200">Active conflicting challenges:</p>
                  {challengeConflict.conflictingChallenges.map((conflictChallenge, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 mb-1 bg-red-800 rounded">
                      <span className="text-sm">{conflictChallenge.icon || '🏃‍♂️'}</span>
                      <span className="text-xs font-medium text-red-100">{conflictChallenge.name}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="p-2 bg-yellow-100 border border-yellow-400 rounded">
                <p className="text-xs font-semibold text-yellow-800">
                  💡 Solution: Complete or leave your current {challengeConflict.conflictType === 'step_overlap' ? 'step' : 'activity'} challenges first!
                </p>
              </div>
            </div>
            <button
              disabled
              className="w-full py-2 mt-2 text-sm font-medium text-red-600 bg-red-100 border-2 border-red-300 rounded-lg cursor-not-allowed"
            >
              🚨 Cannot Join - Conflict Detected
            </button>
          </div>
        );
      }

      // Century Challenge Access Control - Gold/Platinum Only!
      // IMPORTANT: Do not hide an already-active Century challenge even if the user isn't Gold/Platinum.
      const isUserAlreadyActiveOnThis = userActiveChallenges?.some(c => c.challengeId === challenge.id || c.id === challenge.id);
      
      // 🐛 DEBUG: Log subscription details for Gold member issue
      if (challenge.difficulty === 'century') {
        console.log('🔍 CENTURY CHALLENGE ACCESS DEBUG:', {
          challengeId: challenge.id,
          userSubscriptionTier: userSubscription?.tier,
          subscriptionLimitsName: subscriptionLimits.name,
          isUserAlreadyActive: isUserAlreadyActiveOnThis,
          accessGranted: subscriptionLimits.name === 'Gold' || subscriptionLimits.name === 'Platinum'
        });
      }
      
      if (challenge.difficulty === 'century' && !isUserAlreadyActiveOnThis && subscriptionLimits.name !== 'Gold' && subscriptionLimits.name !== 'Platinum') {
        return (
          <div className="mt-3">
            <div className="p-3 border-2 border-yellow-400 rounded-lg bg-gradient-to-r from-black to-gray-900">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">🔒</span>
                <p className="text-sm font-bold text-yellow-400">EXCLUSIVE ACCESS REQUIRED</p>
              </div>
              <p className="mb-2 text-xs text-yellow-300">
                Century Conquest challenges are exclusive to Gold and Platinum members only!
              </p>
              <div className="flex items-center gap-2 mb-2 text-xs">
                <span className="px-2 py-1 text-black bg-yellow-400 rounded-full">💎 Gold</span>
                <span className="px-2 py-1 text-black bg-yellow-400 rounded-full">💎 Platinum</span>
              </div>
            </div>
            <button
              disabled
              className="w-full py-2 mt-2 text-sm font-medium text-yellow-600 bg-blue-100 border-2 border-blue-300 rounded-lg cursor-not-allowed"
            >
              👑 Upgrade to Gold/Platinum to Unlock
            </button>
          </div>
        );
      }

      // 👑 CENTURY CHALLENGE LIMIT - Only ONE active Century challenge at a time!
      if (challenge.difficulty === 'century' && hasActiveCenturyChallenge && !userStatus?.active) {
        return (
          <div className="mt-3">
            <div className="p-3 border-2 border-orange-400 rounded-lg bg-gradient-to-r from-orange-900 to-red-900">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">⚠️</span>
                <p className="text-sm font-bold text-orange-300">CENTURY CHALLENGE LIMIT</p>
              </div>
              <p className="mb-2 text-xs text-orange-200">
                You can only have ONE active Century challenge at a time! Complete your current Century challenge before starting another.
              </p>
              <div className="p-2 mt-2 bg-orange-100 border border-orange-300 rounded">
                <p className="text-xs font-semibold text-orange-800">
                  🏆 Active Century Challenge: {activeCenturyChallenges[0]?.name || 'Century Challenge'}
                </p>
              </div>
            </div>
            <button
              disabled
              className="w-full py-2 mt-2 text-sm font-medium text-orange-600 bg-orange-100 border-2 border-orange-300 rounded-lg cursor-not-allowed"
            >
              ⚠️ Complete Current Century Challenge First
            </button>
          </div>
        );
      }
      
      if (!userStatus) {
        // Show subscription limit warning if at limit
        if (currentActiveChallenges >= subscriptionLimits.activeChallenges) {
          return (
            <div className="mt-3">
              <div className={`p-3 ${subscriptionLimits.bgColor} rounded-lg border border-orange-200 mb-2`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-xs font-bold ${subscriptionLimits.color}`}>
                    {subscriptionLimits.icon} {subscriptionLimits.name} Plan Limit
                  </span>
                  <span className="text-xs font-semibold text-orange-600">
                    {currentActiveChallenges}/{subscriptionLimits.activeChallenges} Active
                  </span>
                </div>
                <p className="mb-2 text-xs text-orange-700">
                  Complete current challenges or upgrade to join more!
                </p>
                <div className="w-full h-2 bg-orange-200 rounded-full">
                  <div 
                    className="h-2 transition-all duration-300 bg-orange-500 rounded-full"
                    style={{ width: `${(currentActiveChallenges / subscriptionLimits.activeChallenges) * 100}%` }}
                  />
                </div>
              </div>
              <button
                disabled
                className="w-full py-2 mt-1 text-sm font-medium text-gray-500 bg-gray-300 rounded-lg cursor-not-allowed"
              >
                🔒 Complete Active Challenges First
              </button>
            </div>
          );
        }
        
        // Check if challenge is paused
        const isPaused = isUserChallengePaused(challenge.id);
        if (isPaused) {
          return (
            <button
              onClick={onJoin}
              className="w-full py-2 mt-3 text-sm font-medium text-white transition-all rounded-lg shadow-lg bg-gradient-to-r from-orange-500 to-orange-600 hover:opacity-90"
            >
              ▶️ Resume
            </button>
          );
        }

        return (
          <button
            onClick={onJoin}
            className={`w-full py-2 mt-3 bg-gradient-to-r ${gradientClass} text-white rounded-lg text-sm font-medium hover:opacity-90 transition-all shadow-lg`}
          >
            🚀 Start Earning
          </button>
        );
      }

      switch (userStatus.status) {
        case 'active':
          // Calculate progress with real-time backend data if available
          let progress = 0;
          let currentValue = 0;
          let totalValue = challenge.total || 100;
          
          if (isBackendConnected && healthData) {
            // Use real-time data for progress calculation
            if (challenge.metric === 'steps' && healthData.realTimeMetrics?.steps) {
              currentValue = healthData.realTimeMetrics.steps;
              progress = (currentValue / totalValue) * 100;
            } else if (challenge.metric === 'heartRate' && healthData.realTimeMetrics?.heartRate) {
              currentValue = healthData.realTimeMetrics.heartRate;
              progress = (currentValue / totalValue) * 100;
            } else {
              // Fallback to user status data
              currentValue = userStatus.data?.currentProgress || 0;
              progress = (currentValue / totalValue) * 100;
            }
          } else {
            // Use local state data
            currentValue = userStatus.data?.currentProgress || 0;
            progress = (currentValue / totalValue) * 100;
          }
          
          return (
            <div className="mt-3">
              {/* Real-time data indicator */}
              {isBackendConnected && healthData && (
                <div className="flex items-center gap-1 mb-2 text-xs text-green-600">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span>Live tracking active</span>
                </div>
              )}
              
              <div className="flex items-center justify-between mb-1 text-xs text-gray-600">
                <span>Your Progress</span>
                <span className="font-semibold">{progress.toFixed(1)}%</span>
              </div>
              
              {/* Enhanced progress display with real-time values */}
              <div className="mb-2 text-xs text-gray-500">
                <span>{currentValue.toLocaleString()} / {totalValue.toLocaleString()} {challenge.unit || challenge.metric}</span>
                {isBackendConnected && healthData && (
                  <span className="ml-2 text-green-600">• Live data</span>
                )}
              </div>
              
              <div className="w-full h-2 mb-3 overflow-hidden bg-gray-200 rounded-full">
                <div 
                  className={`h-full bg-gradient-to-r ${gradientClass} rounded-full transition-all duration-500`}
                  style={{ width: `${Math.min(progress, 100)}%` }}
                />
              </div>
              <button
                onClick={onJoin}
                className={`w-full py-2 bg-gradient-to-r ${gradientClass} text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity shadow-lg`}
              >
                💪 Continue Challenge
              </button>
            </div>
          );
          
        case 'completed':
          return (
            <div className="mt-3">
              <div className="flex items-center justify-center p-2 mb-2 border border-green-200 rounded-lg bg-green-50">
                <CheckCircle className="w-4 h-4 mr-1 text-green-600" />
                <span className="text-sm font-medium text-green-700">✅ Completed</span>
              </div>
              {userStatus.canRetry && (
                <button
                  onClick={onJoin}
                  className="w-full py-2 text-sm font-medium text-gray-700 transition-colors border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  🔄 Retry for Bonus
                </button>
              )}
            </div>
          );
          
        case 'failed':
          return (
            <div className="mt-3">
              <button
                onClick={onJoin}
                className="flex items-center justify-center w-full py-2 text-sm font-medium text-orange-600 transition-colors border border-orange-500 rounded-lg hover:bg-orange-50"
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                🎯 Try Again
              </button>
            </div>
          );
          
        default:
          // Check subscription limits for default case too
          if (currentActiveChallenges >= subscriptionLimits.activeChallenges) {
            return (
              <div className="mt-3">
                <div className={`p-3 ${subscriptionLimits.bgColor} rounded-lg border border-orange-200 mb-2`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-xs font-bold ${subscriptionLimits.color}`}>
                      {subscriptionLimits.icon} {subscriptionLimits.name} Plan Limit
                    </span>
                    <span className="text-xs font-semibold text-orange-600">
                      {currentActiveChallenges}/{subscriptionLimits.activeChallenges} Active
                    </span>
                  </div>
                  <p className="mb-2 text-xs text-orange-700">
                    Complete current challenges or upgrade to join more!
                  </p>
                  <div className="w-full h-2 bg-orange-200 rounded-full">
                    <div 
                      className="h-2 transition-all duration-300 bg-orange-500 rounded-full"
                      style={{ width: `${(currentActiveChallenges / subscriptionLimits.activeChallenges) * 100}%` }}
                    />
                  </div>
                </div>
                <button
                  disabled
                  className="w-full py-2 mt-1 text-sm font-medium text-gray-500 bg-gray-300 rounded-lg cursor-not-allowed"
                >
                  🔒 Complete Active Challenges First
                </button>
              </div>
            );
          }
          
          return (
            <div className={`w-full py-2 mt-3 bg-gradient-to-r ${gradientClass} text-white rounded-lg text-sm font-medium shadow-lg relative overflow-hidden`}>
              {(() => {
                const challengeKey = `${challenge.id}-daily`;
                const isJoining = joiningStates[challengeKey];
                const hasJoined = isUserChallengeActive(challenge.id);
                
                if (isJoining === 'success') {
                  return (
                    <div className="text-center">
                      <div className="absolute inset-0 bg-green-400/30"></div>
                      ✅ Joined!
                    </div>
                  );
                } else if (isJoining === 'error') {
                  return (
                    <div className="text-center">
                      <div className="absolute inset-0 bg-red-400/30"></div>
                      ❌ Error
                    </div>
                  );
                } else if (isJoining) {
                  return (
                    <div className="text-center">
                      <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                      ⏳ Joining...
                    </div>
                  );
                } else if (hasJoined) {
                  // Get detailed challenge progress data
                  const activeChallenge = userActiveChallenges.find(c => c.challengeId === challenge.id);
                  const progress = getUserChallengeProgress(challenge.id);
                  
                  // 🔧 HEALTHCARE CRITICAL: Use frontend calculated progress (accurate challenge-period data)
                  let currentProgress = 0;
                  if (challenge.unit === 'steps' || isStepChallenge(challenge.id)) {
                    // ✅ FIXED: Frontend calculations are correct - they properly filter to challenge start date
                    // Azure data may include pre-challenge historical data, so use frontend calculations
                    currentProgress = progress.currentProgress || progress.cumulativeSteps || 0;
                    
                    // Optional debug logging for step challenges
                    if (challenge.id === 'century-step-master') {
                    }
                  } else {
                    currentProgress = progress.currentProgress || 0;
                  }
                  const totalDuration = parseInt(challenge.duration?.replace(' days', '')) || parseInt(challenge.total) || 21; // Total days
                  const target = challenge.target?.value || (() => {
                    // Calculate daily target for step-based challenges
                    if (challenge.unit === 'steps' && challenge.targetValue && totalDuration) {
                      return challenge.targetValue / totalDuration; // Daily steps = total steps / total days
                    }
                    return challenge.targetValue || 3000; // Fallback for non-step challenges
                  })(); // Daily target for steps
                  
                  // 🔧 FIXED: Accurate day calculation for real-time health context
                  const startDate = activeChallenge?.startDate ? new Date(activeChallenge.startDate) : new Date();
                  const now = new Date();
                  
                  // Calculate using calendar days properly
                  const startDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
                  const currentDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                  const daysDifference = Math.floor((currentDay.getTime() - startDay.getTime()) / (1000 * 60 * 60 * 24));
                  
                  // 🎯 CORRECTED: Start day = Day 1, next day = Day 2, etc.
                  const daysElapsed = daysDifference + 1;
                  // 🔧 FIXED: Remaining days - if on Day 2 of 21, then 19 days remaining  
                  const daysRemaining = Math.max(0, totalDuration - daysElapsed);
                  const hoursRemaining = daysRemaining > 0 ? ((daysRemaining - 1) * 24) + (24 - now.getHours()) : 0;
                  
                  // Calculate daily progress percentage - PRODUCTION FIX: Use tracking method specific data
                  const trackingMethod = challenge.trackingMethod || 'step_tracking';
                  let dailyProgress = 0;
                  
                  switch (trackingMethod) {
                    case 'sugar_monitoring':
                      // For blood sugar: daily success is 1 if under target, 0 if not
                      const todaysGlucoseProgress = progress.todaysGlucose || 0;
                      dailyProgress = todaysGlucoseProgress > 0 ? 100 : 0; // Either successful day (100%) or not (0%)
                      break;
                      
                    case 'blood_pressure_log':
                      // 🩺 FIXED: BP Logger should show 0% when no device connected
                      if (progress.message && progress.message.includes('No blood pressure monitoring device connected')) {
                        console.warn('🩺 BP Logger: No device connected - setting daily progress to 0%');
                        dailyProgress = 0;
                      } else {
                        // For BP logging: progress based on readings taken today vs daily target (usually 2)
                        const todaysBPReadings = progress.todaysBPReadings || 0;
                        const dailyBPTarget = challenge.targetValue ? Math.ceil(challenge.targetValue / totalDuration) : 2;
                        dailyProgress = Math.min((todaysBPReadings / dailyBPTarget) * 100, 100);
                      }
                      break;
                      
                    case 'hydration_log':
                    case 'hydration_goal':
                      // 💧 Robust: take the best of backend value and locally computed from today's glasses
                      {
                        const backendDaily = (typeof progress.dailyProgressPercentage === 'number')
                          ? progress.dailyProgressPercentage
                          : 0;
                        const todaysWaterGlasses = (
                          // Prefer explicit today value fields over streak counts
                          progress.todaysProgress ?? progress.waterGlasses ?? 0
                        );
                        // IMPORTANT: For hydration streak challenges, targetValue can represent DAYS (e.g., 21)
                        // Daily target for hydration is fixed at 7 glasses regardless of overall duration
                        const dailyWaterTarget = 7;
                        const computedDaily = Math.min((todaysWaterGlasses / dailyWaterTarget) * 100, 100);
                        dailyProgress = Math.max(backendDaily, computedDaily);
                      }
                      break;
                      
                    case 'sleep_duration':
                    case 'consistent_sleep_optimization':
                    case 'precision_sleep_optimization':
                    case 'century_sleep_optimization':
                    case 'wake_time':
                      // Extract real sleep hours from health data
                      const realSleepHours = healthData?.healthMetrics?.lifestyle?.sleep?.value || 
                                           healthData?.realTimeMetrics?.sleep || 
                                           progress.sleepHours || 
                                           progress.currentProgress || 0;
                      // IMPORTANT: For Sleep Starter, Sleep Rookie, Sleep Champion, and Sleep Rhythm Reset, daily target is 7 hours (duration differs)
                      const dailySleepTarget = (challenge?.id === 'sleep-starter' || challenge?.id === 'sleep-rookie' || challenge?.id === 'sleep-champion' || challenge?.id === 'sleep-rhythm-reset' || challenge?.id === 'century-sleep-reset')
                        ? 7
                        : (challenge.targetValue || 7);
                      dailyProgress = Math.min((realSleepHours / dailySleepTarget) * 100, 100);
                      console.log(`😴 Sleep daily progress [${challenge?.id}]: ${realSleepHours}h / ${dailySleepTarget}h = ${dailyProgress.toFixed(1)}%`);
                      break;
                      
                    case 'resting_heart_rate':
                    case 'resting_heart_rate_optimization':
                    case 'hrv_stress_monitoring':
                      // ❤️ Robust: take max of backend and computed-from-today HR; compute from real HR not streak
                      {
                        const backendDaily = (typeof progress.dailyProgressPercentage === 'number')
                          ? progress.dailyProgressPercentage
                          : 0;
                        const todaysHR = (
                          progress.todaysProgress || progress.heartRate ||
                          healthData?.healthMetrics?.vitalSigns?.heartRate?.value ||
                          healthData?.realTimeMetrics?.heartRate || 0
                        );
                        const targetHR = challenge.targetValue || 70;
                        const computedDaily = todaysHR > 0
                          ? (todaysHR <= targetHR ? 100 : Math.max(0, 100 - ((todaysHR - targetHR) * 5)))
                          : 0;
                        dailyProgress = Math.max(backendDaily, computedDaily);
                      }
                      break;
                      
                    case 'meal_logging':
                    case 'macro_tracking':
                    case 'comprehensive_macro_tracking':
                    case 'advanced_macro_tracking':
                      const todaysMeals = progress.currentProgress || 0;
                      const dailyMealTarget = challenge.targetValue || 3;
                      dailyProgress = Math.min((todaysMeals / dailyMealTarget) * 100, 100);
                      break;
                      
                    case 'meditation_time':
                    case 'extended_meditation_tracking':
                    case 'breathing_exercise':
                      const todaysMeditation = progress.currentProgress || 0;
                      const dailyMeditationTarget = challenge.targetValue || 10;
                      dailyProgress = Math.min((todaysMeditation / dailyMeditationTarget) * 100, 100);
                      break;
                      
                    case 'medication_log':
                    case 'mood_log':
                      const todaysLogs = progress.currentProgress || 0;
                      const dailyLogTarget = challenge.targetValue || 1;
                      dailyProgress = Math.min((todaysLogs / dailyLogTarget) * 100, 100);
                      break;
                      
                    case 'guided_exercise':
                    case 'activity_duration':
                    case 'cardio_session_tracking':
                    case 'strength_training_tracking':
                      const todaysExercise = progress.currentProgress || 0;
                      const dailyExerciseTarget = challenge.targetValue || 30;
                      dailyProgress = Math.min((todaysExercise / dailyExerciseTarget) * 100, 100);
                      break;
                      
                    case 'standing_hours':
                    case 'hourly_activity':
                      const todaysStanding = progress.currentProgress || 0;
                      const dailyStandingTarget = challenge.targetValue || 8;
                      dailyProgress = Math.min((todaysStanding / dailyStandingTarget) * 100, 100);
                      break;
                      
                    case 'running_distance_tracking':
                      const todaysDistance = progress.currentProgress || 0;
                      const dailyDistanceTarget = challenge.targetValue || 5;
                      dailyProgress = Math.min((todaysDistance / dailyDistanceTarget) * 100, 100);
                      break;
                      
                    case 'digital_wellness_tracking':
                      const todaysScreenTime = progress.currentProgress || 0;
                      const dailyScreenTimeLimit = challenge.targetValue || 4;
                      // For screen time, lower is better, so reverse the calculation
                      dailyProgress = todaysScreenTime > 0 ? Math.max(0, Math.min(100, ((dailyScreenTimeLimit - todaysScreenTime) / dailyScreenTimeLimit) * 100)) : 100;
                      break;
                      
                    case 'steps_before_time':
                      // For daily consistency challenges like Gentle Morning Walker
                      const todaysGoalMet = progress?.todaysGoalMet || false;
                      dailyProgress = todaysGoalMet ? 100 : 0; // Binary: either goal met (100%) or not (0%)
                      break;
                      
                    case 'daily_steps':
                    case 'daily_step_streak':
                    case 'step_tracking':
                    default:
                      const todaysSteps = progress?.todaysSteps || healthData?.realTimeMetrics?.steps || 0;
                      dailyProgress = Math.min((todaysSteps / target) * 100, 100);
                      break;
                  }
                  
                  // overallProgress computed elsewhere in UI where needed
                  
                  // Format display values - PRODUCTION FIX: Show correct progress based on tracking method
                  let todaysProgressText = '';
                  let totalProgressText = '';
                  
                  // Variables for steps_before_time tracking method
                  let successfulDays = 0;
                  let targetDays = challenge.targetValue || 7;
                  let todaysGoalMet = false;
                  let currentChallengeDay = 1;
                  let dayProgressMessage = '';
                  
                  // Use the already defined trackingMethod from above
                  
                  switch (trackingMethod) {
                    case 'sugar_monitoring':
                      const todaysGlucose = progress.todaysGlucose || 0;
                      const targetGlucose = challenge.targetValue || 21;
                      todaysProgressText = progress.message 
                        ? `${progress.message}` 
                        : `${todaysGlucose} glucose readings today`;
                      totalProgressText = `Total: ${progress.cumulativeSuccessfulDays || 0} / ${targetGlucose} successful days`;
                      break;
                      
                    case 'blood_pressure_log':
                      const todaysBP = progress.todaysBPReadings || 0;
                      const targetBP = challenge.targetValue || 42;
                      todaysProgressText = progress.message 
                        ? `${progress.message}` 
                        : `${todaysBP} BP readings today`;
                      totalProgressText = `Total: ${progress.cumulativeReadings || 0} / ${targetBP} readings`;
                      break;
                      
                    case 'hydration_log':
                    case 'hydration_goal':
                    case 'premium_hydration_goal':
                    case 'premium_hydration_mastery':
                      // Extract real water intake from health data
                      const todaysWaterMetrics = healthData?.healthMetrics?.lifestyle?.waterIntake?.value || 
                                                 healthData?.realTimeMetrics?.water || 
                                                 progress.waterGlasses || 
                                                 progress.currentProgress || 0;
                      
                      // Convert ml to glasses if needed (1 glass = ~240ml)
                      const todaysWaterDisplay = todaysWaterMetrics > 50 ? Math.floor(todaysWaterMetrics / 240) : todaysWaterMetrics;
                      // targetWater removed (unused after hydration refactor)
                      
                      // For Hydration Hero, show consecutive days progress
                      if (challenge.id === 'hydration-hero') {
                        const consecutiveDays = progress.consecutiveDays || 0;
                        todaysProgressText = `${todaysWaterDisplay} glasses today`;
                        totalProgressText = `${consecutiveDays} / 7 consecutive days`;
                      } else if (challenge.id === 'hydration-initiate' || challenge.id === 'hydration-strider' || challenge.id === 'hydration-master') {
                        const daysRequired = challenge.id === 'hydration-initiate' ? 21 : (challenge.id === 'hydration-strider' ? 60 : 90);
                        const consecutiveDays = progress.consecutiveDays || progress.currentProgress || 0;
                        todaysProgressText = `${todaysWaterDisplay} glasses today`;
                        totalProgressText = `${consecutiveDays} / ${daysRequired} consecutive days`;
                      }
                      break;
                      
                    case 'sleep_duration':
                    case 'consistent_sleep_optimization':
                    case 'precision_sleep_optimization':
                    case 'century_sleep_optimization':
                    case 'wake_time':
                      // Extract real sleep hours from health data
                      const todaysSleepDisplay = healthData?.healthMetrics?.lifestyle?.sleep?.value || 
                                               healthData?.realTimeMetrics?.sleep || 
                                               progress.sleepHours || 
                                               progress.currentProgress || 0;
                      const targetSleep = challenge.targetValue || 7;
                      
                      // Debug logging for Sleep Starter
                      if (challenge.id === 'sleep-starter') {
                        console.log('🌙 Sleep Starter display debug:', {
                          challengeId: challenge.id,
                          healthDataSleep: healthData?.healthMetrics?.lifestyle?.sleep?.value,
                          progressSleepHours: progress.sleepHours,
                          progressCurrentProgress: progress.currentProgress,
                          consecutiveDays: progress.consecutiveDays,
                          finalDisplayValue: todaysSleepDisplay,
                          progress: progress
                        });
                      }
                      
                      // For Sleep Starter, Sleep Rookie, Sleep Champion, and Sleep Rhythm Reset, show consecutive nights progress with neutral "today" credit
                      if (challenge.id === 'sleep-starter' || challenge.id === 'sleep-rookie' || challenge.id === 'sleep-champion' || challenge.id === 'sleep-rhythm-reset' || challenge.id === 'century-sleep-reset') {
                        const consecutiveDays = progress.consecutiveDays || 0;
                        const requiredNights = (challenge.id === 'sleep-champion') ? 60 : (challenge.id === 'sleep-rookie' ? 21 : (challenge.id === 'sleep-rhythm-reset' ? 90 : (challenge.id === 'century-sleep-reset' ? 100 : 7)));
                        // Determine if today's success should visually count (neutral-today logic)
                        const backendDaily = typeof progress.dailyProgressPercentage === 'number'
                          ? progress.dailyProgressPercentage
                          : (typeof progress.todaysPercentage === 'number' ? progress.todaysPercentage : 0);
                        const dailySleepTarget = 7; // Fixed daily target for sleep streak challenges
                        const todayKey = new Date().toISOString().split('T')[0];
                        const todayRecorded = progress?.dailyProgress && progress.dailyProgress[todayKey];
                        const todayRecordedHours = Number(todayRecorded ?? 0);
                        const backendAlreadyValidated = (progress?.message || '').toLowerCase().includes('already validated');
                        const backendCountsToday = (backendDaily >= 100) || (todayRecordedHours >= dailySleepTarget);
                        const alreadyCountedSuccess = backendAlreadyValidated || backendCountsToday;
                        const todaySuccess = (todaysSleepDisplay >= dailySleepTarget) || (backendDaily >= 100);
                        const displayDays = Math.min(consecutiveDays + (todaySuccess && !alreadyCountedSuccess ? 1 : 0), requiredNights);

                        console.log('🌙 Sleep Streak display (neutral-today):', {
                          challengeId: challenge.id,
                          consecutiveDays,
                          requiredNights,
                          todaysSleepDisplay,
                          backendDaily,
                          todaySuccess,
                          displayDays
                        });

                        todaysProgressText = `${todaysSleepDisplay.toFixed(1)} hours sleep`;
                        totalProgressText = `${displayDays} / ${requiredNights} consecutive nights`;
                      } else {
                        todaysProgressText = progress.message 
                          ? `${progress.message}` 
                          : `${todaysSleepDisplay.toFixed(1)} hours sleep`;
                        totalProgressText = `Goal: ${targetSleep} hours nightly`;
                      }
                      break;
                      
                    case 'resting_heart_rate':
                    case 'resting_heart_rate_optimization':
                    case 'hrv_stress_monitoring':
                      // Extract real heart rate from health data
                      const todaysHRMetrics = healthData?.healthMetrics?.vitalSigns?.heartRate?.value || 
                                             healthData?.realTimeMetrics?.heartRate || 
                                             progress.heartRate || 
                                             progress.currentProgress || 0;
                      const targetHR = challenge.targetValue || 70;
                      
                      // For Heart Rate Calm/Cadet, show consecutive days progress
                      if (challenge.id === 'heart-rate-calm') {
                        const requiredDays = 7;
                        const backendDaily = typeof progress.dailyProgressPercentage === 'number' ? progress.dailyProgressPercentage : (typeof progress.todaysPercentage === 'number' ? progress.todaysPercentage : 0);
                        const todayKey = new Date().toISOString().split('T')[0];
                        const alreadyHaveToday = !!(progress?.dailyProgress && progress.dailyProgress[todayKey] !== undefined);
                        const todaySuccess = (todaysHRMetrics > 0 && todaysHRMetrics < targetHR) || (backendDaily >= 100);
                        const displayConsecutiveDays = Math.min((progress.consecutiveDays || 0) + (todaySuccess && !alreadyHaveToday ? 1 : 0), requiredDays);
                        todaysProgressText = todaysHRMetrics > 0 ? `${todaysHRMetrics} bpm today` : 'No reading today';
                        totalProgressText = `${displayConsecutiveDays} / ${requiredDays} consecutive days`;
                      } else if (challenge.id === 'heart-rate-cadet') {
                        const requiredDays = 21;
                        const backendDaily = typeof progress.dailyProgressPercentage === 'number' ? progress.dailyProgressPercentage : (typeof progress.todaysPercentage === 'number' ? progress.todaysPercentage : 0);
                        const todayKey = new Date().toISOString().split('T')[0];
                        const alreadyHaveToday = !!(progress?.dailyProgress && progress.dailyProgress[todayKey] !== undefined);
                        const todaySuccess = (todaysHRMetrics > 0 && todaysHRMetrics < targetHR) || (backendDaily >= 100);
                        const displayConsecutiveDays = Math.min((progress.consecutiveDays || 0) + (todaySuccess && !alreadyHaveToday ? 1 : 0), requiredDays);
                        todaysProgressText = todaysHRMetrics > 0 ? `${todaysHRMetrics} bpm today` : 'No reading today';
                        totalProgressText = `${displayConsecutiveDays} / ${requiredDays} consecutive days`;
                      } else if (challenge.id === 'heart-rate-mover') {
                        const requiredDays = 60;
                        const backendDaily = typeof progress.dailyProgressPercentage === 'number' ? progress.dailyProgressPercentage : (typeof progress.todaysPercentage === 'number' ? progress.todaysPercentage : 0);
                        const todayKey = new Date().toISOString().split('T')[0];
                        const alreadyHaveToday = !!(progress?.dailyProgress && progress.dailyProgress[todayKey] !== undefined);
                        const todaySuccess = (todaysHRMetrics > 0 && todaysHRMetrics < targetHR) || (backendDaily >= 100);
                        const displayConsecutiveDays = Math.min((progress.consecutiveDays || 0) + (todaySuccess && !alreadyHaveToday ? 1 : 0), requiredDays);
                        todaysProgressText = todaysHRMetrics > 0 ? `${todaysHRMetrics} bpm today` : 'No reading today';
                        totalProgressText = `${displayConsecutiveDays} / ${requiredDays} consecutive days`;
                      } else if (challenge.id === 'heart-rate-legend') {
                        const requiredDays = 120;
                        const backendDaily = typeof progress.dailyProgressPercentage === 'number' ? progress.dailyProgressPercentage : (typeof progress.todaysPercentage === 'number' ? progress.todaysPercentage : 0);
                        const todayKey = new Date().toISOString().split('T')[0];
                        const alreadyHaveToday = !!(progress?.dailyProgress && progress.dailyProgress[todayKey] !== undefined);
                        const todaySuccess = (todaysHRMetrics > 0 && todaysHRMetrics < targetHR) || (backendDaily >= 100);
                        const displayConsecutiveDays = Math.min((progress.consecutiveDays || 0) + (todaySuccess && !alreadyHaveToday ? 1 : 0), requiredDays);
                        todaysProgressText = todaysHRMetrics > 0 ? `${todaysHRMetrics} bpm today` : 'No reading today';
                        totalProgressText = `${displayConsecutiveDays} / ${requiredDays} consecutive days`;
                      } else {
                        // Fallback for any other heart rate style challenge IDs
                        todaysProgressText = progress.message 
                          ? `${progress.message}` 
                          : `${todaysHRMetrics} bpm today`;
                        totalProgressText = `Target: Under ${targetHR} bpm`;
                      }
                      break;
                      
                    case 'meal_logging':
                    case 'macro_tracking':
                    case 'comprehensive_macro_tracking':
                    case 'advanced_macro_tracking':
                      const todaysMeals = progress.currentProgress || 0;
                      const targetMeals = challenge.targetValue || 3;
                      todaysProgressText = progress.message 
                        ? `${progress.message}` 
                        : `${todaysMeals} meals logged today`;
                      totalProgressText = `Goal: ${targetMeals} meals daily`;
                      break;
                      
                    case 'meditation_time':
                    case 'extended_meditation_tracking':
                    case 'breathing_exercise':
                      const todaysMeditation = progress.currentProgress || 0;
                      const targetMeditation = challenge.targetValue || 10;
                      todaysProgressText = progress.message 
                        ? `${progress.message}` 
                        : `${todaysMeditation} minutes today`;
                      totalProgressText = `Goal: ${targetMeditation} minutes daily`;
                      break;
                      
                    case 'medication_log':
                    case 'mood_log':
                      const todaysLogs = progress.currentProgress || 0;
                      const targetLogs = challenge.targetValue || 1;
                      todaysProgressText = progress.message 
                        ? `${progress.message}` 
                        : `${todaysLogs} entries today`;
                      totalProgressText = `Goal: ${targetLogs} entries daily`;
                      break;
                      
                    case 'guided_exercise':
                    case 'activity_duration':
                    case 'cardio_session_tracking':
                    case 'strength_training_tracking':
                      const todaysExercise = progress.currentProgress || 0;
                      const targetExercise = challenge.targetValue || 30;
                      todaysProgressText = progress.message 
                        ? `${progress.message}` 
                        : `${todaysExercise} minutes today`;
                      totalProgressText = `Goal: ${targetExercise} minutes daily`;
                      break;
                      
                    case 'standing_hours':
                    case 'hourly_activity':
                      const todaysStanding = progress.currentProgress || 0;
                      const targetStanding = challenge.targetValue || 8;
                      todaysProgressText = progress.message 
                        ? `${progress.message}` 
                        : `${todaysStanding} hours today`;
                      totalProgressText = `Goal: ${targetStanding} hours daily`;
                      break;
                      
                    case 'running_distance_tracking':
                      const todaysDistance = progress.currentProgress || 0;
                      const targetDistance = challenge.targetValue || 5;
                      todaysProgressText = progress.message 
                        ? `${progress.message}` 
                        : `${todaysDistance.toFixed(1)} km today`;
                      totalProgressText = `Goal: ${targetDistance} km daily`;
                      break;
                      
                    case 'digital_wellness_tracking':
                      const todaysScreenTime = progress.currentProgress || 0;
                      const targetScreenTime = challenge.targetValue || 4;
                      todaysProgressText = progress.message 
                        ? `${progress.message}` 
                        : `${todaysScreenTime.toFixed(1)} hours screen time`;
                      totalProgressText = `Limit: ${targetScreenTime} hours daily`;
                      break;
                      
                    case 'steps_before_time':
                      // Daily consistency challenges like Gentle Morning Walker
                      successfulDays = progress?.successfulDays || 0;
                      targetDays = challenge.targetValue || 7;
                      todaysGoalMet = progress?.todaysGoalMet || false;
                      currentChallengeDay = progress?.currentChallengeDay || 1;
                      dayProgressMessage = progress?.dayProgressMessage || `Day ${currentChallengeDay} of ${targetDays}`;
                      const todaysStepCount = progress?.todaysSteps || 0;
                      
                      // 🔧 CRITICAL FIX: Correct successful days count for display consistency
                      let displaySuccessfulDays = successfulDays;
                      if (todaysGoalMet) {
                        // If today's goal is met, ensure the successful days count includes today
                        // Since we're on currentChallengeDay and today is successful, successful days should be at least currentChallengeDay
                        displaySuccessfulDays = Math.max(successfulDays, currentChallengeDay);
                      }
                      // Enhanced display for daily consistency tracking
                      todaysProgressText = todaysGoalMet 
                        ? `✅ Today: ${todaysStepCount} steps (Goal achieved!)` 
                        : `Today: ${todaysStepCount} steps (Need 500+ before 9AM)`;
                      totalProgressText = `${dayProgressMessage} • ${displaySuccessfulDays}/${targetDays} successful days`;
                      
                      // Update successfulDays variable for the progress bar calculation
                      successfulDays = displaySuccessfulDays;
                      break;
                      
                    case 'daily_steps':
                    case 'daily_step_streak':
                    case 'step_tracking':
                    default:
                      if (challenge.id === 'heart-rate-legend' || challenge.id === 'heart-rate-mover') {
                        // SAFEGUARD: If legend ever falls into default, show BPM not steps
                        const bpm = progress?.heartRate || progress?.todaysProgress || 0;
                        todaysProgressText = bpm > 0 ? `${bpm} bpm today` : 'No reading today';
                        const requiredDaysLegend = challenge.id === 'heart-rate-mover' ? 60 : 120;
                        const todayKey = new Date().toISOString().split('T')[0];
                        const alreadyHaveToday = !!(progress?.dailyProgress && progress.dailyProgress[todayKey] !== undefined);
                        const targetHR = challenge.targetValue || 70;
                        const backendDaily = typeof progress.dailyProgressPercentage === 'number' ? progress.dailyProgressPercentage : 0;
                        const todaySuccess = (bpm > 0 && bpm < targetHR) || backendDaily >= 100;
                        const displayDays = Math.min((progress.consecutiveDays || 0) + (todaySuccess && !alreadyHaveToday ? 1 : 0), requiredDaysLegend);
                        totalProgressText = `${displayDays} / ${requiredDaysLegend} consecutive days`;
                      } else {
                        const todaysSteps = progress?.todaysSteps || healthData?.realTimeMetrics?.steps || 0;
                        todaysProgressText = `${todaysSteps.toLocaleString()} / ${target.toLocaleString()} steps today`;
                        totalProgressText = `Total: ${currentProgress.toLocaleString()} / ${(challenge.targetValue || (target * totalDuration)).toLocaleString()} steps`;
                      }
                      break;
                  }
                  
                  return (
                    <div className="w-full">
                      <div className="absolute inset-0 bg-green-400/30"></div>
                      <div className="relative flex flex-col p-2 space-y-2">
                        {/* Status Header */}
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-bold text-green-100">✅ ACTIVE</span>
                          <span className="text-xs text-green-200">
                            {(challenge.trackingMethod === 'steps_before_time' && progress?.dayProgressMessage) 
                              ? progress.dayProgressMessage 
                              : `Day ${daysElapsed}/${totalDuration}`}
                          </span>
                        </div>
                        
                        {/* Daily Progress Bar */}
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs">
                            <span className="text-green-100">Today's Progress</span>
                            <span className="text-green-200">{dailyProgress.toFixed(0)}%</span>
                          </div>
                          <div className="w-full h-2 rounded-full bg-white/20">
                            <div 
                              className="h-2 transition-all duration-500 rounded-full bg-gradient-to-r from-green-400 to-emerald-400"
                              style={{ width: `${dailyProgress}%` }}
                            ></div>
                          </div>
                          <div className="text-xs text-center text-green-100">{todaysProgressText}</div>
                        </div>
                        
                        {/* Overall Challenge Progress */}
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs">
                            <span className="text-green-100">Total Progress</span>
                            <span className="text-green-200">
                              {(() => {
                                // Special handling for Sleep Starter, Sleep Rookie, and Sleep Champion consecutive days with neutral "today" credit
                                if (challenge.id === 'sleep-starter' || challenge.id === 'sleep-rookie' || challenge.id === 'sleep-champion' || challenge.id === 'sleep-rhythm-reset' || challenge.id === 'century-sleep-reset') {
                                  const consecutiveDays = progress.consecutiveDays || 0;
                                  const requiredNights = (challenge.id === 'sleep-champion') ? 60 : (challenge.id === 'sleep-rookie' ? 21 : (challenge.id === 'sleep-rhythm-reset' ? 90 : (challenge.id === 'century-sleep-reset' ? 100 : 7)));
                                  const backendDaily = typeof progress.dailyProgressPercentage === 'number' ? progress.dailyProgressPercentage : (typeof progress.todaysPercentage === 'number' ? progress.todaysPercentage : 0);
                                  const todayKey = new Date().toISOString().split('T')[0];
                                  // Determine today's sleep from available sources
                                  const todaysSleep = (
                                    healthData?.healthMetrics?.lifestyle?.sleep?.value ||
                                    healthData?.realTimeMetrics?.sleep ||
                                    progress.sleepHours ||
                                    progress.currentProgress || 0
                                  );
                                  const dailySleepTarget = 7;
                                  const todayRecorded = progress?.dailyProgress && progress.dailyProgress[todayKey];
                                  const todayRecordedHours = Number(todayRecorded ?? 0);
                                  const backendAlreadyValidated = (progress?.message || '').toLowerCase().includes('already validated');
                                  const backendCountsToday = (backendDaily >= 100) || (todayRecordedHours >= dailySleepTarget);
                                  const alreadyCountedSuccess = backendAlreadyValidated || backendCountsToday;
                                  const todaySuccess = (todaysSleep >= dailySleepTarget) || (backendDaily >= 100);
                                  const displayDays = Math.min(consecutiveDays + (todaySuccess && !alreadyCountedSuccess ? 1 : 0), requiredNights);
                                  return `${Math.round((displayDays / requiredNights) * 100)}%`;
                                }
                                // Special handling for Hydration Hero and Heart Rate Calm
                                if (challenge.id === 'hydration-hero' || challenge.id === 'heart-rate-calm') {
                                  const consecutiveDays = progress.consecutiveDays || 0;
                                  // Include today's success if not already counted
                                  const requiredDays = 7;
                                  const backendDaily = typeof progress.dailyProgressPercentage === 'number' ? progress.dailyProgressPercentage : (typeof progress.todaysPercentage === 'number' ? progress.todaysPercentage : 0);
                                  const todayVal = Number(progress.todaysProgress || 0);
                                  const todayKey = new Date().toISOString().split('T')[0];
                                  const alreadyHaveToday = !!(progress?.dailyProgress && progress.dailyProgress[todayKey] !== undefined);
                                  const todaySuccess = (todayVal > 0 && todayVal < (challenge.targetValue || 70)) || (backendDaily >= 100);
                                  const displayDays = Math.min(consecutiveDays + (todaySuccess && !alreadyHaveToday ? 1 : 0), requiredDays);
                                  return `${Math.round((displayDays / requiredDays) * 100)}%`;
                                }
                                // Special handling for Hydration Initiate (21-day streak)
                                if (challenge.id === 'hydration-initiate') {
                                  const consecutiveDays = progress.consecutiveDays || 0;
                                  return `${Math.round((consecutiveDays / 21) * 100)}%`;
                                }
                                // Special handling for Heart Rate Cadet (21-day streak)
                                if (challenge.id === 'heart-rate-cadet') {
                                  const requiredDays = 21;
                                  const backendDaily = typeof progress.dailyProgressPercentage === 'number' ? progress.dailyProgressPercentage : (typeof progress.todaysPercentage === 'number' ? progress.todaysPercentage : 0);
                                  const todayVal = Number(progress.todaysProgress || 0);
                                  const todayKey = new Date().toISOString().split('T')[0];
                                  const alreadyHaveToday = !!(progress?.dailyProgress && progress.dailyProgress[todayKey] !== undefined);
                                  const todaySuccess = (todayVal > 0 && todayVal < (challenge.targetValue || 70)) || (backendDaily >= 100);
                                  const displayDays = Math.min((progress.consecutiveDays || 0) + (todaySuccess && !alreadyHaveToday ? 1 : 0), requiredDays);
                                  return `${Math.round((displayDays / requiredDays) * 100)}%`;
                                }
                                // Default logic for other challenges
                                return (challenge.trackingMethod === 'steps_before_time') 
                                  ? `${((successfulDays / targetDays) * 100).toFixed(0)}%`
                                  : `${((currentProgress / (challenge.targetValue || (target * totalDuration))) * 100).toFixed(0)}%`;
                              })()}
                            </span>
                          </div>
                          <div className="w-full bg-white/20 rounded-full h-1.5">
                            <div 
                              className="bg-gradient-to-r from-yellow-400 to-orange-400 h-1.5 rounded-full transition-all duration-500"
                              style={{ width: `${(() => {
                                // Special handling for Sleep Starter, Sleep Rookie, and Sleep Champion consecutive days with neutral "today" credit
                                if (challenge.id === 'sleep-starter' || challenge.id === 'sleep-rookie' || challenge.id === 'sleep-champion' || challenge.id === 'sleep-rhythm-reset' || challenge.id === 'century-sleep-reset') {
                                  const consecutiveDays = progress.consecutiveDays || 0;
                                  const requiredNights = (challenge.id === 'sleep-champion') ? 60 : (challenge.id === 'sleep-rookie' ? 21 : (challenge.id === 'sleep-rhythm-reset' ? 90 : (challenge.id === 'century-sleep-reset' ? 100 : 7)));
                                  const backendDaily = typeof progress.dailyProgressPercentage === 'number' ? progress.dailyProgressPercentage : (typeof progress.todaysPercentage === 'number' ? progress.todaysPercentage : 0);
                                  const todayKey = new Date().toISOString().split('T')[0];
                                  const backendSnapshot = (challengeProgress && challengeProgress[challenge.id]) || {};
                                  const altSleepA = Number(challengeProgress?.['sleep-rookie']?.sleepHours || 0);
                                  const altSleepB = Number(challengeProgress?.['sleep-starter']?.sleepHours || 0);
                                  const altSleepC = Number(challengeProgress?.['sleep-champion']?.sleepHours || 0);
                                  const altSleepD = Number(challengeProgress?.['sleep-rhythm-reset']?.sleepHours || 0);
                                  const altSleepE = Number(challengeProgress?.['century-sleep-reset']?.sleepHours || 0);
                                  const dailySleepTarget = Number(backendSnapshot.targetDailyHours || 7);
                                  const todaysSleep = Math.max(
                                    Number(healthData?.healthMetrics?.lifestyle?.sleep?.value || 0),
                                    Number(healthData?.realTimeMetrics?.sleep || 0),
                                    Number(progress.sleepHours || 0),
                                    Number(progress.currentProgress || 0),
                                    Number(backendSnapshot.sleepHours || 0),
                                    altSleepA,
                                    altSleepB,
                                    altSleepC,
                                    altSleepD,
                                    altSleepE
                                  );
                                  const todayRecorded = (backendSnapshot?.dailyProgress && backendSnapshot.dailyProgress[todayKey]) || (progress?.dailyProgress && progress.dailyProgress[todayKey]);
                                  const todayRecordedHours = Number(todayRecorded ?? 0);
                                  const backendAlreadyValidated = ((backendSnapshot?.message || progress?.message || '')).toLowerCase().includes('already validated');
                                  const backendCountsToday = (backendDaily >= 100) || (todayRecordedHours >= dailySleepTarget);
                                  const alreadyCountedSuccess = backendAlreadyValidated || backendCountsToday;
                                  const todaySuccess = (todaysSleep >= dailySleepTarget) || (backendDaily >= 100);
                                  const displayDays = Math.min(consecutiveDays + (todaySuccess && !alreadyCountedSuccess ? 1 : 0), requiredNights);
                                  return Math.min((displayDays / requiredNights) * 100, 100);
                                }
                                // Special handling for Hydration Hero and Heart Rate Calm
                                if (challenge.id === 'hydration-hero' || challenge.id === 'heart-rate-calm') {
                                  const consecutiveDays = progress.consecutiveDays || 0;
                                  const requiredDays = 7;
                                  const backendDaily = typeof progress.dailyProgressPercentage === 'number' ? progress.dailyProgressPercentage : (typeof progress.todaysPercentage === 'number' ? progress.todaysPercentage : 0);
                                  const todayVal = Number(progress.todaysProgress || 0);
                                  const todayKey = new Date().toISOString().split('T')[0];
                                  const alreadyHaveToday = !!(progress?.dailyProgress && progress.dailyProgress[todayKey] !== undefined);
                                  const todaySuccess = (todayVal > 0 && todayVal < (challenge.targetValue || 70)) || (backendDaily >= 100);
                                  const displayDays = Math.min(consecutiveDays + (todaySuccess && !alreadyHaveToday ? 1 : 0), requiredDays);
                                  return Math.min((displayDays / requiredDays) * 100, 100);
                                }
                                // Special handling for Hydration Initiate (21-day streak)
                                if (challenge.id === 'hydration-initiate') {
                                  const consecutiveDays = progress.consecutiveDays || 0;
                                  return Math.min((consecutiveDays / 21) * 100, 100);
                                }
                                // Special handling for Heart Rate Cadet (21-day streak)
                                if (challenge.id === 'heart-rate-cadet') {
                                  const requiredDays = 21;
                                  const backendDaily = typeof progress.dailyProgressPercentage === 'number' ? progress.dailyProgressPercentage : (typeof progress.todaysPercentage === 'number' ? progress.todaysPercentage : 0);
                                  const todayVal = Number(progress.todaysProgress || 0);
                                  const todayKey = new Date().toISOString().split('T')[0];
                                  const alreadyHaveToday = !!(progress?.dailyProgress && progress.dailyProgress[todayKey] !== undefined);
                                  const todaySuccess = (todayVal > 0 && todayVal < (challenge.targetValue || 70)) || (backendDaily >= 100);
                                  const displayDays = Math.min((progress.consecutiveDays || 0) + (todaySuccess && !alreadyHaveToday ? 1 : 0), requiredDays);
                                  return Math.min((displayDays / requiredDays) * 100, 100);
                                }
                                // Default logic for other challenges
                                return (challenge.trackingMethod === 'steps_before_time') 
                                  ? Math.min(((successfulDays / targetDays) * 100), 100)
                                  : Math.min(((currentProgress / (challenge.targetValue || (target * totalDuration))) * 100), 100);
                              })()}%` }}
                            ></div>
                          </div>
                          <div className="text-xs text-center text-green-100">{totalProgressText}</div>
                        </div>
                        
                        {/* Time Remaining */}
                        <div className="flex justify-between text-xs">
                          <span className="text-green-100">
                            {daysRemaining > 0 ? `${daysRemaining} days left` : 'Final day!'}
                          </span>
                          <span className="text-green-200">
                            {hoursRemaining > 24 ? `${Math.floor(hoursRemaining/24)}d ${hoursRemaining%24}h` : `${hoursRemaining}h left`}
                          </span>
                        </div>
                        
                        {/* Achievement Status */}
                        {dailyProgress >= 100 && (
                          <div className="text-xs text-center text-yellow-300 animate-pulse">
                            🎉 Today's goal achieved!
                          </div>
                        )}
                        
                        {/* 🏥 HEALTHCARE COMPLIANCE STATUS */}
                        {isStepChallenge(challenge.id) && (() => {
                          const hasReachedTarget = currentProgress >= (challenge.targetValue || (target * totalDuration));
                          const hasCompletedDuration = daysElapsed >= totalDuration;
                          
                          if (hasReachedTarget && !hasCompletedDuration) {
                            const remainingDays = totalDuration - daysElapsed;
                            return (
                              <div className="px-2 py-1 mx-1 text-xs text-center border rounded-lg bg-blue-500/20 border-blue-400/30">
                                <div className="font-semibold text-blue-200">🏥 Healthcare Consistency</div>
                                <div className="text-blue-100">Target reached! Continue {remainingDays} more days for health benefits</div>
                              </div>
                            );
                          } else if (hasCompletedDuration && hasReachedTarget) {
                            return (
                              <div className="px-2 py-1 mx-1 text-xs text-center border rounded-lg bg-green-500/20 border-green-400/30">
                                <div className="font-semibold text-green-200">✅ Healthcare Complete</div>
                                <div className="text-green-100">Both target and duration achieved!</div>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>
                  );
                } else {
                  const isPaused = isUserChallengePaused(challenge.id);
                  if (isPaused) {
                    return (
                      <button
                        onClick={onJoin}
                        className="w-full h-full font-medium text-center text-white transition-all rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 hover:opacity-90"
                      >
                        ▶️ Resume
                      </button>
                    );
                  } else {
                    return (
                      <button
                        onClick={onJoin}
                        className="w-full h-full text-center"
                      >
                        🚀 Start Earning
                      </button>
                    );
                  }
                }
              })()}
            </div>
          );
      }
    };
    
    return (
      <div className={`relative overflow-hidden transition-all duration-300 shadow-md rounded-xl ${
        challenge.isPreviewCard 
          ? 'bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-200 hover:shadow-xl hover:scale-105' 
          : challenge.isActive === false || challenge.futurePhase 
            ? 'bg-gray-100 opacity-70 hover:opacity-80' 
            : 'bg-white hover:shadow-lg hover:scale-105'
      }`}>
        {/* (Ribbon removed by request; using simple disabled button only) */}
        {/* Top gradient section */}
        <div className={`relative p-5 bg-gradient-to-br ${gradientClass} text-white ${
          challenge.isActive === false || challenge.futurePhase 
            ? 'opacity-60' 
            : ''
        }`}>
          {/* Header with badges */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <span className="text-3xl">{challenge.icon}</span>
              <div className="flex flex-col gap-1">
                <span className="px-2 py-0.5 bg-white/20 backdrop-blur-sm rounded-full text-xs font-medium uppercase inline-block w-fit">
                  {challenge.difficulty}
                </span>
                {challenge.seniorFriendly && (
                  <span className="px-2 py-0.5 bg-white/20 backdrop-blur-sm rounded-full text-xs font-medium inline-block w-fit">
                    👥 Senior Friendly
                  </span>
                )}
                {challenge.verificationRequired && (
                  <span className="px-2 py-0.5 bg-yellow-400/30 backdrop-blur-sm rounded-full text-xs font-bold inline-block w-fit">
                    🔒 Verified Only
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {/* Challenge name */}
          <h4 className="text-lg font-bold">
            {challenge.name}
          </h4>
        </div>
        
        {/* Bottom white section */}
        <div className={`p-5 ${
          challenge.isActive === false || challenge.futurePhase 
            ? 'bg-gray-50' 
            : 'bg-white'
        }`}>
          {/* Description */}
          <p className="mb-3 text-sm text-gray-600">
            {challenge.description}
          </p>
          
          {/* Future Phases bottom label removed to preserve card height; overlay ribbon used instead */}

          {/* Preview Card Special Label */}
          {challenge.isPreviewCard && (
            <div className="p-2 mb-3 border border-indigo-200 rounded-lg bg-gradient-to-r from-indigo-50 to-purple-50">
              <div className="flex items-center justify-center gap-2">
                <span className="text-sm">�</span>
                <p className="text-sm font-semibold text-indigo-800">Future Challenges</p>
              </div>
            </div>
          )}
          
          {/* Device Tracking Info - NEW! */}
          {challenge.deviceTracked && (
            <div className="p-2 mb-3 border border-blue-200 rounded-lg bg-blue-50">
              <p className="mb-1 text-xs font-semibold text-blue-800">📱 Device Verified Tracking:</p>
              <div className="flex flex-wrap gap-1">
                {challenge.deviceTracked.map((device, index) => (
                  <span key={index} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                    {device}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Health Benefit - NEW! */}
          {challenge.healthBenefit && (
            <div className="p-2 mb-3 border border-green-200 rounded-lg bg-green-50">
              <p className="mb-1 text-xs font-semibold text-green-800">💚 Health Impact:</p>
              <p className="text-xs text-green-700">{challenge.healthBenefit}</p>
            </div>
          )}

          {/* Real-time Health Context - BACKEND INTEGRATION */}
          {healthContext && userStatus?.status === 'active' && (
            <div className="p-3 mb-3 border border-blue-200 rounded-lg bg-gradient-to-r from-blue-50 to-green-50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-blue-800">📊 Today's Real-Time Progress</span>
                <span className="text-xs font-semibold text-blue-600">
                  {healthContext.current} / {healthContext.target} {healthContext.unit}
                </span>
              </div>
              <div className="w-full h-2 mb-1 bg-gray-200 rounded-full">
                <div 
                  className="h-2 transition-all duration-500 rounded-full bg-gradient-to-r from-blue-500 to-green-500"
                  style={{ width: `${healthContext.percentage}%` }}
                ></div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600">Live from your devices</span>
                <span className="text-xs font-semibold text-green-600">
                  {healthContext.percentage >= 100 ? '✅ Target Met!' : `${Math.round(healthContext.percentage)}%`}
                </span>
              </div>
            </div>
          )}

          {/* 🔧 NEW: Data Quality Indicator for Step Challenges */}
          {userStatus?.status === 'active' && isStepChallenge(challenge.id) && (
            (() => {
              const progressData = getUserChallengeProgress(challenge.id);
              const dataQuality = progressData?.dataQuality;
              
              if (!dataQuality) return null;
              
              const getQualityColor = (score) => {
                switch(score) {
                  case 'perfect': return 'border-green-200 bg-green-50 text-green-800';
                  case 'good': return 'border-yellow-200 bg-yellow-50 text-yellow-800';
                  case 'needs_attention': return 'border-orange-200 bg-orange-50 text-orange-800';
                  default: return 'border-gray-200 bg-gray-50 text-gray-800';
                }
              };
              
              const getQualityIcon = (score) => {
                switch(score) {
                  case 'perfect': return '✅';
                  case 'good': return '⚠️';
                  case 'needs_attention': return '🔄';
                  default: return '📊';
                }
              };
              
              const getQualityMessage = (score, missingDays, isDataFresh) => {
                if (!isDataFresh) return 'Device data may be from yesterday';
                if (score === 'perfect') return 'Perfect tracking - no missing days';
                if (score === 'good') return `Good tracking - ${missingDays} missing days`;
                return `Needs attention - ${missingDays} days without device use`;
              };
              
              return (
                <div className={`p-2 mb-3 border rounded-lg ${getQualityColor(dataQuality.qualityScore)}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm">{getQualityIcon(dataQuality.qualityScore)}</span>
                      <span className="text-xs font-semibold">
                        Data Quality: {dataQuality.qualityScore.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                    <span className="text-xs">
                      {dataQuality.validDays}/{dataQuality.validDays + dataQuality.missingDays} days
                    </span>
                  </div>
                  <p className="mt-1 text-xs">
                    {getQualityMessage(dataQuality.qualityScore, dataQuality.missingDays, dataQuality.isDataFresh)}
                  </p>
                  {dataQuality.missingDays > 0 && (
                    <p className="mt-1 text-xs opacity-75">
                      💡 Use your device daily for accurate step tracking
                    </p>
                  )}
                </div>
              );
            })()
          )}

          {/* Backend Challenge Status - REAL-TIME VALIDATION */}
          {backendStatus && (
            <div className={`p-2 mb-3 border rounded-lg ${
              backendStatus.color.includes('green') ? 'border-green-200 bg-green-50' : 
              backendStatus.color.includes('blue') ? 'border-blue-200 bg-blue-50' : 
              'border-gray-200 bg-gray-50'
            }`}>
              <div className="flex items-center space-x-2">
                <backendStatus.icon className={`w-4 h-4 ${backendStatus.color} ${backendStatus.animate ? 'animate-pulse' : ''}`} />
                <span className={`text-xs font-semibold ${backendStatus.color}`}>{backendStatus.text}</span>
              </div>
            </div>
          )}

          {/* 🚨 CONFLICT WARNING SYSTEM - Show potential conflicts before joining */}
          {challengeConflict.hasConflict && !userStatus?.active && (
            <div className="p-3 mb-3 border-2 border-orange-400 rounded-lg bg-gradient-to-r from-orange-50 to-red-50">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">⚠️</span>
                <p className="text-xs font-bold text-orange-700">POTENTIAL CONFLICT WARNING</p>
              </div>
              <p className="mb-2 text-xs text-orange-600">
                This {challengeConflict.conflictType === 'step_overlap' ? 'step-based' : 'activity-based'} challenge conflicts with your active challenges.
              </p>
              <div className="text-xs">
                <p className="mb-1 font-semibold text-orange-700">🔍 Why this matters:</p>
                <p className="text-orange-600">
                  Multiple {challengeConflict.conflictType === 'step_overlap' ? 'step' : 'activity'} challenges would allow automatic completion 
                  using the same activity, which violates fair play principles.
                </p>
              </div>
            </div>
          )}

          {/* Century Challenge Features */}
          {challenge.difficulty === 'century' && (
            <div className="p-3 mb-3 border-2 border-yellow-400 rounded-lg bg-gradient-to-r from-black to-gray-900">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm">👑</span>
                <p className="text-xs font-bold text-yellow-400">CENTURY CONQUEST SERIES</p>
                <span className="px-1.5 py-0.5 text-xs font-bold text-black bg-yellow-400 rounded-full">BRANDED</span>
              </div>
              
              {challenge.specialBadge && (
                <div className="flex items-center gap-1 mb-1">
                  <span className="text-xs">🏆</span>
                  <span className="text-xs font-semibold text-yellow-300">Unlock: {challenge.specialBadge} Badge</span>
                </div>
              )}
              
              {challenge.bonusPoints && (
                <div className="flex items-center gap-1 mb-1">
                  <span className="text-xs">💰</span>
                  <span className="text-xs font-semibold text-yellow-300">+{challenge.bonusPoints} Bonus Points Available</span>
                </div>
              )}
              
              {challenge.unlockFeature && (
                <div className="flex items-center gap-1 mb-1">
                  <span className="text-xs">🔓</span>
                  <span className="text-xs font-semibold text-yellow-300">Unlock: {challenge.unlockFeature}</span>
                </div>
              )}
              
              <div className="flex items-center gap-1 text-xs">
                <span className="px-2 py-0.5 text-black bg-yellow-400 rounded-full">💎 Gold+ Only</span>
                <span className="text-yellow-300">100-Day Epic Challenge</span>
              </div>
            </div>
          )}
          
          {/* Info row */}
          <div className="flex items-center justify-between mb-3 text-sm">
            <span className="flex items-center text-gray-500">
              <Clock className="w-4 h-4 mr-1" />
              {challenge.duration}
            </span>
            <span className="flex items-center text-gray-500">
              <Users className="w-4 h-4 mr-1" />
              {challenge.participants?.toLocaleString()}
            </span>
          </div>
          
          {/* Points display - Clean professional format */}
          <div className="flex items-center mb-3">
            <Trophy className="w-4 h-4 mr-1 text-yellow-500" />
            <span className="font-bold text-gray-800">{challenge.points?.toLocaleString()} Medi Points</span>
          </div>
          
          {/* Status/Action */}
          {renderChallengeStatus()}
          
          {/* Action buttons for joined challenges - Separate from main button to avoid nesting */}
          {isUserChallengeActive(challenge.id) && (
            <div className={`flex gap-1 mt-2 ${challenge.name?.toLowerCase().includes('sleep starter') ? 'flex-wrap' : ''}`}>
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  handleViewChallengeDetails(challenge.id);
                }}
                className="flex-1 px-2 py-1 text-xs text-blue-700 transition-colors bg-blue-100 rounded hover:bg-blue-200"
              >
                📊 Details
              </button>
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  handlePauseChallenge(challenge.id);
                }}
                className="flex-1 px-2 py-1 text-xs text-orange-700 transition-colors bg-orange-100 rounded hover:bg-orange-200"
              >
                ⏸️ Pause
              </button>
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  if (window.confirm('Are you sure you want to stop this challenge? Progress will be marked as uncompleted.')) {
                    handleStopChallenge(challenge.id);
                  }
                }}
                disabled={stoppingStates[challenge.id]}
                className={`flex-1 px-2 py-1 text-xs transition-colors rounded ${
                  stoppingStates[challenge.id] 
                    ? 'text-gray-600 bg-gray-200 cursor-not-allowed' 
                    : 'text-red-700 bg-red-100 hover:bg-red-200'
                }`}
              >
                {stoppingStates[challenge.id] ? '⏳ Stopping...' : '🛑 Stop'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Phase 1 Launch Banner */}
      <div className="p-4 border-2 border-blue-400 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-100">
        <div className="flex items-center gap-3">
          <span className="text-2xl">🚀</span>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-blue-900">The Future of Healthcare - Phase 1 Live!</h3>
            <p className="text-sm text-blue-700">
              <span className="font-semibold">MediCureOn: The New Standard for Healthcare.</span> Advanced challenge system ready with continuous innovation every 2-3 weeks. The next 5 years of healthcare transformation starts here!
            </p>
          </div>
          <div className="text-right">
            <span className="px-3 py-1 text-xs font-bold text-white bg-blue-500 rounded-full">LIVE READY</span>
          </div>
        </div>
      </div>

      {/* Real-time Backend Status */}
      {!isBackendConnected && (
        <div className="p-4 border rounded-lg text-amber-800 bg-amber-100 border-amber-200">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></div>
            <span className="text-sm">Challenge system is operating in offline mode. Your progress is being saved locally.</span>
          </div>
        </div>
      )}

      {isBackendConnected && (
        <div className="p-4 text-green-800 bg-green-100 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-sm">✅ Connected to production challenge system • Real-time health data active</span>
          </div>
        </div>
      )}

      {/* Fresh Start Message for New Cloud System */}
      {!loading && userActiveChallenges.length === 0 && userCompletedChallenges.length === 0 && (
        <div className="p-4 border border-blue-200 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-start gap-3">
            <span className="text-2xl">🏁</span>
            <div className="flex-1">
              <h3 className="mb-2 text-sm font-bold text-blue-900">Welcome to MediCureOn Challenges!</h3>
              <div className="space-y-2 text-xs text-blue-700">
                <p>✅ <span className="font-semibold">Track Your Progress:</span> Monitor your health goals with our challenge system</p>
                <p>✅ <span className="font-semibold">Earn Medi Points:</span> Complete challenges to earn rewards</p>
                <p>✅ <span className="font-semibold">Stay Motivated:</span> Join challenges that match your fitness level</p>
                <p>🚀 <span className="font-semibold">Ready to Start:</span> Browse available challenges below!</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-blue-500 rounded-full border-t-transparent animate-spin"></div>
            <span className="text-blue-700">Loading challenge data...</span>
          </div>
        </div>
      )}

      {/* Removed technical cloud system welcome and local migration notice per product guidance */}

      {/* Loading State Display */}
      {loading && (
        <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-blue-500 rounded-full border-t-transparent animate-spin"></div>
            <span className="text-blue-700">Loading challenge data...</span>
          </div>
        </div>
      )}

      {/* Statistics Dashboard with Navy Blue Gradient Background */}
      <div className="p-6 rounded-xl bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div className="p-4 bg-white bg-opacity-10 rounded-xl backdrop-blur-sm">
            <Target className="w-8 h-8 mx-auto mb-2 text-yellow-300" />
            <p className="text-3xl font-bold text-white">{userActiveChallenges.length}</p>
            <p className="text-sm text-blue-100">Active Challenges</p>
            <p className="mt-1 text-xs text-yellow-300">🔥 Keep going!</p>
            {/* Subscription limit indicator */}
            <div className="flex items-center justify-center mt-2">
              <span className="px-2 py-1 text-xs rounded-full bg-white/20">
                {subscriptionLimits.icon} {userActiveChallenges.length}/{subscriptionLimits.activeChallenges} limit
              </span>
            </div>
          </div>
          
          <div className="p-4 bg-white bg-opacity-10 rounded-xl backdrop-blur-sm">
            <Trophy className="w-8 h-8 mx-auto mb-2 text-green-300" />
            <p className="text-3xl font-bold text-white">{userCompletedChallenges.length}</p>
            <p className="text-sm text-blue-100">Completed</p>
            <p className="mt-1 text-xs text-green-300">✅ Well done!</p>
          </div>
          
          <div className="p-4 bg-white bg-opacity-10 rounded-xl backdrop-blur-sm">
            <Zap className="w-8 h-8 mx-auto mb-2 text-yellow-300" />
            <p className="text-3xl font-bold text-yellow-300">
              {userCompletedChallenges.reduce((acc, c) => acc + (c.points || 0), 0).toLocaleString()}
            </p>
            <p className="text-sm text-blue-100">Points Earned</p>
            <p className="mt-1 text-xs text-yellow-300">
              💰 Medi Points earned through challenges
            </p>
          </div>
          
          <div className="p-4 bg-white bg-opacity-10 rounded-xl backdrop-blur-sm">
            <Clock className="w-8 h-8 mx-auto mb-2 text-purple-300" />
            <p className="text-3xl font-bold text-white">
              {userCompletedChallenges.length > 0 ? Math.round((userCompletedChallenges.length / (userCompletedChallenges.length + userActiveChallenges.length)) * 100) : 0}%
            </p>
            <p className="text-sm text-blue-100">Success Rate</p>
            <p className="mt-1 text-xs text-purple-300">📈 Keep improving!</p>
          </div>
        </div>
      </div>
        
        {/* Points-focused banner */}
        <div className="p-4 mt-6 text-gray-900 bg-gradient-to-r from-yellow-400 via-yellow-500 to-orange-500 rounded-xl">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold">🎯 Future of Healthcare Philosophy</h3>
              <p className="text-sm opacity-90">Real Health Data → Verified Progress → Medi Points → Transformative Rewards!</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold">🏥 💪 📈</p>
              <p className="text-xs font-semibold">New Healthcare Standard</p>
            </div>
          </div>
        </div>
        
        {/* Subscription Upgrade Banner - Only show if not Platinum */}
        {subscriptionLimits.name !== 'Platinum' && (
          <div className="p-4 mt-4 text-white rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600">
            <div className="flex items-center gap-3">
              <span className="text-2xl">⬆️</span>
              <div>
                <h3 className="text-sm font-bold">Upgrade to Access More Challenges!</h3>
                <p className="mt-1 text-xs text-purple-100">
                  Unlock higher tier challenges with {subscriptionLimits.name === 'Free' ? 'Premium' : 'Platinum'} membership.
                </p>
              </div>
            </div>
          </div>
        )}


        {/* Fair Play banner: shows conflicts and pruning notices */}
        {fairPlayBanner && (
          <div className={`p-3 mt-4 rounded-xl border ${
            fairPlayBanner.type === 'error'
              ? 'bg-red-50 border-red-200 text-red-800'
              : 'bg-yellow-50 border-yellow-200 text-yellow-800'
          }`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h4 className="text-sm font-bold">
                  {fairPlayBanner.type === 'error' ? 'Fair Play Blocked' : 'Fair Play Notice'}
                </h4>
                <p className="mt-1 text-xs whitespace-pre-line">{fairPlayBanner.message}</p>
                {Array.isArray(fairPlayWarnings) && fairPlayWarnings.length > 0 && (
                  <p className="mt-1 text-[11px] opacity-80">Details: {fairPlayWarnings.length} conflict{fairPlayWarnings.length > 1 ? 's' : ''} detected.</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setFairPlayBanner(null)}
                className="text-xs font-medium underline opacity-80 hover:opacity-100"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

      {/* � PHASE 2 ROADMAP - Comprehensive IoMT Integration Showcase */}
      <div className="p-6 mb-6 border-2 border-purple-400 rounded-xl bg-gradient-to-br from-indigo-900 via-purple-900 to-blue-900">
        <div className="flex items-start gap-4">
          <span className="text-3xl">🚀</span>
          <div className="flex-1">
            <h3 className="mb-3 text-xl font-bold text-white">Phase 2: Revolutionary IoMT Ecosystem Expansion</h3>
            <p className="mb-4 text-sm text-purple-100">
              <span className="font-semibold">Coming Soon:</span> MediCureOn will integrate with the world's most comprehensive IoMT ecosystem - 
              from consumer wearables to clinical-grade equipment, voice assistants to hospital systems.
            </p>
            
            {/* Device Categories Grid */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {/* Consumer Devices */}
              <div className="p-4 rounded-lg bg-gradient-to-br from-green-800/40 to-green-600/40 backdrop-blur-sm">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">⌚</span>
                  <h4 className="font-semibold text-green-200">Consumer Wearables</h4>
                </div>
                <div className="space-y-1 text-xs text-green-100">
                  <p>✅ Apple Watch Series 9/10/Ultra</p>
                  <p>✅ Samsung Galaxy Watch 5-7</p>
                  <p>✅ Fitbit Versa/Sense/Charge</p>
                  <p>✅ Garmin Venu/Fenix/Forerunner</p>
                  <p>✅ Withings Complete Ecosystem</p>
                  <p>✅ Oura Ring Gen 3/4</p>
                  <p>🔜 WHOOP, Polar, Suunto</p>
                  <p>🔜 Xiaomi Mi Band, Huawei</p>
                </div>
              </div>

              {/* Clinical Devices */}
              <div className="p-4 rounded-lg bg-gradient-to-br from-blue-800/40 to-blue-600/40 backdrop-blur-sm">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">🏥</span>
                  <h4 className="font-semibold text-blue-200">Clinical Grade</h4>
                </div>
                <div className="space-y-1 text-xs text-blue-100">
                  <p>✅ AliveCor KardiaMobile ECG</p>
                  <p>✅ Omron HeartGuide BP</p>
                  <p>🔜 Dexcom CGM Integration</p>
                  <p>🔜 Philips Clinical Equipment</p>
                  <p>🔜 Omron Professional BPMs</p>
                  <p>🔜 ResMed Sleep Devices</p>
                  <p>🔜 VoCare Vitals360</p>
                  <p>🔜 FreeStyle Libre CGM</p>
                </div>
              </div>

              {/* Emerging Tech */}
              <div className="p-4 rounded-lg bg-gradient-to-br from-purple-800/40 to-purple-600/40 backdrop-blur-sm">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">🔬</span>
                  <h4 className="font-semibold text-purple-200">Emerging Tech</h4>
                </div>
                <div className="space-y-1 text-xs text-purple-100">
                  <p>🔜 K'Watch Non-invasive Glucose</p>
                  <p>🔜 Smart Clinical Glasses</p>
                  <p>🔜 Smart Contact Lenses</p>
                  <p>🔜 Smart Bandages</p>
                  <p>🔜 Smart Pills/Sensors</p>
                  <p>🔜 Smart Mirrors</p>
                  <p>🔜 Posture Monitors</p>
                  <p>🔜 Smart Inhalers</p>
                </div>
              </div>

              {/* Voice & Smart Home */}
              <div className="p-4 rounded-lg bg-gradient-to-br from-yellow-800/40 to-orange-600/40 backdrop-blur-sm">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">🎤</span>
                  <h4 className="font-semibold text-yellow-200">Voice & Smart Home</h4>
                </div>
                <div className="space-y-1 text-xs text-yellow-100">
                  <p>🔜 Amazon Alexa Integration</p>
                  <p>🔜 Google Assistant</p>
                  <p>🔜 Apple Siri/HomePod</p>
                  <p>🔜 Samsung Bixby</p>
                  <p>🔜 Voice Health Scanners</p>
                  <p>🔜 Smart Health Hubs</p>
                  <p>🔜 Voice Medication Dispensers</p>
                  <p>🔜 Smart Speaker Health Monitoring</p>
                </div>
              </div>
            </div>

            {/* Phase 2 Highlights */}
            <div className="grid gap-4 mt-6 md:grid-cols-3">
              <div className="p-3 rounded-lg bg-white/10 backdrop-blur-sm">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">🌟</span>
                  <h5 className="font-semibold text-white">10+ New Integrations</h5>
                </div>
                <p className="text-xs text-gray-300">
                  Adding WHOOP, Polar, Xiaomi, Huawei, professional clinical equipment, and voice assistants
                </p>
              </div>
              
              <div className="p-3 rounded-lg bg-white/10 backdrop-blur-sm">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">🏥</span>
                  <h5 className="font-semibold text-white">Hospital Integration</h5>
                </div>
                <p className="text-xs text-gray-300">
                  Stryker smart beds, Philips ventilators, professional EKGs, and clinical information systems
                </p>
              </div>
              
              <div className="p-3 rounded-lg bg-white/10 backdrop-blur-sm">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">🎯</span>
                  <h5 className="font-semibold text-white">Global Market Access</h5>
                </div>
                <p className="text-xs text-gray-300">
                  Targeting Asia (Xiaomi), Europe (Nokia), and specialized markets with region-specific devices
                </p>
              </div>
            </div>

            {/* Current Integration Status */}
            <div className="p-4 mt-6 rounded-lg bg-gradient-to-r from-green-800/30 to-blue-800/30">
              <h5 className="mb-2 font-semibold text-white">🔥 Phase 1 Active Integrations</h5>
              <div className="grid gap-2 text-xs md:grid-cols-2">
                <div className="text-green-300">
                  <p>✅ <span className="font-semibold">Apple Health:</span> Steps, Heart Rate, Sleep, ECG</p>
                  <p>✅ <span className="font-semibold">Withings:</span> Smart Scales, BP Monitors (API Ready)</p>
                  <p>✅ <span className="font-semibold">Garmin:</span> Fitness Tracking, GPS (API Ready)</p>
                </div>
                <div className="text-blue-300">
                  <p>✅ <span className="font-semibold">Samsung:</span> Galaxy Watch Ecosystem (Public API)</p>
                  <p>✅ <span className="font-semibold">Fitbit:</span> Comprehensive Health (Public API)</p>
                  <p>✅ <span className="font-semibold">Oura:</span> Sleep & Recovery (Public API)</p>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <div className="p-3 mt-4 text-center rounded-lg bg-gradient-to-r from-purple-600 to-pink-600">
              <p className="text-sm font-semibold text-white">
                🚀 Ready to experience the future of healthcare? Connect your devices today and be part of the revolution!
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* �️ COMPREHENSIVE ANTI-GAMING PROTECTION INFORMATION BANNER */}

      {/* �🚨 COMPREHENSIVE ANTI-GAMING PROTECTION INFORMATION BANNER */}
      <div className="p-4 mb-6 border border-blue-200 bg-gradient-to-r from-blue-50 to-green-50 rounded-xl">
        <div className="flex items-start gap-3">
          <span className="text-2xl">🛡️</span>
          <div className="flex-1">
            <h3 className="mb-2 text-sm font-bold text-blue-900">Comprehensive Fair Play Protection System</h3>
            <div className="grid gap-3 text-xs md:grid-cols-2">
              <div className="p-3 bg-white border border-blue-200 rounded-lg">
                <p className="mb-1 font-semibold text-blue-800">🚫 ALL Challenge Conflicts Protected</p>
                <p className="text-blue-600">
                  You cannot join multiple challenges of the same type simultaneously:
                  <br />• Steps (Century Step Master, Million Step, etc.)
                  <br />• Hydration (Hydration Hero, Hydration Week)
                  <br />• Sleep (Sleep Starter, Sleep Reset, etc.)
                  <br />• Nutrition (Meal Tracker, Macro Mastery, etc.)
                  <br />• Mindfulness (Mindful Minutes, Meditation Master, etc.)
                  <br />• Fitness (Workout Warrior, Fitness Focus, etc.)
                </p>
              </div>
              <div className="p-3 bg-white border border-green-200 rounded-lg">
                <p className="mb-1 font-semibold text-green-800">✅ Complete Protection</p>
                <p className="text-green-600">
                  Our advanced system prevents ALL forms of gaming abuse by detecting conflicting challenge types. 
                  You can't double-dip on ANY category - whether it's steps, hydration, sleep, nutrition, mindfulness, 
                  cardiovascular, fitness, strength, or logging challenges!
                </p>
              </div>
            </div>
            <div className="p-2 mt-3 bg-yellow-100 border border-yellow-300 rounded">
              <p className="text-xs text-yellow-800">
                <span className="font-semibold">💡 Strategy Tip:</span> Mix DIFFERENT challenge types (steps + nutrition + sleep + mindfulness + fitness) to maximize your Medi Points without any conflicts!
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Challenge Categories */}
      {['beginner', 'easy', 'medium', 'hard', 'extreme', 'century'].map((difficulty) => {
        // Use production challenges for beginner, easy, medium, hard, extreme, and century; existing challenges for others
        const difficultyChallenges = difficulty === 'beginner' 
          ? productionChallenges.beginner 
          : difficulty === 'easy'
          ? productionChallenges.easy
          : difficulty === 'medium'
          ? productionChallenges.medium
          : difficulty === 'hard'
          ? productionChallenges.hard
          : difficulty === 'extreme'
          ? productionChallenges.extreme
          : difficulty === 'century'
          ? productionChallenges.century
          : (challenges || []).filter(c => c.difficulty === difficulty);
        
        // Filter out Century challenges for non-Gold/Platinum users - Show section but with access control
        // (We still show the section to encourage upgrades)
        
        const difficultyColors = {
          beginner: 'from-green-50 to-emerald-50 border-green-200',
          easy: 'from-blue-50 to-cyan-50 border-blue-200',
          medium: 'from-yellow-50 to-orange-50 border-yellow-200',
          hard: 'from-purple-50 to-pink-50 border-purple-200',
          extreme: 'from-red-50 to-rose-50 border-red-200',
          century: 'from-black to-gray-900 border-yellow-400'
        };

        const difficultyDescriptions = {
          beginner: '🌱 Perfect for health journey starters - Senior friendly with device verification',
          easy: '🎯 Build healthy habits - Moderate commitment required',
          medium: '� Serious commitment - 70-80 days of consistency for transformative results',
          hard: '🔥 Elite dedication - 80-100 days of habit mastery and peak performance',
          extreme: '⚡ Legendary status - Epic challenges for health warriors seeking ultimate achievement',
          century: '👑 Century Conquest - Exclusive 100-day branded challenges for Gold & Platinum members only'
        };

        const pointsRewards = {
          beginner: 'Earn 90-200 Medi Points per challenge',
          easy: 'Earn 400-600 Medi Points per challenge',
          medium: 'Earn 2400-2600 Medi Points per challenge',
          hard: 'Earn 4000-5000 Medi Points per challenge',
          extreme: 'Earn 10000-12000 Medi Points per challenge',
          century: 'Earn 12000-20000 Medi Points per challenge + Exclusive Bonuses'
        };

        return (
          <div key={difficulty} className="mb-8">
            <div className={`p-6 mb-6 border rounded-xl ${
              difficulty === 'century' 
                ? 'border-yellow-400 bg-gradient-to-r from-black to-gray-900' 
                : 'border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className={`flex items-center text-2xl font-bold capitalize ${
                  difficulty === 'century' ? 'text-yellow-500' : 'text-gray-900'
                }`}>
                  {difficulty} Challenges
                  {difficulty === 'beginner' && <span className="ml-2 text-green-600">🆕</span>}
                  {difficulty === 'easy' && <span className="ml-2 text-cyan-600">🎯</span>}
                  {difficulty === 'medium' && <span className="ml-2 text-orange-600">🚀</span>}
                  {difficulty === 'hard' && <span className="ml-2 text-red-600">🔥</span>}
                  {difficulty === 'extreme' && <span className="ml-2 text-purple-600">⚡</span>}
                  {difficulty === 'century' && <span className="ml-2 text-yellow-600">👑</span>}
                </h3>
                <span className={`px-3 py-1 text-sm rounded-full ${
                  difficulty === 'century' 
                    ? 'text-black bg-yellow-400' 
                    : 'text-gray-500 bg-white'
                }`}>
                  {difficultyChallenges.length} available
                </span>
              </div>
              
              <p className={`mb-2 text-sm ${
                difficulty === 'century' ? 'text-yellow-200' : 'text-gray-600'
              }`}>{difficultyDescriptions[difficulty]}</p>
              <p className={`text-sm font-semibold ${
                difficulty === 'century' ? 'text-yellow-400' : 'text-purple-600'
              }`}>💰 {pointsRewards[difficulty]}</p>
              
              {difficulty === 'beginner' && (
                <div className="p-3 mt-3 border border-blue-200 rounded-lg bg-blue-50">
                  <p className="mb-1 text-sm font-semibold text-blue-800">🔒 Device-Verified Health Tracking</p>
                  <p className="text-xs text-blue-700">
                    Every challenge is tracked via connected devices. No cheating possible - only real health improvements earn Medi Points!
                  </p>
                </div>
              )}
              
              {difficulty === 'easy' && (
                <div className="p-3 mt-3 border rounded-lg border-cyan-200 bg-cyan-50">
                  <p className="mb-1 text-sm font-semibold text-cyan-800">🎯 21-Day Wellness Revolution</p>
                  <p className="text-xs text-cyan-700">
                    Perfect for building lasting habits! Device-verified tracking ensures authentic progress with higher Medi Points rewards.
                  </p>
                </div>
              )}
              
              {difficulty === 'medium' && (
                <div className="p-3 mt-3 border border-orange-200 rounded-lg bg-orange-50">
                  <p className="mb-1 text-sm font-semibold text-orange-800">🚀 Elite Transformation Program</p>
                  <p className="text-xs text-orange-700">
                    70-80 days of serious commitment! Maximum Medi Points for life-changing health transformations. Real dedication required.
                  </p>
                </div>
              )}
              
              {difficulty === 'hard' && (
                <div className="p-3 mt-3 border border-red-200 rounded-lg bg-red-50">
                  <p className="mb-1 text-sm font-semibold text-red-800">🔥 Master's Championship</p>
                  <p className="text-xs text-red-700">
                    80-100 days of elite dedication! Premium Medi Points (4000-5000) for habit mastery and peak performance achievement.
                  </p>
                </div>
              )}
              
              {difficulty === 'extreme' && (
                <div className="p-3 mt-3 border border-purple-200 rounded-lg bg-purple-50">
                  <p className="mb-1 text-sm font-semibold text-purple-800">⚡ Legendary Hall of Fame</p>
                  <p className="text-xs text-purple-700">
                    Epic 90-365 day challenges! Ultimate Medi Points (10000-12000) for achieving legendary health warrior status. Only for the most dedicated!
                  </p>
                </div>
              )}
              
              {difficulty === 'century' && (
                <div className="p-4 mt-3 border-2 border-yellow-400 rounded-lg bg-gradient-to-r from-black to-gray-900">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg">👑</span>
                    <p className="text-sm font-bold text-yellow-400">Century Conquest Series - EXCLUSIVE ACCESS</p>
                    <span className="px-2 py-1 text-xs font-bold text-black bg-yellow-400 rounded-full">BRANDED</span>
                  </div>
                  <p className="mb-2 text-xs text-yellow-300">
                    🎯 Epic 100-day branded challenges! Premium Medi Points (12000-20000) with exclusive bonuses and achievements.
                  </p>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="px-2 py-1 text-black bg-yellow-400 rounded-full">💎 Gold Required</span>
                    <span className="px-2 py-1 text-black bg-yellow-400 rounded-full">💎 Platinum Preferred</span>
                  </div>
                  {subscriptionLimits.name !== 'Gold' && subscriptionLimits.name !== 'Platinum' && (
                    <div className="p-2 mt-2 border border-red-300 rounded bg-red-50">
                      <p className="text-xs font-semibold text-red-700">🔒 Access Denied: Upgrade to Gold or Platinum to unlock Century Challenges!</p>
                    </div>
                  )}
                  
                  {/* Century Challenge Limit Policy */}
                  <div className="p-2 mt-3 bg-yellow-100 border border-yellow-300 rounded">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm">⚠️</span>
                      <p className="text-xs font-bold text-yellow-800">CENTURY CHALLENGE POLICY</p>
                    </div>
                    <p className="text-xs text-yellow-700">
                      Only ONE active Century challenge allowed at a time to maintain exclusivity and prevent abuse.
                    </p>
                    {hasActiveCenturyChallenge && (
                      <div className="p-1 mt-1 bg-orange-100 border border-orange-400 rounded">
                        <p className="text-xs font-semibold text-orange-800">
                          🏆 Current: {activeCenturyChallenges[0]?.name || 'Active Century Challenge'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Subscription Benefits Info */}
              <div className="p-3 mt-3 border border-gray-300 rounded-lg bg-gradient-to-r from-gray-100 to-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-gray-700">
                    💎 Challenge Slot Usage
                  </span>
                  <span className={`text-xs font-semibold ${subscriptionLimits.color}`}>
                    {subscriptionLimits.icon} {subscriptionLimits.name}
                  </span>
                </div>
                <div className="flex items-center justify-between mb-2 text-xs text-gray-600">
                  <span>Active Challenges:</span>
                  <span className="font-semibold">
                    {currentActiveChallenges}/{subscriptionLimits.activeChallenges}
                  </span>
                </div>
                <div className="w-full h-2 bg-gray-300 rounded-full">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${
                      currentActiveChallenges >= subscriptionLimits.activeChallenges 
                        ? 'bg-red-500' 
                        : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min((currentActiveChallenges / subscriptionLimits.activeChallenges) * 100, 100)}%` }}
                  />
                </div>
                {currentActiveChallenges >= subscriptionLimits.activeChallenges && (
                  <p className="mt-1 text-xs font-semibold text-red-600">
                    ⚠️ Challenge limit reached! Complete challenges or upgrade plan.
                  </p>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              {difficultyChallenges.map((challenge) => {
                const userChallenge = getUserChallengeStatusLocal(challenge.id);
                
                // Use CenturyCard for century challenges, regular ChallengeCard for others
                if (difficulty === 'century') {
                  return (
                    <CenturyCard
                      key={challenge.id}
                      challenge={challenge}
                      userStatus={userChallenge}
                      onJoin={() => {
                        if (isUserChallengePaused(challenge.id)) {
                          handleResumeChallenge(challenge.id);
                        } else {
                          handleJoinChallenge(challenge.id);
                        }
                      }}
                    />
                  );
                }
                
                return (
                  <ChallengeCard
                    key={challenge.id}
                    challenge={challenge}
                    userStatus={userChallenge}
                    difficultyColor={difficultyColors[difficulty]}
                    onJoin={() => {
                      if (isUserChallengePaused(challenge.id)) {
                        handleResumeChallenge(challenge.id);
                      } else {
                        handleJoinChallenge(challenge.id);
                      }
                    }}
                  />
                );
              })}
            </div>
          </div>
        );
      })}
      
      {/* Phase 2: Revolutionary IoMT Ecosystem Expansion */}
      <div className="p-6 mt-8 border-2 shadow-2xl border-gradient bg-gradient-to-r from-blue-900 via-purple-900 to-indigo-900 rounded-xl">
        <div className="text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="p-2 mr-3 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500">
              <span className="text-2xl">🚀</span>
            </div>
            <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">
              Future Phases: Revolutionary Smart Device Ecosystem Expansion
            </h2>
          </div>
          
          <div className="mb-6 space-y-3">
            <p className="text-lg font-semibold text-white">
              <span className="text-yellow-300">MediCureOn MVP 2.0</span> — Transforming Healthcare Through Advanced Challenge Gamification
            </p>
            <p className="text-sm leading-relaxed text-blue-200">
              Experience the future of personalized healthcare with our streamlined challenge ecosystem. Every interaction is powered by AI-driven insights, real-time smart device integration, and enterprise-grade health optimization protocols.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6 md:grid-cols-4">
            <div className="p-3 border rounded-lg bg-white/10 backdrop-blur-sm border-white/20">
              <div className="mb-1 text-lg text-yellow-400">🎯</div>
              <div className="text-sm font-semibold text-white">Precision Challenges</div>
              <div className="text-xs text-blue-200">6 Difficulty Tiers</div>
            </div>
            <div className="p-3 border rounded-lg bg-white/10 backdrop-blur-sm border-white/20">
              <div className="mb-1 text-lg text-green-400">📊</div>
              <div className="text-sm font-semibold text-white">Real-Time Analytics</div>
              <div className="text-xs text-blue-200">Smart Device Sync</div>
            </div>
            <div className="p-3 border rounded-lg bg-white/10 backdrop-blur-sm border-white/20">
              <div className="mb-1 text-lg text-purple-400">👑</div>
              <div className="text-sm font-semibold text-white">Premium Tiers</div>
              <div className="text-xs text-blue-200">Century Challenges</div>
            </div>
            <div className="p-3 border rounded-lg bg-white/10 backdrop-blur-sm border-white/20">
              <div className="mb-1 text-lg text-orange-400">⚡</div>
              <div className="text-sm font-semibold text-white">Enterprise Scale</div>
              <div className="text-xs text-blue-200">Global Deployment</div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 text-xs text-blue-300">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 bg-green-400 rounded-full"></span>
              Advanced Health Tracking
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 bg-yellow-400 rounded-full"></span>
              Gamified Wellness Journey
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 bg-purple-400 rounded-full"></span>
              Premium Member Benefits
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
              Revolutionary User Experience
            </span>
          </div>

          <div className="p-3 mt-4 border rounded-lg bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-yellow-500/30">
            <p className="text-sm font-medium text-yellow-100">
              💎 <span className="text-white">Next Evolution:</span> Advanced AI-powered challenge recommendations, multi-device synchronization, and enterprise health analytics launching Q1 2026
            </p>
          </div>
        </div>
      </div>
      
      {/* Challenge Details Popup */}
      {showDetailsPopup && selectedChallengeDetails && (
        <ChallengeDetailsPopup
          challenge={selectedChallengeDetails.challenge}
          userChallenge={selectedChallengeDetails.userChallenge}
          onClose={() => {
            setShowDetailsPopup(false);
            setSelectedChallengeDetails(null);
          }}
        />
      )}
    </div>
  );
};

export default ChallengesTab;

